import { setDefaultOptions, loadModules } from 'esri-loader'
import store from '@/store/index'
import constants from './constants'
import * as symbology from './GraphicsSimbology'
import VueInst from '../main'
import JSZip from 'jszip'

// import MedioPopup from '../components/map/popup/MedioPopup'
import Vue from 'vue'

import proj4 from 'proj4'

import DateService, { FMT, DayjsUnit } from '@/helpers/DateService'
import api from '../api'
import shpwrite from 'shp-write-update'
import { toKML } from '@placemarkio/tokml'
import * as toGeoJSON from '@tmcw/togeojson'
import { buildWindColorKey } from './Utils'

setDefaultOptions({ css: true })

const Q = require('q')

/**
 * Global variables
 */
let map
let mapView
let mapView2D = null
let sceneView3D = null
let mapViewOverview
let mapViewPlanOperaciones
let simulacionActivaId = null

// #region MAP
let ExaggeratedElevationLayerSubClass
let elevationLayer
export const createMap = async() => {
  try {
    await iniciarModulosCore()

    await initExaggeratedElevationLayerSubClass()
    elevationLayer = new ExaggeratedElevationLayerSubClass()

    map = new EsriMap({
      ground: {
        layers: [elevationLayer]
      },
      basemap: 'satellite'
    })

    await registerToken()
    await initializeViews()

    const layerErr = []
    mapView.on('layerview-create-error', (event) => {
      const msg = event.layer.title
      layerErr.push(msg)
    })

    await createLayers()
    await drawContorno()
    reorderLayers()

    await setSearchWidget()
    await compassWidget()
    await legendWidget()

    redrawMedios()
    redrawIncendios()
    redrawHermes()
    redrawInfraestructuras()
    redrawInfraestructurasPropias()

    if (layerErr.length) {
      Vue.notify({
        title: 'Error',
        text: `${layerErr.length} errores al cargar las layers: ${layerErr.join(', ')}`,
        type: 'error'
      })
    }
  } catch (err) {
    VueInst.$log.error(err)
  }
}

/**
 * Variables modulos core esri
 */
let EsriMap, MapView, SceneView
let Graphic, Point, Polyline, Polygon, CIMSymbol
let GeometryEngine
let FeatureLayer, GraphicsLayer
const iniciarModulosCore = async() => {
  try {
    [
      EsriMap, MapView, SceneView,
      Graphic, Point, Polyline, Polygon, CIMSymbol,
      GeometryEngine,
      FeatureLayer, GraphicsLayer
    ] = await loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/views/SceneView',
      'esri/Graphic',
      'esri/geometry/Point',
      'esri/geometry/Polyline',
      'esri/geometry/Polygon',
      'esri/symbols/CIMSymbol',
      'esri/geometry/geometryEngine',
      'esri/layers/FeatureLayer',
      'esri/layers/GraphicsLayer'
    ])
  } catch (error) {
    console.error('Error inicializando modulos ESRI core', error)
    throw error
  }
}

let draw
export const drawPolygonAreaToTrackMedios = async() => {
  try {
    const [Draw] = await loadModules(['esri/views/draw/Draw'])
    const [projection] = await loadModules(['esri/geometry/projection'])

    let poligono = null
    let poligonoGraphic

    const layer = map.findLayerById('layer-drawPolygon')

    if (!draw) {
      // Se inicializa si no lo está ya
      draw = new Draw({
        view: mapView
      })
    }
    const action = draw.create('polygon')

    return new Promise((resolve, reject) => {
      // PolygonDrawAction.vertex-add
      // Fires when user clicks, or presses the "F" key.
      // Can also be triggered when the "R" key is pressed to redo.
      action.on('vertex-add', async(evt) => {
        const lastVertex = evt.vertices[evt.vertexIndex]
        const point = new Point({
          x: lastVertex[0],
          y: lastVertex[1]
        })

        if (!poligono) {
          poligono = new Polygon({
            spatialReference: {
              wkid: 3857
            }
          })
          poligono.addRing([point, point])
        } else {
          const ringLength = poligono.rings[0].length
          poligono.insertPoint(0, ringLength - 1, point)
        }
        const poligonoGrafico = await createPolygonGraphic(evt.vertices)
        layer.graphics.removeAll()
        layer.graphics.push(poligonoGrafico)
      })

      // Fires when the pointer moves over the view
      action.on('cursor-update', async(evt) => {
        const poligonoGrafico = await createPolygonGraphic(evt.vertices)
        layer.graphics.removeAll()
        layer.graphics.push(poligonoGrafico)
      })

      // Add a graphic representing the completed polygon
      // when user double-clicks on the view or presses the "C" key
      action.on('draw-complete', (evt) => {
        const geometry4326 = projection.project(poligono, { wkid: 4326 })
        let coordenadasPoligono = '('

        for (let i = 0; i < geometry4326.rings[0].length; i++) {
          const coordenadas = String(geometry4326.rings[0][i]).split(',')
          if (i === geometry4326.rings[0].length - 1) {
            coordenadasPoligono += `${coordenadas[0]} ${coordenadas[1]})`
          } else {
            coordenadasPoligono += `${coordenadas[0]} ${coordenadas[1]}, `
          }
        }

        mapView.goTo(poligono, {
          duration: 3000,
          easing: 'in-expo'
        })
        layer.graphics.removeAll()
        resolve(coordenadasPoligono)
      })
    })
  } catch (err) {
    VueInst.$log.error(err)
    throw err
  }
}
export function cancelDrawingPolygon() {
  const layer = map.findLayerById('layer-drawPolygon')
  if (draw) {
    draw.reset()
    layer.graphics.removeAll()
  }
}

function createPolygonGraphic(vertices) {
  const deferred = Q.defer()
  try {
    const geometry = new Polygon({
      hasZ: false,
      hasM: true,
      rings: vertices,
      spatialReference: { wkid: 3857 }
    })

    const poligonoGraphic = new Graphic({
      geometry,
      symbol: {
        type: 'simple-fill', // autocasts as SimpleFillSymbol
        color: 'purple',
        style: 'solid',
        outline: {
          // autocasts as SimpleLineSymbol
          color: 'white',
          width: 1
        }
      }
    })

    deferred.resolve(poligonoGraphic)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}
async function initExaggeratedElevationLayerSubClass() {
  try {
    const [ElevationLayer, BaseElevationLayer] = await loadModules([
      'esri/layers/ElevationLayer',
      'esri/layers/BaseElevationLayer'
    ])

    ExaggeratedElevationLayerSubClass = BaseElevationLayer.createSubclass({
      properties: {
        id: 'elevation-layer',
        exaggeration: 1
      },

      load() {
        this._elevation = new ElevationLayer({
          id: 'elevation-layer',
          url: 'https://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer'
        })

        this.addResolvingPromise(
          this._elevation.load().then(() => {
            this.tileInfo = this._elevation.tileInfo
            this.spatialReference = this._elevation.spatialReference
            this.fullExtent = this._elevation.fullExtent
          })
        )

        return this
      },

      fetchTile(level, row, col, options) {
        return this._elevation.fetchTile(level, row, col, options).then(
          (data) => {
            const exaggeration = this.exaggeration
            for (let i = 0; i < data.values.length; i++) {
              data.values[i] = data.values[i] * exaggeration
            }

            return data
          }
        )
      }
    })
  } catch (err) {
    VueInst.$log.error(err)
  }
}

const cimSymbolCache = new WeakMap()
function getCachedCIMSymbol(baseSymbol, size) {
  if (!baseSymbol?.data?.symbol) {
    return baseSymbol
  }

  let sizeCache = cimSymbolCache.get(baseSymbol)

  if (!sizeCache) {
    sizeCache = new Map()
    cimSymbolCache.set(baseSymbol, sizeCache)
  }

  if (sizeCache.has(size)) {
    return sizeCache.get(size)
  }

  const symbolData = baseSymbol.data
  const symbol = symbolData.symbol

  let newSymbol

  if (symbol.type === 'CIMPointSymbol') {
    newSymbol = new CIMSymbol({
      data: {
        ...symbolData,
        symbol: {
          ...symbol,
          symbolLayers: symbol.symbolLayers
            ? symbol.symbolLayers.map(layer => {
              const newLayer = { ...layer }

              if (newLayer.size) {
                newLayer.size = size * 2
              }

              return newLayer
            })
            : []
        }
      }
    })
  } else if (symbol.type === 'CIMLineSymbol') {
    const sizeArrow = size / 2

    newSymbol = new CIMSymbol({
      data: {
        ...symbolData,
        symbol: {
          ...symbol,
          symbolLayers: symbol.symbolLayers
            ? symbol.symbolLayers.map(layer => {
              if (layer.type !== 'CIMVectorMarker' && 'size' in layer) {
                return {
                  ...layer,
                  size: sizeArrow
                }
              }

              return layer
            })
            : []
        }
      }
    })
  } else {
    return baseSymbol
  }

  sizeCache.set(size, newSymbol)
  return newSymbol
}

function getSymbolSizeForScale(scale) {
  if (scale <= 1128) return 40
  if (scale <= 4513) return 30
  if (scale <= 18055) return 20
  if (scale <= 144447) return 10
  return 6
}

function createSizedGraphics(graphicList) {
  try {
    if (!graphicList) return []

    if (!mapViewPlanOperaciones) {
      return graphicList
    }

    const currentScale = mapViewPlanOperaciones.scale
    const size = getSymbolSizeForScale(currentScale)
    const cleanList = graphicList.filter(g => g.symbol)

    return cleanList.map(g => {
      if (!g.baseSymbolCache) {
        g.baseSymbolCache = g.symbol
      }

      const baseSymbol = g.baseSymbolCache

      if (baseSymbol?.type !== 'cim') {
        return g
      }

      g.symbol = getCachedCIMSymbol(baseSymbol, size)

      return g
    })
  } catch (e) {
    console.warn('Error controlado en createSizedGraphics:', e)
    return graphicList || []
  }
}

let zoomTimeout
let previousSize = null
function watchZoomAndResizeGraphics(view) {
  view.watch('scale', newScale => {
    if (zoomTimeout) {
      clearTimeout(zoomTimeout)
    }

    zoomTimeout = setTimeout(() => {
      const sizeIcon = getSymbolSizeForScale(newScale)

      if (sizeIcon === previousSize) {
        return
      }

      previousSize = sizeIcon

      const graphicsLayer = map.findLayerById('layer-plan-operaciones')

      if (!graphicsLayer) return

      graphicsLayer.graphics.items.forEach(g => {
        if (!g.baseSymbolCache) {
          g.baseSymbolCache = g.symbol
        }

        const baseSymbol = g.baseSymbolCache

        if (baseSymbol?.type !== 'cim') {
          return
        }

        const cachedSymbol = getCachedCIMSymbol(baseSymbol, sizeIcon)

        if (g.symbol !== cachedSymbol) {
          g.symbol = cachedSymbol
        }
      })
    }, 150)
  })
}

async function initializeViews() {
  const initialExtent = store.getters['map/paramsComunidad'].EXTENT

  mapView2D = new MapView({
    map,
    container: 'mapNode',
    constraints: {
      rotationEnabled: false
    }
  })

  if (initialExtent) {
    mapView2D.extent = initialExtent
  }

  mapView2D.ui.remove('zoom')

  mapView2D.ui.padding = {
    top: 70,
    left: 0,
    right: 15,
    bottom: 24
  }

  mapViewPlanOperaciones = mapView2D
  watchZoomAndResizeGraphics(mapView2D)
  assignMapViewEvents(mapView2D)

  sceneView3D = new SceneView({
    map,
    container: document.getElementById('sceneViewPreload'),
    constraints: {
      rotationEnabled: false
    }
  })

  if (initialExtent) {
    sceneView3D.extent = initialExtent
  }

  const navigation = sceneView3D.ui.find('navigation-toggle')
  sceneView3D.ui.remove('zoom')

  if (navigation) {
    sceneView3D.ui.move(
      navigation,
      'top-right'
    )
  }

  sceneView3D.ui.remove('compass')
  sceneView3D.ui.remove('legend')

  assignMapViewEvents(sceneView3D)

  await Promise.all([
    mapView2D.when(),
    sceneView3D.when()
  ])

  mapView = mapView2D
  store.dispatch('map/setMap3D', false)

  onMapViewChange(mapView2D)

  sceneView3D.container = null
}

export const use2D = () => {
  try {
    closeMapPopups()

    if (sceneView3D) {
      mapView2D.viewpoint = sceneView3D.viewpoint.clone()
    }

    if (mapView) {
      mapView.container = null
    }

    mapView2D.container = 'mapNode'
    mapView = mapView2D
    mapViewPlanOperaciones = mapView2D

    store.dispatch('map/setMap3D', false)

    onMapViewChange(mapView2D)
  } catch (err) {
    VueInst.$log.error(err)
  }
}

export const use3D = () => {
  try {
    closeMapPopups()

    if (mapView2D) {
      sceneView3D.viewpoint = mapView2D.viewpoint.clone()
    }

    if (mapView) {
      mapView.container = null
    }

    sceneView3D.container = 'mapNode'
    mapView = sceneView3D

    store.dispatch('map/setMap3D', true)

    onMapViewChange(sceneView3D)
  } catch (err) {
    VueInst.$log.error(err)
  }
}

function onMapViewChange(newMapView) {
  mapView = newMapView

  clearMeasurement()
  setSearchWidget()
  compassWidget()
  updateWidgetView()
  updateRendererViento()
}

export const getMapView = () => {
  return mapView
}

export const getMap = () => {
  return map
}

export const getCaptureMapOverview = async(layer) => {
  return await getCaptureMapFeatures(mapViewOverview.graphics, layer)
}

export const measureOverview = async() => {
  return await measurePerimetros(mapViewOverview.graphics)
}
// #endregion

// #region MAPVIEW EVENTS
let graphicHighlight = null
const trackHighlight = []

function assignMapViewEvents(view) {
  let pointerDown = false

  view.watch('extent', (event) => {
    if (event) {
      store.dispatch('shareMap/changeMapExtent', {
        xmax: event.xmax,
        xmin: event.xmin,
        ymax: event.ymax,
        ymin: event.ymin,
        wkid: event.spatialReference.wkid,

        viewpoint: view.viewpoint
      })
    }
  })

  // Scale
  view.watch('scale', (event) => {
    VueInst.$eventHub.$emit('escalaMap', event)
  })

  // Move mouse -> update coordinates
  view.on('pointer-move', (event) => {
    if (!pointerDown) {
      sendCoordinates(view.toMap(event))
    }
  })

  view.on('click', (event) => {
    // Enviar coordenadas para representarlas en el MapaWeb
    sendCoordinates(view.toMap(event))

    // Right click -> Context Menu
    if (event.button !== 0) {
      const position = {
        x: event.x,
        y: event.y
      }

      const attributes = {}
      attributes.X = event.mapPoint.x
      attributes.Y = event.mapPoint.y
      attributes.LATITUDE = event.mapPoint.latitude
      attributes.LONGITUDE = event.mapPoint.longitude

      VueInst.$eventHub.$emit('showContextMenu', {
        attributes,
        position
      })
    }

    // Click -> tooltip layer
    view
      .hitTest(event)
      .then((response) => {
        const simGraphic = response.results.find(r => {
          const layerId = r.graphic?.layer?.id
          const simId = r.graphic?.attributes?.simId

          return simId && (
            layerId?.startsWith('layer-simulacion') ||
            layerId?.startsWith('layer-viento')
          )
        })

        if (simGraphic) {
          const simId = simGraphic.graphic.attributes.simId

          simulacionActivaId = simId

          VueInst.$eventHub.$emit('setSimulacionActiva', simId)

          return
        }

        let showCustomTooltip = false
        let showSimpleTooltip = false
        let layerId

        const graphicsFilter = response.results.filter((x) => {
          if (event.button === 0) {
            const layer = x.graphic.layer

            if (layer) {
              layerId = layer.id

              // Capas con tooltip custom
              const layersCustomTooltip = [
                'layer-medios-activos',
                'layer-medios-inactivos',
                'layer-track',
                'layer-incendios'
              ]

              showCustomTooltip = layersCustomTooltip.includes(layerId)

              if (layerId === 'layer-track' && x.graphic.symbol.type === 'simple-line') {
                // Si es la linea del track, no mostrar
                showCustomTooltip = false
              }

              if (showCustomTooltip) {
                return true
              }

              // Capas con coordenadas
              // ['layer-avion', 'layer-helicoptero', 'layer-pick-up']
              const layersWithPopup = [
                'layer-estaciones-meteorologicas',
                'layer-punto-agua-aer',
                'layer-punto-agua-ter',
                'layer-punto-encuentro',
                'layer-montes-utilidad',
                'layer-emergencias',
                'layer-imagen-satelite',
                'layer-infraestructuras-propias',
                'layer-infraestructuras',
                'layer-perimetros',
                'layer-municipios',
                'layer-autobomba',
                'layer-bomberos',
                'layer-BRIF',
                'layer-central-comunicaciones',
                'layer-repetidor-comunicaciones',
                'layer-reten',
                'layer-SOS',
                'layer-torreta-vigilancia',
                'layer-hotspots-viirs',
                'layer-hotspots-modis',
                'layer-hermes',
                'layer-rothermel-peninsula'
                // 'layer-vector-imagen-satelite'
              ]

              showSimpleTooltip = layersWithPopup.includes(layerId)
            }

            return showSimpleTooltip
          }
        })

        if (graphicsFilter.length > 0) {
          const graphic = graphicsFilter[0].graphic

          const highlightGraphic = []
          highlightGraphic.push(graphicsFilter[0].graphic)

          if (layerId === 'layer-contorno' || layerId === 'layer-track') {
            const layer = map.findLayerById('layer-track')

            for (const i of layer.graphics) {
              if (i.attributes.ID_MEDIO === graphic.attributes.ID_MEDIO) {
                highlightGraphic.push(i)
              }
            }
          }

          view.whenLayerView(graphic.layer).then((layerView) => {
            for (const i of highlightGraphic) {
              if (i.layer.id !== 'layer-rothermel-peninsula') {
                graphicHighlight = layerView.highlight(i)
                trackHighlight.push(graphicHighlight)
              }
            }
          })

          const position = {
            x: event.x,
            y: event.y + 3
          }

          const attributes = {
            ...graphic.attributes
          }

          if (showCustomTooltip) {
            attributes.LATITUDE = graphic.geometry.latitude
            attributes.LONGITUDE = graphic.geometry.longitude
          }

          if (attributes) {
            const popupData = {
              layer: graphic.layer.id,
              attributes,
              position
            }

            VueInst.$eventHub.$emit('showPopup', popupData)
          }
        }
      })
      .catch((err) => {
        VueInst.$log.error(err)
      })
  })

  view.on('pointer-up', (event) => {
    pointerDown = false
  })

  view.on('pointer-down', (event) => {
    pointerDown = true

    closeMapPopups()
  })

  // Popup functions
  view.popup.on('trigger-action', (event) => {
    if (event.action.id === 'measure-this') {
      // TODO: Esto es para los popups de ArcGIS con los medios, no se usa
    }
  })
}

function closeMapPopups() {
  // Cierra el popup / context menu y deselecciona el grafico seleccionado
  VueInst.$eventHub.$emit('showPopup', false)
  VueInst.$eventHub.$emit('showContextMenu', false)

  if (graphicHighlight) {
    graphicHighlight.remove()
    graphicHighlight = null
  }

  if (trackHighlight.length > 0) {
    for (let i = 0; i < trackHighlight.length; i++) {
      trackHighlight[i].remove()
    }

    trackHighlight.length = 0
  }
}

function sendCoordinates(point) {
  if (point) {
    const X = point.x.toFixed(2)
    const Y = point.y.toFixed(2)
    const LAT = point.latitude.toFixed(4).toString().replace('.', ',')
    const LON = point.longitude.toFixed(4).toString().replace('.', ',')
    const LATGMS = convertirLatLonGMS(point.latitude, 'LATITUDE').valor
    const LONGMS = convertirLatLonGMS(point.longitude, 'LONGITUDE').valor
    const ETRS89 = convertirETRS89(X, Y)
    const X_ETRS89 = ETRS89[0].toFixed(0).toString().replace('.', ',')
    const Y_ETRS89 = ETRS89[1].toFixed(0).toString().replace('.', ',')

    const coordenadas = {
      X,
      Y,
      LATITUDE: LAT,
      LONGITUDE: LON,
      LATITUDE_GMS: LATGMS,
      LONGITUDE_GMS: LONGMS,
      X_ETRS89,
      Y_ETRS89
    }
    VueInst.$eventHub.$emit('coordenadasMap', coordenadas)
  }
}
// #endregion

// #region LAYERS
export const reorderLayers = () => {
  const layersAdd = store.getters['map/layersAdd']
  const layersMap = map.layers.items

  for (let i = 0; i < layersAdd.length; i++) {
    const layerInMap = layersMap.find((x) => x.id === layersAdd[i].id)

    if (layerInMap) {
      map.reorder(layerInMap, layersAdd[i].posicion)
    }
  }
}

export const createLayers = () => {
  const thePromises = []
  const layerStore = store.getters['map/layersAdd']

  for (let i = 0; i < layerStore.length; i++) {
    const layerItem = layerStore[i]

    /* if (layerItem.type === 'WMS') {
      thePromises.push(createWMS(layerItem).then((layer) => {
        map.add(layer)
        store.dispatch('addLayer', {id: layerItem.id, title: layerItem.title, visible: layerItem.visible, posicion: layerItem.posicion, addListLayer: layerItem.addListLayer})
      }))
    } else if (layerItem.type === 'wmtsLayer') {
      thePromises.push(createWMTS(layerItem).then((layer) => {
        map.add(layer)
        store.dispatch('addLayer', {id: layerItem.id, title: layerItem.title, visible: layerItem.visible, posicion: layerItem.posicion, addListLayer: layerItem.addListLayer})
      }))
    } else if (layerItem.type === 'geoJsonPuntos') {
      thePromises.push(createGeoJsonPuntos(layerItem).then((layer) => {
        map.add(layer)
        store.dispatch('addLayer', {id: layerItem.id, title: layerItem.title, visible: layerItem.visible, posicion: layerItem.posicion, addListLayer: layerItem.addListLayer})
      }))
    } else */ if (layerItem.type === 'WMS') {
      thePromises.push(
        createWMS(layerItem).then((layer) => {
          map.add(layer)
          store.dispatch('map/addLayer', {
            id: layerItem.id,
            type: layerItem.type,
            title: layerItem.title,
            visible: layerItem.visible,
            featureInfoFormat: layerItem.featureInfoFormat,
            featureInfoUrl: layerItem.featureInfoUrl,
            popupEnabled: layerItem.popupEnabled,
            sublayers: layerItem.sublayers,
            opacity: layerItem.opacity,
            posicion: layerItem.posicion,
            addListLayer: layerItem.addListLayer,
            url: layerItem.baseUrl
          })
        })
      )
    } else if (layerItem.type === 'geoJson') {
      thePromises.push(
        createGeoJsonLayer(layerItem
        ).then((layer) => {
          map.add(layer)
          store.dispatch('map/addLayer', {
            id: layerItem.id,
            title: layerItem.title,
            visible: layerItem.visible,
            opacity: layerItem.opacity,
            posicion: layerItem.posicion,
            addListLayer: layerItem.addListLayer,
            url: layerItem.url
          })
        })
      )
    } else if (layerItem.type === 'featureLayer') {
      thePromises.push(
        createFeatureLayer(layerItem).then((layer) => {
          map.add(layer)
          store.dispatch('map/addLayer', {
            id: layerItem.id,
            title: layerItem.title,
            visible: layerItem.visible,
            opacity: layerItem.opacity,
            posicion: layerItem.posicion,
            addListLayer: layerItem.addListLayer,
            renderer: layer.renderer,
            fields: layer.fields
          })
        })
      )
    } else if (layerItem.type === 'graphicsLayer') {
      thePromises.push(
        createGraphicsLayer(layerItem).then((layer) => {
          map.add(layer)
          store.dispatch('map/addLayer', {
            id: layerItem.id,
            title: layerItem.title,
            visible: layerItem.visible,
            opacity: layerItem.opacity,
            posicion: layerItem.posicion,
            addListLayer: layerItem.addListLayer
          })
        })
      )
    } else if (layerItem.type === 'vectorTileLayer') {
      thePromises.push(
        createVectorTileLayer(layerItem).then((layer) => {
          map.add(layer)
          store.dispatch('map/addLayer', {
            id: layerItem.id,
            title: layerItem.title,
            visible: layerItem.visible,
            opacity: layerItem.opacity,
            posicion: layerItem.posicion,
            addListLayer: layerItem.addListLayer
          })
        })
      )
    } else if (layerItem.type === 'MediaLayer') {
      thePromises.push(
        createMediaLayer(layerItem).then((layer) => {
          map.add(layer)
          store.dispatch('map/addLayer', {
            id: layerItem.id,
            source: layerItem.source,
            title: layerItem.title,
            visible: layerItem.visible,
            opacity: layerItem.opacity,
            posicion: layerItem.posicion,
            addListLayer: layerItem.addListLayer
          })
        })
      )
    } /* else if (layer.type === 'simif') {
      thePromises.push(createWMSSimif(layer).then((layer) => {
        map.add(layer)
        store.dispatch('addLayer', {id: layerItem.id, title: layerItem.title, visible: layerItem.visible})
      }))
    } */
  }

  return Q.all(thePromises)
}
export const setExageration3D = (exageration) => {
  const layerEx = map.ground.layers.getItemAt(0)

  if (layerEx) {
    const layer = new ExaggeratedElevationLayerSubClass({
      exaggeration: exageration,
      url: layerEx.url
    })
    map.ground.layers.remove(layerEx)
    map.ground.layers.add(layer)
    // layer.exaggeration = exageration
  }
}
export const setVisibilityLayer = (id, visibility) => {
  if (map) {
    const layer = map.findLayerById(id)
    if (layer) {
      layer.visible = visibility
    }
  }
}

export const setVisibilitySubLayer = (idLayer, sublayer, visibility) => {
  if (map) {
    const layer = map.findLayerById(idLayer)
    if (!layer) return

    const sublayerIndex = layer.allSublayers.items.findIndex((x) => x.title === sublayer.title)
    if (sublayerIndex === -1) return

    layer.allSublayers.items[sublayerIndex].visible = visibility
  }
}

export const setOpacityLayer = (id, opacity) => {
  if (map) {
    const layer = map.findLayerById(id)

    if (layer) {
      layer.opacity = opacity
    }
  }
}

export const setDefinitionExpressionLayer = (id, definitionExpression) => {
  if (map) {
    const layer = map.findLayerById(id)
    if (layer) {
      layer.definitionExpression = definitionExpression
    }
  }
}

async function createFeatureLayer(layerItem) {
  const deferred = Q.defer()

  try {
    const [SpatialReference] = await loadModules([
      'esri/geometry/SpatialReference'
    ])

    let layer
    if (layerItem.url) {
      layer = new FeatureLayer({
        id: layerItem.id,
        url: layerItem.url,
        title: layerItem.title,
        visible: layerItem.visible,
        opacity: layerItem.opacity,
        spatialReference: SpatialReference.WGS84,
        outFields: ['*']
      })
    } else if (layerItem.idItem) {
      layer = new FeatureLayer({
        id: layerItem.id,
        // url: layerItem.url,
        title: layerItem.title,
        visible: layerItem.visible,
        opacity: layerItem.opacity,
        spatialReference: SpatialReference.WGS84,
        outFields: ['*'],
        portalItem: {
          id: layerItem.idItem
        }
      })
    } else if (layerItem.id === 'layer-infraestructuras') {
      layer = new FeatureLayer({
        id: layerItem.id,
        source: [],
        title: layerItem.title,
        visible: layerItem.visible,
        opacity: layerItem.opacity,
        objectIdField: 'ID',
        geometryType: 'point',
        spatialReference: SpatialReference.WGS84,
        renderer: await renderInfraestructuraLayer(),
        labelingInfo: await featureLayerlabels('NOMBRE'),
        labelsVisible: layerItem.labelsVisible,
        outFields: ['*'],
        fields: layerItem.fields
      })
    } else if (layerItem.id === 'layer-infraestructuras-propias') {
      layer = new FeatureLayer({
        id: layerItem.id,
        source: [],
        title: layerItem.title,
        visible: layerItem.visible,
        opacity: layerItem.opacity,
        objectIdField: 'ID',
        geometryType: 'point',
        spatialReference: SpatialReference.WGS84,
        renderer: await renderInfraestructuraPropiaLayer(),
        labelingInfo: await featureLayerlabels('NOMBRE'),
        labelsVisible: layerItem.labelsVisible,
        outFields: ['*'],
        fields: layerItem.fields
      })
    } else if (layerItem.id === 'layer-simulacion') {
      layer = new FeatureLayer({
        id: layerItem.id,
        source: [],
        title: layerItem.title,
        visible: layerItem.visible,
        opacity: layerItem.opacity,
        objectIdField: layerItem.objectIdField,
        geometryType: layerItem.geometryType,
        spatialReference: SpatialReference.WGS84,
        outFields: ['*'],
        fields: layerItem.fields,
        popupTemplate: {
          title: 'Simulación',
          content: [
            {
              type: 'fields',
              fieldInfos: [
                { fieldName: 'Elapsed_Mi', label: 'Tiempo (min)' },
                { fieldName: 'Fire_Type', label: 'Tipo' },
                { fieldName: 'Hour', label: 'Hora' },
                { fieldName: 'Day', label: 'Día' },
                { fieldName: 'Month', label: 'Mes' }
              ]
            }
          ]
        },
        renderer: {
          type: 'unique-value',
          field: 'COLOR',
          uniqueValueInfos: []
        }
      })
    } else if (layerItem.id === 'layer-isocronas') {
      layer = new FeatureLayer({
        id: layerItem.id,
        source: [],
        title: layerItem.title,
        visible: layerItem.visible,
        opacity: layerItem.opacity,
        objectIdField: layerItem.objectIdField,
        geometryType: layerItem.geometryType,
        spatialReference: SpatialReference.WGS84,
        outFields: ['*'],
        fields: layerItem.fields,
        renderer: {
          type: 'unique-value',
          field: 'COLOR',
          uniqueValueInfos: []
        }
      })
    } else {
      layer = new FeatureLayer({
        id: layerItem.id,
        source: [],
        title: layerItem.title,
        visible: layerItem.visible,
        opacity: layerItem.opacity,
        objectIdField: 'ID',
        render: {},
        geometryType: 'point',
        spatialReference: SpatialReference.WGS84,
        fields: layerItem.fields
      })
    }

    deferred.resolve(layer)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

async function featureLayerlabels(labelAtribute) {
  const [LabelClass] = await loadModules([
    'esri/layers/support/LabelClass'
  ])

  const stringExpression = labelAtribute && labelAtribute !== '' ? `$feature.${labelAtribute}` : '$feature.NOMBRE + \' - \' + $feature.PROVEEDOR'
  const statesLabelClass = new LabelClass({
    labelExpressionInfo: { expression: stringExpression },
    labelPlacement: 'below-center',
    symbol: {
      type: 'text', // autocasts as new TextSymbol()
      color: 'black',
      haloColor: 'white',
      haloSize: '2px',
      font: {
        // autocasts as new Font()
        size: 8,
        family: 'Arial Unicode MS',
        weight: 'bold'
      }
    }
  })
  return [statesLabelClass]
}

export function createGraphicsLayer(layerItem) {
  const deferred = Q.defer()

  try {
    const layer = new GraphicsLayer({
      id: layerItem.id,
      title: layerItem.title,
      visible: layerItem.visible,
      opacity: layerItem.opacity
    })

    deferred.resolve(layer)
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}

async function createVectorTileLayer(layerItem) {
  const deferred = Q.defer()

  try {
    const [VectorTileLayer] = await loadModules([
      'esri/layers/VectorTileLayer'
    ])

    const layer = new VectorTileLayer({
      id: layerItem.id,
      url: layerItem.url,
      title: layerItem.title,
      visible: layerItem.visible,
      opacity: layerItem.opacity,
      style: layerItem.style
    })

    deferred.resolve(layer)
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}

async function createMediaLayer(layerItem) {
  const deferred = Q.defer()

  try {
    const [MediaLayer] = await loadModules(['esri/layers/MediaLayer'])

    const layer = new MediaLayer({
      id: layerItem.id,
      source: [layerItem.source],
      title: layerItem.title,
      visible: layerItem.visible
      // blendMode: "color-dodge",
      // blendMode: "vivid-light",
      // blendMode: "screen",
      // blendMode: "reflect",
      // blendMode: 'overlay'
    })

    deferred.resolve(layer)
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}

async function createWMS(layerItem) {
  const deferred = Q.defer()

  try {
    const [WMSLayer] = await loadModules(['esri/layers/WMSLayer'])

    const layer = new WMSLayer({
      id: layerItem.id,
      url: layerItem.baseUrl,
      popupEnabled: layerItem.popupEnabled,
      featureInfoFormat: layerItem.featureInfoFormat,
      featureInfoUrl: layerItem.featureInfoUrl,
      sublayers: layerItem.sublayers,
      fetchFeatureInfoFunction: async(query) => {
        const [esriRequest] = await loadModules([
          'esri/request'
        ])
        // Explicitly request a GeoJSON response.
        query.info_format = 'application/json'

        // Request the first five features (if any).
        query.feature_count = 5

        // Perform the web request.
        const { data } = await esriRequest(layerItem.featureInfoUrl, { query })

        // Convert each GeoJSON feature into an Esri graphic.
        return data.features.map(
          (feature) =>
            new Graphic({
              attributes: feature.properties,

              // Define a popup template to format field names and values in a table.
              popupTemplate: {
                title: layerItem.title,
                content: [{
                  type: 'fields',
                  fieldInfos: layerItem.sublayerFieldInfos
                }]
              }
            })

        )
      },
      title: layerItem.title,
      visible: layerItem.visible,
      opacity: layerItem.opacity
    })
    deferred.resolve(layer)
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}

function getMinSpeed(features) {
  return features.reduce((min, current) =>
    min.properties.speed < current.properties.speed ? min : current
  ).properties.speed
}

function getMaxSpeed(features) {
  return features.reduce((max, current) =>
    max.properties.speed > current.properties.speed ? max : current
  ).properties.speed
}

export const redrawInfraestructuras = async() => {
  // store.dispatch('map/setVisibilityLayer', { visible: !store.getters['usuario/isCurrentUsuarioExterno'], id: 'layer-infraestructuras' })
  if (map) {
    const layerInfraestructurasPropias = map.findLayerById('layer-infraestructuras')

    if (layerInfraestructurasPropias) {
      const graphicsInfraestructuras = await createGraphicsInfraestructuras()

      const { features } = await layerInfraestructurasPropias.queryFeatures()
      await layerInfraestructurasPropias.applyEdits({
        addFeatures: graphicsInfraestructuras, // new updated features
        deleteFeatures: features // delete the old features
      })
    }
  }
}

function createGraphicsInfraestructuras() {
  const deferred = Q.defer()
  try {
    // Cambiar esta chapuza cuando haya tiempo de arreglar la chapuza de query nativa en backend y usar sequelize
    const bases = [...store.getters['base/bases']].filter(base => base.X && base.Y)
    const sources = []
    for (let i = 0; i < bases.length; i++) {
      const base = bases[i]
      const attributes = {
        ID_BASE: base.ID_BASE,
        NOMBRE: base.NOMBRE,
        X: base.X,
        Y: base.Y,
        icon: 'base.png'
      }
      const latLong = convertirETRS89ToLatLon(base.X, base.Y)
      const geometry = new Point({
        x: latLong[0],
        y: latLong[1]
      })

      const graphic = new Graphic({
        geometry,
        attributes
      })
      sources.push(graphic)
    }
    deferred.resolve(sources)
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}

async function renderInfraestructuraLayer() {
  const [UniqueValueRenderer, PictureMarkerSymbol] = await loadModules([
    'esri/renderers/UniqueValueRenderer',
    'esri/symbols/PictureMarkerSymbol'
  ])
  const renderer = new UniqueValueRenderer({
    field: 'TIPO_BASE',
    defaultSymbol: new PictureMarkerSymbol({
      url: 'icons/base.png', // URL por defecto si no hay coincidencia
      width: '25px',
      height: '25px'
    })
  })
  constants.iconosTiposBases.forEach(async icono => {
    const value = `${icono.URL}.png`
    renderer.addUniqueValueInfo({
      value: icono.TIPO,
      symbol: await createSymbolInfraestructuras(`icons/${value}`)
    })
  })
  return renderer
}

async function createSymbolInfraestructuras(icono) {
  const [PictureMarkerSymbol] = await loadModules([
    'esri/symbols/PictureMarkerSymbol'
  ])
  return new PictureMarkerSymbol({
    url: icono, // URL por defecto si no hay coincidencia
    width: '25px',
    height: '25px'
  })
}

function getWindRenderer2D(minSpeed, maxSpeed, colors) {
  return {
    type: 'simple',

    symbol: {
      type: 'simple-marker',
      path: 'm15.707 15.707-3 3a1 1 0 0 1-1.414 0l-3-3a1 1 0 0 1 1.414-1.414L11 15.586V6a1 1 0 0 1 2 0v9.586l1.293-1.293a1 1 0 0 1 1.414 1.414z',
      color: 'white',
      angle: 0,
      size: 10,
      outline: {
        color: [255, 255, 255, 0.5],
        width: 0
      }
    },
    visualVariables: [
      {
        type: 'rotation',
        field: 'direction',
        rotationType: 'geographic'
      },
      {
        type: 'color',
        field: 'speed',
        stops: [
          { value: minSpeed, color: colors?.minSpeed },
          { value: (maxSpeed / 2), color: colors?.medSpeed },
          { value: maxSpeed, color: colors?.maxSpeed }
        ],
        valueExpressionTitle: 'Intensidad viento',
        valueExpression: '3'
      },

      {
        type: 'size',
        field: 'speed',
        minDataValue: 0,
        maxDataValue: 5,
        minSize: 17,
        maxSize: 17,
        legendOptions: {
          showLegend: false
        }

      }
    ]
  }
}

function getWindRenderer3D(minSpeed, maxSpeed, colors) {
  return {
    type: 'simple',
    symbol: {
      type: 'point-3d',
      symbolLayers: [
        {
          type: 'object',
          material: { color: '#a38a8a' },
          resource: { href: '/models/flecha_3D.glb' },
          tilt: 0,
          width: 30,
          depth: 80,
          height: 30
        }
      ]
    },
    visualVariables: [
      {
        type: 'rotation',
        valueExpression: '$feature.direction + 180',
        axis: 'heading'
      },
      {
        type: 'color',
        field: 'speed',
        stops: [
          { value: minSpeed, color: colors?.minSpeed },
          { value: (maxSpeed / 2), color: colors?.medSpeed },
          { value: maxSpeed, color: colors?.maxSpeed }
        ]
      },
      // LONGITUD de la flecha
      {
        type: 'size',
        field: 'speed',
        axis: 'depth',
        stops: [
          { value: minSpeed, size: 50 },
          { value: maxSpeed, size: 200 }
        ]
      },
      // GROSOR vertical
      {
        type: 'size',
        field: 'speed',
        axis: 'height',
        stops: [
          { value: minSpeed, size: 15 },
          { value: maxSpeed, size: 30 }
        ]
      },
      // ANCHURA lateral
      {
        type: 'size',
        field: 'speed',
        axis: 'width',
        stops: [
          { value: minSpeed, size: 15 },
          { value: maxSpeed, size: 30 }
        ]
      }
    ]
  }
}

function getWindLineRenderer3D(minSpeed, maxSpeed, colors) {
  return {
    type: 'simple',
    symbol: {
      type: 'line-3d',
      symbolLayers: [
        {
          type: 'line',
          size: 2,
          material: { color: '#ffaa00' }
        }
      ]
    },
    visualVariables: [
      {
        type: 'color',
        field: 'speed',
        stops: [
          { value: minSpeed, color: colors?.minSpeed },
          { value: (maxSpeed / 2), color: colors?.medSpeed },
          { value: maxSpeed, color: colors?.maxSpeed }
        ]
      }
    ]
  }
}

export async function createGeoJsonLayer(layerItem) { //, is3d = false
  const deferred = Q.defer()

  try {
    const [GeoJSONLayer] = await loadModules(['esri/layers/GeoJSONLayer'])

    let aux = layerItem.url[0]
    let url = layerItem.url

    if (layerItem.url.length === 0) {
      aux = {
        type: 'FeatureCollection',
        name: 'polygon',
        crs: { type: 'name', properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' } },
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [
                [
                  [100.0, 0.0]
                ]
              ]
            },
            properties: {
              Shape_Leng: 100,
              Shape_Area: null,
              VALUE: null
            }
          }
        ]
      }

      const blob = new Blob([JSON.stringify(aux)], { // layerItem.url | geojson
        type: 'application/json'
      })

      // URL reference to the blob
      url = URL.createObjectURL(blob)
    }

    // create new geojson layer using the blob url
    let layer
    if (layerItem.id === 'layer-trabajo-pistas' || layerItem.id === 'layer-puntos-agua') {
      layer = new GeoJSONLayer({
        url, // Using Object as attribute value for GeoJSON features is not supported.
        id: layerItem.id,
        title: layerItem.title,
        visible: layerItem.visible,
        opacity: layerItem.opacity,
        legendEnabled: layerItem.legendEnabled,
        popupEnabled: layerItem.popupEnabled,
        popupTemplate: layerItem.popupTemplate ?? false,
        outFields: ['*']
      })
    } else {
      layer = new GeoJSONLayer({
        url, // Using Object as attribute value for GeoJSON features is not supported.
        id: layerItem.id,
        title: layerItem.title,
        visible: layerItem.visible,
        opacity: layerItem.opacity,
        legendEnabled: layerItem.legendEnabled,
        popupEnabled: layerItem.popupEnabled,
        outFields: ['*']
      })
    }
    deferred.resolve(layer)
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}

function createGraphicsPoligonos(response) {
  const deferred = Q.defer()

  try {
    const features = response.features.map((feature, i) => {
      return {
        geometry: new Polygon({
          hasZ: false,
          hasM: false,
          rings: feature.geometry.rings,
          spatialReference: {
            wkid: 3857
          }
        }),
        attributes: {
          // Nombre: feature.properties.Nombre
        }
      }
    })
    deferred.resolve(features)
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}

export const setLabelsLayer = (id, labelsVisible) => {
  if (map) {
    const layer = map.findLayerById(id)

    if (layer) {
      layer.labelsVisible = labelsVisible
    }
  }
}
// #endregion

// #region HERMES
export const redrawHermes = async() => {
  if (map) {
    const layer = map.findLayerById('layer-hermes')

    if (layer) {
      const newGraphics = createGraphicsHermes()

      layer.removeAll()
      layer.addMany(newGraphics)
    }
  }
}

function createGraphicsHermes() {
  try {
    const dispositivosHermes = store.getters['medio/dispositivosHermes']
    const graphics = []

    for (const dispositivo of dispositivosHermes) {
      if (!dispositivo.GEO_POS_ACTUAL[0]) continue

      const geometry = new Point({
        x: dispositivo.GEO_POS_ACTUAL[0].LONGITUD,
        y: dispositivo.GEO_POS_ACTUAL[0].LATITUD
      })

      const attributes = {
        NOMBRE: dispositivo.GEO_DISPOSITIVO.CODIGO_EXTERNO,
        PROVEEDOR: dispositivo.GEO_DISPOSITIVO.PROVEEDOR.NOMBRE,
        FECHA: DateService.toString(DateService.toDayjs(dispositivo.GEO_POS_ACTUAL[0].FECHA), FMT.display),
        LONGITUD: `${dispositivo.GEO_POS_ACTUAL[0].LONGITUD.toFixed(5)}`,
        LATITUD: `${dispositivo.GEO_POS_ACTUAL[0].LATITUD.toFixed(5)}`
      }

      graphics.push(
        new Graphic({
          geometry,
          attributes,
          symbol: {
            type: 'picture-marker',
            url: 'icons/hermes_0.png',
            width: '40px',
            height: '40px'
          }
        })
      )

      if (store.getters['map/etiquetasVisibles']) {
        graphics.push(
          new Graphic({
            geometry,
            attributes,
            symbol: {
              type: 'text',
              color: 'black',
              haloColor: 'white',
              haloSize: '2px',
              text: attributes.NOMBRE,
              xoffset: 0,
              yoffset: -22,
              font: {
                size: 8,
                family: 'Arial Unicode MS',
                weight: 'bold'
              }
            }
          })
        )
      }
    }

    return graphics
  } catch (err) {
    console.error('Error al crear gráficos de hermes: ', err)
    return []
  }
}

// #endregion

// #region INCENDIOS
export const redrawIncendios = async() => {
  // console.time('REDRAWINCENDIOS')
  if (map) {
    const layer = map.findLayerById('layer-incendios')

    if (layer) {
      const graphicsIncendios = await createGraphicsIncendios()
      layer.graphics.items = graphicsIncendios
    }
  }

  // console.timeEnd('REDRAWINCENDIOS')
}
function createGraphicsIncendios() {
  const deferred = Q.defer()

  try {
    const svgPath = constants.incendioSVGPath

    const incendios = store.getters['incendio/incendiosReducedFiltered']
    const estadosIncendio = store.getters['incendio/tiposIncendioEstado']
    const sources = []
    let color = null

    for (let i = 0; i < incendios.length; i++) {
      const incendio = incendios[i]
      if (incendio.NIVELES.length > 0 && !incendio.NIVELES[0].FECHA_FIN) {
        color = incendio.NIVELES[0].TIPO_INCENDIO_NIVEL.COLOR
      } else {
        color = estadosIncendio.find(
          (x) =>
            x.ID_TIPO_INCENDIO_ESTADO ===
            incendios[i].ESTADOS[0].ID_TIPO_INCENDIO_ESTADO
        ).COLOR
      }

      const attributes = {
        ID_INCENDIO: incendio.ID_INCENDIO,
        NOMBRE: incendio.NOMBRE,
        MUNICIPIO: incendio.MUNICIPIO,
        LOCALIDAD: incendio.LOCALIDAD,
        OBSERVACIONES: incendio.OBSERVACIONES,
        ID_TIPO_DETECCION: incendio.ID_TIPO_DETECCION
      }
      const geometry = new Point({
        x: incendio.LONGITUD,
        y: incendio.LATITUD
      })

      sources.push({
        // Circulo blanco de fondo
        geometry,
        attributes,
        symbol: {
          type: 'simple-marker',
          style: 'circle',
          color: 'white',
          size: '32px',
          outline: {
            color: 'white'
          }
        }
      })
      sources.push({
        // Icono
        geometry,
        attributes,
        symbol: {
          type: 'simple-marker',
          path: svgPath,
          color,
          size: '25px',
          outline: {
            color
          }
        }
        // popupTemplate: popupTemplate
      })
      sources.push({
        // Texto
        geometry,
        attributes,
        symbol: {
          type: 'text', // autocasts as new TextSymbol()
          color: 'black',
          haloColor: 'white',
          haloSize: '2px',
          text: incendios[i].NOMBRE
            ? incendios[i].NOMBRE
            : incendios[i].MUNICIPIO,
          xoffset: 0,
          yoffset: -22,
          font: {
            // autocasts as new Font()
            size: 8,
            family: 'Arial Unicode MS',
            weight: 'bold'
          }
        }
      })
    }
    deferred.resolve(sources)
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}
export const paintPositionIncendio = (point) => {
  try {
    mapView.graphics = []

    if (point) {
      const symbol = {
        type: 'simple-marker',
        style: 'x',
        color: 'white',
        size: '15px',
        outline: {
          color: 'red',
          width: '2px'
        }
      }

      const geometry = new Point({
        x: parseFloat(point.LONGITUD),
        y: parseFloat(point.LATITUD)
      })

      const graphic = new Graphic({
        geometry,
        symbol
      })

      mapView.graphics.add(graphic)
    }
  } catch (err) {
    VueInst.$log.error(err)
  }
}
// #endregion

// #region INFRAESTRUCTURAS

export const redrawInfraestructurasPropias = async() => {
  if (map) {
    const layerlayerInfraestructurasPropias = map.findLayerById('layer-infraestructuras-propias')

    if (layerlayerInfraestructurasPropias) {
      const graphicsInfraestructurasPropias = await createGraphicsInfraestructurasPropias()

      const { features } = await layerlayerInfraestructurasPropias.queryFeatures()
      await layerlayerInfraestructurasPropias.applyEdits({
        addFeatures: graphicsInfraestructurasPropias, // new updated features
        deleteFeatures: features // delete the old features
      })
    }
  }
}

function createGraphicsInfraestructurasPropias() {
  const deferred = Q.defer()
  try {
    const infraestructuras = store.getters['infraestructura/infraestructuras'].filter(infra => infra.X && infra.Y && !infra.BORRADO)
    const tiposInfraestructuras = store.getters['infraestructura/tipos']
    const categoriasInfraestructuras = store.getters['infraestructura/categorias']
    const municipiosInfraestructuras = store.getters['infraestructura/municipios']
    const provinciasInfraestructuras = store.getters['infraestructura/provincias']
    const organismosInfraestructuras = store.getters['infraestructura/organismos']
    const sources = []
    for (let i = 0; i < infraestructuras.length; i++) {
      const infraestructura = infraestructuras[i]
      const tipo = tiposInfraestructuras.find(x => x.ID_INFRAESTRUCTURA_TIPO === infraestructura.ID_INFRAESTRUCTURA_TIPO)
      const municipio = municipiosInfraestructuras.find(x => x.ID_MUNICIPIO === infraestructura.ID_MUNICIPIO)
      const provincia = provinciasInfraestructuras.find(x => x.ID_INE_PROVINCIA === infraestructura.ID_INE_PROVINCIA)
      const attributes = {
        ID_INFRAESTRUCTURA: infraestructura.ID_INFRAESTRUCTURA,
        TIPO: tipo ? tipo.NOMBRE : null,
        CATEGORIA: categoriasInfraestructuras.find(x => x.ID_INFRAESTRUCTURA_CATEGORIA === tipo.ID_INFRAESTRUCTURA_CATEGORIA)
          ? categoriasInfraestructuras.find(x => x.ID_INFRAESTRUCTURA_CATEGORIA === tipo.ID_INFRAESTRUCTURA_CATEGORIA).NOMBRE
          : null,
        MUNICIPIO: municipio ? municipio.NOMBRE : null,
        PROVINCIA: provincia ? provincia.LITERAL : null,
        ORGANISMO: organismosInfraestructuras.find(x => x.ID_ORGANISMO === infraestructura.ID_ORGANISMO)
          ? organismosInfraestructuras.find(x => x.ID_ORGANISMO === infraestructura.ID_ORGANISMO).ORGANISMO
          : null,
        NOMBRE: infraestructura.NOMBRE,
        FECHA_ALTA: infraestructura.FECHA_ALTA,
        FECHA_BAJA: infraestructura.FECHA_BAJA,
        BORRADO: infraestructura.BORRADO,
        DESCRIPCION: infraestructura.DESCRIPCION,
        DIRECION: infraestructura.DIRECION,
        CODIGO: infraestructura.CODIGO,
        icon: `${constants.iconosTiposInfraestructuras.find(icono => icono.TIPO === tipo.NOMBRE).URL}.png`
      }
      const latLong = convertirETRS89ToLatLon(infraestructura.X, infraestructura.Y)
      const geometry = new Point({
        x: latLong[0],
        y: latLong[1]
      })

      const graphic = new Graphic({
        geometry,
        attributes
      })
      sources.push(graphic)
    }
    deferred.resolve(sources)
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}

async function renderInfraestructuraPropiaLayer() {
  const [UniqueValueRenderer, PictureMarkerSymbol] = await loadModules([
    'esri/renderers/UniqueValueRenderer',
    'esri/symbols/PictureMarkerSymbol'
  ])

  const renderer = new UniqueValueRenderer({
    field: 'TIPO',
    defaultSymbol: new PictureMarkerSymbol({
      url: 'icons/base.png', // Icono por defecto para todas las infraestructuras
      width: '40px',
      height: '40px'
    })
  })

  /*
  constants.iconosTiposInfraestructuras.forEach(async icono => {
    let value = icono.URL + '.png';
    renderer.addUniqueValueInfo({
      value: icono.TIPO,
      symbol: await createSymbolInfraestructurasPropias('icons/' + value)
    });
  });
  */

  return renderer
}

async function createSymbolInfraestructurasPropias(icono) {
  const [PictureMarkerSymbol] = await loadModules([
    'esri/symbols/PictureMarkerSymbol'
  ])
  return new PictureMarkerSymbol({
    url: icono, // URL por defecto si no hay coincidencia
    width: '40px',
    height: '40px'
  })
}
// #endregion

// #region MEDIOS
let isPaintingMedios = false // Evitar que pinte multiples veces // TODO: dara problemas con hermes?
export const redrawMedios = async() => {
  if (!isPaintingMedios) {
    isPaintingMedios = true

    // console.time('REDRAWMEDIOS')
    if (map) {
      const layerMediosInactivos = map.findLayerById('layer-medios-inactivos')
      const layerMediosActivos = map.findLayerById('layer-medios-activos')
      const layerEtiquetasAct = map.findLayerById('layer-etiquetas-activos')
      const layerEtiquetasInact = map.findLayerById('layer-etiquetas-inactivos')

      if (
        layerMediosInactivos &&
        layerMediosActivos &&
        layerEtiquetasInact &&
        layerEtiquetasAct
      ) {
        const graphicsMedios = await createGraphicsMedios()
        layerMediosInactivos.graphics.items = graphicsMedios.INACTIVOS
        layerMediosActivos.graphics.items = graphicsMedios.ACTIVOS
        layerEtiquetasAct.graphics.items = graphicsMedios.ETIQUETAS_ACTIVOS
        layerEtiquetasInact.graphics.items = graphicsMedios.ETIQUETAS_INACTIVOS
      }
    }
    // console.timeEnd('REDRAWMEDIOS')

    isPaintingMedios = false
  }
}

let drawLines = false
export function drawLineas(draw) {
  drawLines = draw
}

function createGraphicsMedios() {
  const deferred = Q.defer()

  const verMultiplesDispositivos = constants.verMultiplesDispositivos

  try {
    const now = VueInst.$date.now()
    const tiempoInactivo = parseInt(
      store.getters['parametro/getParametro']('TIEMPO_POS_INACTIVO')
    )
    const tiempoDesactivado = parseInt(
      store.getters['parametro/getParametro']('TIEMPO_POS_DESACTIVADO')
    )
    const fechaMargenInactivo = VueInst.$date.subtract(
      now,
      tiempoInactivo,
      'minute'
    )
    const fechaMargenDesactivado = VueInst.$date.subtract(
      now,
      tiempoDesactivado,
      'day'
    )

    const estadosMedios = constants.estadosMedio
    const medios = store.getters['medio/medios']
    const incendios = store.getters['incendio/incendiosReduced']

    const sourcesMediosInactivos = []
    const sourcesMediosActivos = []
    const sourcesEtiquetasActivos = []
    const sourcesEtiquetasInactivos = []

    /* const popupActions = [
      { title: 'Track', id: 'measure-this', className: 'esri-icon-tracking' },
      { title: 'Track entre fechas', id: 'measure-this', className: 'esri-icon-calendar' }
    ]

    let popupTemplate = {
      title: 'Medio {MEDIO}',
      content: [{
        type: 'fields',
        fieldInfos: [
          { fieldName: 'NOMBRE', label: 'Nombre' },
          { fieldName: 'TELEFONO_INTERNO', label: 'Tel. interno' },
          { fieldName: 'ZONA', label: 'Zona' },
          { fieldName: 'FECHA', label: 'Fecha ult. señal' },
          { fieldName: 'TIPO', label: 'Tipo' },
          { fieldName: 'CATEGORIA', label: 'Categoría' },
          { fieldName: 'HORA_ENTRADA', label: 'Hora entrada' },
          { fieldName: 'HORA_SALIDA', label: 'Hora salida' }
        ]
      }],
      actions: popupActions
    } */

    /* let medio = {}
    var ComponentClass = Vue.extend(MedioPopup)
    var instance = new ComponentClass({
      propsData: { medio: medio }
    })
    instance.$mount() // pass nothing
    let popupTemplate = {
      title: 'Medio {MEDIO}',
      content: instance.$el
    } */

    for (let i = 0; i < medios.length; i++) {
      const medio = medios[i]

      const lastPosition = medio.ULT_POSICION

      if (lastPosition && lastPosition.LATITUD && lastPosition.LONGITUD) {
        // Icono segun tipo
        let icon = constants.iconosTiposMedios.find((x) => x.TIPO === medio.TIPO)
          ? constants.iconosTiposMedios.find((x) => x.TIPO === medio.TIPO).URL
          : 'autobomba'

        // Color segun estado
        const color = estadosMedios.find((x) => x.ID === medio.ESTADO) ? estadosMedios.find((x) => x.ID === medio.ESTADO).COLOR : '#FFFFFF'
        const colorId = estadosMedios.find((x) => x.ID === medio.ESTADO) ? estadosMedios.find((x) => x.ID === medio.ESTADO).ID : 24

        // Atributos
        const attr = {
          ID_MEDIO: medio.ID_MEDIO,
          NOMBRE: medio.NOMBRE,
          ZONA: medio.ZONA,
          GUARDIAS: medio.GUARDIAS,
          TELEFONO_INTERNO: medio.TELEFONO_INTERNO,
          TELEFONO_EXTERNO: medio.TELEFONO_EXTERNO,
          // FECHA: DateService.formatDate(medio.FECHA, 'DD/MM/YYYY HH:mm'),
          TIPO: medio.TIPO,
          CATEGORIA: medio.CATEGORIA,
          MEDIO: medio.MEDIO,
          HORA_ENTRADA: medio.HORA_ENTRADA,
          HORA_SALIDA: medio.HORA_SALIDA,

          ID_DISPOSITIVO: lastPosition.ID_GEO_DISPOSITIVO,
          CODIGO_EXTERNO: lastPosition.CODIGO_EXTERNO,
          FECHA: DateService.toString(DateService.toDayjs(lastPosition.FECHA), FMT.date),
          PROVEEDOR: lastPosition.PROVEEDOR
        }

        // Punto
        const geometry = new Point({
          x: lastPosition.LONGITUD,
          y: lastPosition.LATITUD
        })

        // Rombo emergencia
        const graphicRombo = {
          geometry,
          attributes: attr,
          symbol: {
            type: 'simple-marker',
            style: 'diamond',
            color: 'yellow',
            size: '20px',
            outline: {
              color: 'black',
              width: '1px'
            }
          }
        }

        let yoffset = -22
        if (medio.EN_EMERGENCIA) {
          yoffset = -30
        }

        // Label texto
        const graphicLabel = {
          geometry,
          attributes: attr,
          symbol: {
            type: 'text', // autocasts as new TextSymbol()
            color: 'black',
            haloColor: 'white',
            haloSize: '2px',
            text: medio.MEDIO,
            xoffset: 0,
            yoffset,
            font: {
              // autocasts as new Font()
              size: 8,
              family: 'Arial Unicode MS',
              weight: 'bold'
            }
          }
        }

        // Linea a incendio
        let graphicLine = null
        const graphicAlerta = null
        for (let k = 0; k < incendios.length; k++) {
          // Si esta asignado a un incendio, crea una linea que los une (A LA ULTIMA POSICION)
          const incendio = incendios[k]

          if (incendio.ID_INCENDIO === medio.ID_INCENDIO) {
            // EN CONFIGURACION IGUAL LINEAS
            if ((medio.ESTADO === 24 || medio.ESTADO === 7) && drawLines) {
              // Solo pinta linea para alarmado y llegada inc
              graphicLine = {
                geometry: {
                  type: 'polyline',
                  paths: [
                    [lastPosition.LONGITUD, lastPosition.LATITUD],
                    [incendio.LONGITUD, incendio.LATITUD]
                  ]
                },
                attributes: {
                  notShowMaptip: true
                },
                symbol: {
                  type: 'simple-line',
                  style: 'short-dot',
                  color,
                  width: 2
                }
              }
            }

            // Comprobar fecha salida planificada
            // TODO:
            /* let actuaciones = []
            for (let m = 0; m < incendio.SECTORES.length; m++) {
              for (let n = 0; n < incendio.SECTORES[m].MEDIOS.length; n++) {
                let medioSector = incendio.SECTORES[m].MEDIOS[n]

                if (medioSector.ID_MEDIO === medio.ID_MEDIO) {
                  actuaciones.push(medioSector)
                }
              }
            }
            actuaciones = actuaciones.sort((a, b) => {
              return moment(a.FECHA_ALARMADO).valueOf() - moment(b.FECHA_ALARMADO).valueOf()
            })
            let lastActuacion = actuaciones[actuaciones.length - 1]
            let planificacion = lastActuacion.PLANIFICACION
            if (planificacion !== null && planificacion !== undefined) {
              let inicioJornada = moment(planificacion.INICIO_JORNADA).locale('es')
              let tiempoOperativo = planificacion.TIEMPO_OPERATIVO
              let fechaMaximaActuacion = moment(inicioJornada).add(tiempoOperativo, 'hours')
              let horaMovilizacion = lastActuacion.FECHA_ALARMADO ? moment(lastActuacion.FECHA_ALARMADO) : moment(lastActuacion.FECHA_AVISO_PLANIF)
              let horaLlegadaInc = lastActuacion.FECHA_EN_CAMINO ? moment(lastActuacion.FECHA_EN_CAMINO) : moment(lastActuacion.FECHA_LLEGADA_PLANIF)
              let itinere = horaLlegadaInc.diff(horaMovilizacion, 'minutes')
              let fechaSalidaPlanif = moment(fechaMaximaActuacion).subtract(itinere, 'minutes')
              // let horaSalidaPlanif = fechaSalidaPlanif.format('HH:mm')

              // Comprobar si esta dentro de horario
              // let horaSalidaMedio = moment(medio.HORA_SALIDA, 'HH:mm').format('HH:mm')
              let fechaActual = moment()
              // let horaActual = fechaActual.format('HH:mm')
              if ((fechaSalidaPlanif && (fechaActual > fechaSalidaPlanif))) { // || horaActual > horaSalidaMedio
                graphicAlerta = {
                  geometry: geometry,
                  attributes: attr,
                  symbol: {
                    type: 'simple-marker',
                    path: constants.iconAlertaMedio,
                    color: 'red',
                    size: '15px',
                    outline: {
                      color: 'red'
                    },
                    xoffset: '15px',
                    yoffset: '15px'
                  }
                }
              }
            } else {
              console.error(lastActuacion)
            } */
          }
        }

        if (medio.MEDIO_DISPOSITIVOS) {
          if (verMultiplesDispositivos) {
            // Pintar cada dispositivo como un punto separado
            for (let j = 0; j < medio.MEDIO_DISPOSITIVOS.length; j++) {
              const dispositivo = medio.MEDIO_DISPOSITIVOS[j]

              // Atributos del dispositivo
              const attrDisp = JSON.parse(JSON.stringify(attr))
              attrDisp.ID_DISPOSITIVO = dispositivo.ID_DISPOSITIVO
              attrDisp.CODIGO_EXTERNO = dispositivo.CODIGO_EXTERNO
              attrDisp.FECHA = DateService.toString(
                DateService.toDayjs(dispositivo.FECHA),
                FMT.date
              )
              attrDisp.PROVEEDOR = dispositivo.PROVEEDOR

              const geometryDisp = new Point({
                x: dispositivo.LONGITUD,
                y: dispositivo.LATITUD
              })

              // Label
              const graphicLabelDisp = new Graphic(graphicLabel)
              graphicLabelDisp.geometry = geometryDisp
              graphicLabelDisp.attributes = attrDisp
              graphicLabelDisp.symbol.text += ` - ${dispositivo.PROVEEDOR}`

              // LINEA MEDIO -> DISPOSITIVOS
              /* let graphicLineMedioConDispositivos = null
              // Si el medio tienen asignados uno o varios dispositivos, se crea una línea que los une
              graphicLineMedioConDispositivos = {
                geometry: {
                  type: 'polyline',
                  paths: [[medio.LONGITUD, medio.LATITUD], [dispositivo.LONGITUD, dispositivo.LATITUD]]
                },
                attributes: {
                  notShowMaptip: true
                },
                symbol: {
                  type: 'simple-line',
                  style: 'short-dot',
                  color: colorDispositivo,
                  width: 8
                }
              } */

              const lastPosFecha = VueInst.$date.parseDate(dispositivo.FECHA)
              const graficos = []

              if (medio.EN_EMERGENCIA) {
                const graphicRomboDisp = new Graphic(graphicRombo)
                graphicRomboDisp.geometry = geometryDisp
                graficos.push(graphicRomboDisp)
              }

              /* if (graphicLineMedioConDispositivos) {
                graficos.push(graphicLineMedioConDispositivos)
              } */

              let iconDispositivo = icon
              if (!iconDispositivo) {
                iconDispositivo = 'tecnico_extincion'
              }
              iconDispositivo =
                `${iconDispositivo +
                (lastPosFecha >= fechaMargenInactivo ? `_${colorId}` : '_19')
                }.png`

              // Graphic principal
              const graphic = {
                geometry: geometryDisp,
                attributes: attrDisp,
                symbol: {
                  type: 'picture-marker', // autocasts as new PictureMarkerSymbol()
                  // url: constants.server.URLSERVER + '/images/icons/' + icon,
                  url: `icons/${iconDispositivo}`,
                  width: '20px',
                  height: '20px'
                }
                // popupTemplate: popupTemplate
              }
              graficos.push(graphic)

              if (graphicLine) graficos.push(graphicLine)
              if (graphicAlerta) graficos.push(graphicAlerta)

              // Comprobar si el dispositivo asociado está activo, inactivo o desactivado.
              if (lastPosFecha < fechaMargenInactivo && !medio.ID_INCENDIO) {
                // Inactivo
                if (lastPosFecha >= fechaMargenDesactivado) {
                  // No desactivado
                  sourcesMediosInactivos.push(...graficos)
                  sourcesEtiquetasInactivos.push(graphicLabelDisp)
                }
              } else {
                // Activo
                sourcesMediosActivos.push(...graficos)
                sourcesEtiquetasActivos.push(graphicLabelDisp)
              }
            }
          } else {
            // Pintar la ultima posicion del medio
            // Label
            const graphicLabelDisp = new Graphic(graphicLabel)
            graphicLabelDisp.symbol.text += ` - ${lastPosition.PROVEEDOR}`

            const lastPosFecha = VueInst.$date.parseDate(lastPosition.FECHA)
            const graficos = []

            if (medio.EN_EMERGENCIA) {
              graficos.push(graphicRombo)
            }

            // Color del icono
            if (!icon) {
              icon = 'tecnico_extincion'
            }
            icon =
              `${icon +
              (lastPosFecha >= fechaMargenInactivo ? `_${colorId}` : '_19')
              }.png`

            // Graphic principal
            const graphic = {
              geometry,
              attributes: attr,
              symbol: {
                type: 'picture-marker', // autocasts as new PictureMarkerSymbol()
                // url: constants.server.URLSERVER + '/images/icons/' + icon,
                url: `icons/${icon}`,
                width: '40px',
                height: '40px'
              }
              // popupTemplate: popupTemplate
            }
            graficos.push(graphic)

            if (graphicLine) graficos.push(graphicLine)
            if (graphicAlerta) graficos.push(graphicAlerta)

            // Comprobar si esta activo, inactivo o desactivado
            if (lastPosFecha < fechaMargenInactivo && !medio.ID_INCENDIO) {
              // Inactivo
              if (lastPosFecha >= fechaMargenDesactivado) {
                // No desactivado
                sourcesMediosInactivos.push(...graficos)
                sourcesEtiquetasInactivos.push(graphicLabelDisp)
              }
            } else {
              // Activo
              sourcesMediosActivos.push(...graficos)
              sourcesEtiquetasActivos.push(graphicLabelDisp)
            }
          }
        }
      }
    }

    deferred.resolve({
      INACTIVOS: sourcesMediosInactivos,
      ACTIVOS: sourcesMediosActivos,
      ETIQUETAS_ACTIVOS: sourcesEtiquetasActivos,
      ETIQUETAS_INACTIVOS: sourcesEtiquetasInactivos
    })
  } catch (err) {
    VueInst.$log.error(err)
  }
  return deferred.promise
}
// #endregion

// #region TRACKS
export const drawTrack = async(idMedio, medio, positions, activateGOTO) => {
  if (map) {
    const layer = map.findLayerById('layer-track')
    const newPositions = await calcularVelocidad(positions)

    const graphics = await createGraphicsTrack(idMedio, medio, newPositions)
    layer.addMany(graphics)
    if (activateGOTO) {
      mapView.goTo(graphics, {
        duration: 3000,
        easing: 'in-expo'
      })
    }

    layer.visible = true
  }
}

export const hideTrack = (idMedio) => {
  if (map) {
    const layer = map.findLayerById('layer-track')
    layer.removeMany(
      layer.graphics.items.filter((x) => x.attributes.ID_MEDIO === idMedio)
    )
    layer.visible = true
  }
}

export const exportTrack = (data) => {
  if (map) {
    const layer = map.findLayerById('layer-track')

    const points = layer.graphics.items.filter(
      (x) => x.attributes.ID_MEDIO === data.idMedio
    )
    points.sort((a, b) => (a.attributes.FECHA > b.attributes.FECHA ? 1 : -1))

    const fechas = points.map((x) => x.attributes.FECHA).filter((x) => x)
    const fechaInicio = fechas[0]

    // Construir GEOJSON
    const features = [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: points
            .map((x) => x.geometry)
            .map((x) => [x.longitude, x.latitude])
        },
        properties: {
          MEDIO: data.medio
        }
      }
    ]

    for (let i = 0; i < points.length; i++) {
      if (!points[i].attributes.VELOCIDAD) {
        points[i].attributes.VELOCIDAD = 0
      }
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            points[i].geometry.longitude,
            points[i].geometry.latitude
          ]
        },
        properties: points[i].attributes
      })
    }

    const geoJson = {
      type: 'FeatureCollection',
      features
    }

    const nombre = `${data.medio}_${fechaInicio}`

    // Fichero SHP
    try {
      const options = {
        folder: `Track_${data.medio}`,
        types: {
          point: `PUNTOS_${nombre}`,
          polygon: `POLIGONOS${nombre}`,
          line: `LINEAS${nombre}`,
          polyline: `POLYLINE_${nombre}`
        }
      }
      shpwrite.download(geoJson, options)
    } catch (err) {
      console.error('Error generando shp', err)
    }

    // Fichero KML
    try {
      const kml = toKML(geoJson)
      const element = document.createElement('a')
      element.setAttribute(
        'href',
        `data:text/plain;charset=utf-8,${encodeURIComponent(kml)}`
      )
      element.setAttribute('download', `Track_${nombre}.kml`)
      element.style.display = 'none'
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    } catch (err) {
      console.error('Error generando kml', err)
    }
  }
}

const normalizeFileName = (text = '') => {
  return text
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
}

const buildPolygonGeometry = (grafico) => {
  try {
    const rings = JSON.parse(grafico.GEOMETRIA)

    if (!Array.isArray(rings) || rings.length < 3) {
      return null
    }

    const uniqueCoords = new Set(rings.map(coord => coord.join(',')))

    if (uniqueCoords.size < 3) {
      return null
    }

    const first = rings[0]
    const last = rings[rings.length - 1]

    const isClosed = first[0] === last[0] && first[1] === last[1]

    if (!isClosed) {
      rings.push(first)
    }

    return [rings]
  } catch {
    return null
  }
}

const buildProperties = (perimetro, grafico) => {
  const simbolo = JSON.parse(grafico.SIMBOLO)
  const layer = simbolo?.data?.symbol?.symbolLayers?.[0]

  return {
    NOMBRE: perimetro.NOMBRE || '',
    FECHA: perimetro.FECHA || '',
    COLOR: JSON.stringify(layer?.color || []),
    WIDTH: layer?.width || 1,
    DASH: !!layer?.effects
  }
}

const buildGeoJsonPerimetro = (perimetro) => {
  const features = []

  perimetro.GRAFICOS_PERIMETRO
    ?.filter(g => g.TIPO === 'polygon' && g.GEOMETRIA)
    .forEach(grafico => {
      const geometry = buildPolygonGeometry(grafico)

      if (!geometry) {
        return
      }

      features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: geometry
        },
        properties: buildProperties(
          perimetro,
          grafico
        )
      })
    })

  return {
    type: 'FeatureCollection',
    features
  }
}

const generateKMLFiles = (zip, perimetros) => {
  perimetros.forEach(perimetro => {
    const geoJson = buildGeoJsonPerimetro(
      perimetro
    )

    if (!geoJson.features.length) {
      return
    }

    const nombrePerimetro = normalizeFileName(perimetro.NOMBRE)
    const geoJsonTransformado = transformarGeoJsonAEPSG4326(geoJson)
    const kml = toKML(geoJsonTransformado)

    zip.file(`${nombrePerimetro}.kml`, kml)
  })
}

const generateSHPFiles = async(zip, perimetros) => {
  for (const perimetro of perimetros) {
    const geoJson = buildGeoJsonPerimetro(
      perimetro
    )

    if (!geoJson.features.length) {
      continue
    }

    const nombrePerimetro = normalizeFileName(perimetro.NOMBRE)

    const geoJsonTransformado = transformarGeoJsonAEPSG4326(geoJson)

    const shpZipBlob = await shpwrite.zip(
      geoJsonTransformado,
      {
        folder: nombrePerimetro,
        filename: nombrePerimetro,
        types: {
          polygon: nombrePerimetro
        }
      }
    )

    zip.file(`${nombrePerimetro}.zip`, shpZipBlob)
  }
}

export const exportPerimetros = async(perimetros, tipo = 'KML') => {
  try {
    if (!perimetros?.length) {
      throw new Error('No hay perímetros para exportar')
    }

    const zip = new JSZip()

    if (tipo === 'KML') {
      generateKMLFiles(zip, perimetros)
    } else if (tipo === 'SHP') {
      await generateSHPFiles(zip, perimetros)
    }

    const timestamp = DateService.toString(DateService.getCurrentDayJs(), FMT.onlyDateDash)

    const nombreZip = normalizeFileName(`Perimetros_${tipo}_${timestamp}.zip`)

    const zipBlob = await zip.generateAsync({ type: 'blob' })

    downloadBlob(zipBlob, nombreZip)
  } catch (err) {
    console.error('Error exportando perimetros:', err)
  }
}

async function createGraphicsTrack(idMedio, medio, posiciones) {
  const deferred = Q.defer()

  try {
    const [ObjectSymbol3DLayer, PointSymbol3D] = await loadModules([
      'esri/symbols/ObjectSymbol3DLayer',
      'esri/symbols/PointSymbol3D'
    ])
    const rgbRandomArrow = [
      Math.floor(Math.random() * 255),
      Math.floor(Math.random() * 255),
      Math.floor(Math.random() * 255)
    ]
    const is3D = store.getters['map/isMap3D']
    const rgbRandom = [
      Math.floor(Math.random() * 255),
      Math.floor(Math.random() * 255),
      Math.floor(Math.random() * 255)
    ]
    posiciones.reverse() // Poner en orden creciente

    const sources = []
    const pathPosiciones = []
    for (let i = 0; i < posiciones.length; i++) {
      let rumbo = 0

      if (i < posiciones.length - 1) {
        // Calcula el rumbo para todas las posiciones menos para la ultima
        rumbo = calcularRumbo(
          posiciones[i].LATITUD,
          posiciones[i].LONGITUD,
          posiciones[i + 1].LATITUD,
          posiciones[i + 1].LONGITUD
        )
      }
      rumbo = (rumbo + 180) % 360 // La flecha original apunta hacia abajo

      pathPosiciones.push([posiciones[i].LONGITUD, posiciones[i].LATITUD])

      if (is3D) {
        sources.push({
          // Cada punto
          geometry: new Point({
            x: posiciones[i].LONGITUD,
            y: posiciones[i].LATITUD
          }),
          attributes: {
            ID_MEDIO: idMedio,
            MEDIO: medio,
            FECHA: posiciones[i].FECHA,
            VELOCIDAD: posiciones[i].VELOCIDAD
          },
          symbol: new PointSymbol3D({
            symbolLayers: [
              new ObjectSymbol3DLayer({
                anchor: 'relative',
                anchorPosition: { x: 0, y: 0, z: -0.5 },

                width: 200,
                height: 200,
                tilt: 85,
                heading: rumbo,
                resource: {
                  primitive: 'tetrahedron'
                },
                material: {
                  color: '#FFD700'
                }
              })
            ]
          })
        })
      } else {
        sources.push({
          // Cada punto
          geometry: new Point({
            x: posiciones[i].LONGITUD,
            y: posiciones[i].LATITUD
          }),
          attributes: {
            ID_MEDIO: idMedio,
            MEDIO: medio,
            FECHA: posiciones[i].FECHA,
            VELOCIDAD: posiciones[i].VELOCIDAD
          },
          symbol: {
            type: 'simple-marker',
            path: 'M14.5,29 23.5,0 14.5,9 5.5,0z',
            angle: rumbo,
            color: rgbRandomArrow,
            size: '10px',
            outline: {
              color: rgbRandomArrow
            }
          }
        })

        // Etiqueta del medio sobre el punto
        // se añade cada 3 puntos, para no sobrecargar
        if (i % 3 === 0) {
          sources.push({
            // Cada punto
            geometry: new Point({
              x: posiciones[i].LONGITUD,
              y: posiciones[i].LATITUD
            }),
            attributes: {
              ID_MEDIO: idMedio,
              MEDIO: medio,
              FECHA: posiciones[i].FECHA,
              VELOCIDAD: posiciones[i].VELOCIDAD,
              IS_TAG: true
            },
            symbol: {
              type: 'text', // autocasts as new TextSymbol()
              color: rgbRandomArrow,
              haloColor: 'black',
              haloSize: '0.75px',
              text: medio,
              yoffset: 6,
              font: {
                // autocasts as new Font()
                size: 10,
                family: 'Arial Unicode MS'
              }
            }
          })
        }
      }
    }
    sources.unshift({
      // Linea
      geometry: {
        type: 'polyline',
        paths: pathPosiciones
      },
      attributes: {
        ID_MEDIO: idMedio
      },
      symbol: {
        type: 'simple-line',
        style: 'short-dot',
        color: rgbRandom,
        width: 1
      }
    })

    deferred.resolve(sources)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

async function calcularVelocidad(puntos) {
  // TODO: hacer en backend
  const deferred = Q.defer()

  try {
    const [geometryEngine, SpatialReference, webMercatorUtils] =
      await loadModules([
        'esri/geometry/geometryEngine',
        'esri/geometry/SpatialReference',
        'esri/geometry/support/webMercatorUtils'
      ])

    const sr = new SpatialReference({ wkid: 102100 })
    for (let i = 0; i < puntos.length; i++) {
      if (i === 0) {
        puntos[i].VELOCIDAD = 0
      } else if (i < puntos.length - 1) {
        const posPoint = webMercatorUtils.lngLatToXY(
          puntos[i].LONGITUD,
          puntos[i].LATITUD
        )
        const posFollowPoint = webMercatorUtils.lngLatToXY(
          puntos[i + 1].LONGITUD,
          puntos[i + 1].LATITUD
        )

        const point = new Point({
          x: posPoint[0],
          y: posPoint[1],
          spatialReference: sr
        })

        const followPoint = new Point({
          x: posFollowPoint[0],
          y: posFollowPoint[1],
          spatialReference: sr
        })

        const distancia = geometryEngine.distance(
          point,
          followPoint,
          'kilometers'
        )

        const fechaPoint = DateService.toDayjs(puntos[i].FECHA)
        const fechaFollowPoint = DateService.toDayjs(puntos[i + 1].FECHA)

        const tiempo = fechaPoint.diff(fechaFollowPoint, 'seconds') / 3600

        const velocidad = distancia / tiempo

        puntos[i].VELOCIDAD = Math.trunc(velocidad)
      }
    }
    deferred.resolve(puntos)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

function toRadians(degrees) {
  return (degrees * Math.PI) / 180
}

function toDegrees(radians) {
  return (radians * 180) / Math.PI
}
function calcularRumboX(startLat, startLng, destLat, destLng) {
  // TODO: hacer en backend
  startLat = toRadians(startLat)
  startLng = toRadians(startLng)
  destLat = toRadians(destLat)
  destLng = toRadians(destLng)

  const x =
    Math.cos(startLat) * Math.sin(destLat) -
    Math.sin(startLat) * Math.cos(destLat) * Math.cos(destLng - startLng)
  const brng = toDegrees(x)
  return (brng + 360) % 360
}
function calcularRumboY(startLat, startLng, destLat, destLng) {
  // TODO: hacer en backend
  startLat = toRadians(startLat)
  startLng = toRadians(startLng)
  destLat = toRadians(destLat)
  destLng = toRadians(destLng)

  const y = Math.sin(destLng - startLng) * Math.cos(destLat)
  const brng = toDegrees(y)
  return (brng + 360) % 360
}
function calcularRumbo(startLat, startLng, destLat, destLng) {
  // TODO: hacer en backend
  startLat = toRadians(startLat)
  startLng = toRadians(startLng)
  destLat = toRadians(destLat)
  destLng = toRadians(destLng)

  const y = Math.sin(destLng - startLng) * Math.cos(destLat)
  const x =
    Math.cos(startLat) * Math.sin(destLat) -
    Math.sin(startLat) * Math.cos(destLat) * Math.cos(destLng - startLng)
  let brng = Math.atan2(y, x)
  brng = toDegrees(brng)
  return (brng + 360) % 360
}
// #endregion

// #region PLANES_OPERACIONES
let planOpSVM = null

export const initPlanOperacionesSketchViewModel = async(data) => {
  try {
    const [SketchViewModel] = await loadModules([
      'esri/widgets/Sketch/SketchViewModel'
    ])
    const callbackCreate = data.callbackCreate
    const callbackUpdate = data.callbackUpdate
    const callbackDelete = data.callbackDelete

    const layer = map.findLayerById('layer-plan-operaciones')

    planOpSVM = new SketchViewModel({
      view: mapView,
      layer
    })

    planOpSVM.on(['create', 'delete', 'update'], async(evt) => {
      // Arrow logic
      if (
        isDrawingArrow &&
        evt.type === 'create' &&
        evt.graphic?.geometry.type === 'polyline'
      ) {
        if (evt.state === 'active') {
          if (evt.graphic.geometry.paths[0].length === 3) {
            planOpSVM.complete()
          }
        }
      }

      if (evt.state === 'complete' && evt.type === 'create') {
        // Crear grafico
        const attributes = {
          ID_GRAFICO: VueInst.$uuid.createUUID()
        }

        evt.graphic.attributes = JSON.parse(JSON.stringify(attributes)) // Hack para quitar el __observer__ que causa un bucle infinito a arcgis
        const arrowColor = [
          Math.floor(Math.random() * 255),
          Math.floor(Math.random() * 255),
          Math.floor(Math.random() * 255)
        ]

        let geometry
        let type = evt.graphic.geometry.type
        let symbol
        if (
          evt.graphic.geometry.type === 'point' &&
          evt.graphic.symbol.type === 'simple-marker'
        ) {
          // Punto
          const defaultSymbol = planOpSVM.pointSymbol

          geometry = [evt.graphic.geometry.x, evt.graphic.geometry.y]
          symbol = {
            type: defaultSymbol.type,
            color: defaultSymbol.color,
            size: defaultSymbol.size,
            style: defaultSymbol.style
          }
        } else if (
          evt.graphic.geometry.type === 'point' &&
          evt.graphic.symbol.type === 'picture-marker'
        ) {
          // Icono
          const defaultSymbol = planOpSVM.pointSymbol

          attributes.LABEL = {
            label: defaultSymbol.label,
            src: defaultSymbol.url
          }

          geometry = [evt.graphic.geometry.x, evt.graphic.geometry.y]

          type = 'icon'

          // Simbolo que añade una circunferencia al icono, en caso de ser medio externo
          // mediante la variable 'tipoLinea' para reutilizar
          symbol = {
            type: 'cim',
            // CIM Line Symbol
            data: {
              type: 'CIMSymbolReference',
              symbol: {
                type: 'CIMPointSymbol',
                rotation: defaultSymbol.rotation,
                symbolLayers: [
                  {
                    // arrow symbol
                    type: 'CIMVectorMarker',
                    enable: tipoLinea,
                    size: 30,
                    frame: {
                      xmin: 2.5,
                      ymin: 2.5,
                      xmax: 14,
                      ymax: 14
                    },
                    markerGraphics: [
                      {
                        type: 'CIMMarkerGraphic',
                        geometry: {
                          rings: [
                            [
                              [8.5, 0.2],
                              [7.06, 0.33],
                              [5.66, 0.7],
                              [4.35, 1.31],
                              [3.16, 2.14],
                              [2.14, 3.16],
                              [1.31, 4.35],
                              [0.7, 5.66],
                              [0.33, 7.06],
                              [0.2, 8.5],
                              [0.33, 9.94],
                              [0.7, 11.34],
                              [1.31, 12.65],
                              [2.14, 13.84],
                              [3.16, 14.86],
                              [4.35, 15.69],
                              [5.66, 16.3],
                              [7.06, 16.67],
                              [8.5, 16.8],
                              [9.94, 16.67],
                              [11.34, 16.3],
                              [12.65, 15.69],
                              [13.84, 14.86],
                              [14.86, 13.84],
                              [15.69, 12.65],
                              [16.3, 11.34],
                              [16.67, 9.94],
                              [16.8, 8.5],
                              [16.67, 7.06],
                              [16.3, 5.66],
                              [15.69, 4.35],
                              [14.86, 3.16],
                              [13.84, 2.14],
                              [12.65, 1.31],
                              [11.34, 0.7],
                              [9.94, 0.33],
                              [8.5, 0.2]
                            ]
                          ]
                        },
                        symbol: {
                          // black fill for the arrow symbol
                          type: 'CIMPolygonSymbol',
                          symbolLayers: [
                            {
                              type: 'CIMSolidStroke', // CIMSolidFill | CIMSolidStroke
                              enable: true,
                              width: 2,
                              color: [10, 10, 10, 255]
                            }
                          ]
                        }
                      }
                    ]
                  },
                  {
                    type: 'CIMPictureMarker',
                    enable: true,
                    url: defaultSymbol.url,
                    size: 40,
                    rotation: defaultSymbol.rotation
                  },
                  // Fondo del icono
                  {
                    type: 'CIMVectorMarker',
                    enable: showBackground,
                    size: 44,
                    frame: {
                      xmin: 0,
                      ymin: 0,
                      xmax: 20,
                      ymax: 20
                    },
                    markerGraphics: [
                      {
                        type: 'CIMMarkerGraphic',
                        geometry: {
                          rings: [[
                            [0, 0],
                            [20, 0],
                            [20, 20],
                            [0, 20],
                            [0, 0]
                          ]]
                        },
                        symbol: {
                          type: 'CIMPolygonSymbol',
                          symbolLayers: [
                            {
                              type: 'CIMSolidFill',
                              enable: true,
                              color: [255, 255, 255, 180]
                            }
                          ]
                        }
                      }
                    ]
                  }
                ]
              }
            }
          }
          evt.graphic.symbol = symbol
        } else if (
          evt.graphic.geometry.type === 'point' &&
          evt.graphic.symbol.type === 'text'
        ) {
          // Texto
          const defaultSymbol = planOpSVM.pointSymbol

          geometry = [evt.graphic.geometry.x, evt.graphic.geometry.y]
          type = 'text'
          symbol = {
            type: 'text',
            color: defaultSymbol.color,
            text: defaultSymbol.text,
            xoffset: 0,
            yoffset: 0,
            angle: defaultSymbol.angle,
            haloColor: 'white',
            haloSize: '20px',
            font: {
              size: defaultSymbol.font.size,
              family: 'Arial Unicode MS',
              weight: 'bold'
            }
          }
        } else if (
          !isDrawingArrow &&
          evt.graphic.geometry.type === 'polyline'
        ) {
          // Linea
          if (!tipoLinea) {
            // Lineas antiguas
            const defaultSymbol = planOpSVM.polylineSymbol

            geometry = evt.graphic.geometry.paths[0]
            symbol = {
              type: defaultSymbol.type,
              color: defaultSymbol.color,
              width: defaultSymbol.width,
              style: defaultSymbol.style,
              join: defaultSymbol.join,
              miterLimit: defaultSymbol.miterLimit
            }
          } else {
            // Lineas nuevas
            const is3D = store.getters['map/isMap3D']
            let nombreURL
            if (is3D) {
              nombreURL = `${tipoLinea}3D`
            } else {
              nombreURL = tipoLinea
            }

            const geometria = evt.graphic.geometry.paths[0]

            const g = await makeCustomLine(
              geometria,
              nombreURL
            )

            geometry = geometria
            evt.graphic.symbol = g.symbol
            symbol = g.symbol
          }
          attributes.LABEL = planOpSVM.polylineSymbol.label
        } else if (isDrawingArrow || evt.graphic.geometry.type === 'polygon') {
          // Poligonos y flecha
          if (!tipoLinea) {
            // Poligonos y flechas old
            const defaultSymbol = planOpSVM.polygonSymbol

            if (isDrawingArrow) {
              // Convertir la linea a flecha y crear el graphic
              isDrawingArrow = false
              const g = await makeArrow(
                evt.graphic.geometry.paths[0],
                defaultSymbol.color
              )
              evt.graphic.geometry = g.geometry
              evt.graphic.symbol = g.symbol
              type = 'polygon'
            }

            geometry = evt.graphic.geometry.rings[0]
            symbol = {
              type: defaultSymbol.type,
              color: defaultSymbol.color,
              style: defaultSymbol.style,
              outline: {
                color: defaultSymbol.outline.color,
                width: defaultSymbol.outline.width
              }
            }
          } else if (tipoLinea === 'cimPolygon' || (evt.graphic.symbol.type === 'cim')) {
            geometry = evt.graphic.geometry.rings[0]
            symbol = structuredClone(evt.graphic.symbol)
            type = 'polygon'
          } else {
            // New poligons
            const is3D = store.getters['map/isMap3D']
            let nombreURL

            if (is3D) {
              nombreURL = `${tipoLinea}3D`
            } else {
              nombreURL = tipoLinea
            }

            const g = makeCustomPolygon(
              evt.graphic.geometry.rings[0],
              nombreURL
            )

            geometry = evt.graphic.geometry.rings[0]

            evt.graphic.geometry = g.geometry
            evt.graphic.symbol = g.symbol
            type = 'polygon'
            symbol = g.symbol
          }
          attributes.LABEL = planOpSVM.polygonSymbol.label
        }
        const graphic = {
          // TODO: poner los mismos nombres que en BBDD y cambiar en backend en (plan_operaciones addGraphics)
          attributes,
          geometry,
          type,
          symbol
        }
        callbackCreate(graphic)
      } else if (evt.state === 'complete' && evt.type === 'update') {
        // Actualizar grafico
        const graphic = evt.graphics[0]
        const geom = graphic.geometry

        let newGeometryGraphic
        if (geom.type === 'point') {
          newGeometryGraphic = [geom.x, geom.y]
        } else if (geom.type === 'polyline') {
          newGeometryGraphic = geom.paths[0]
        } else if (geom.type === 'polygon') {
          newGeometryGraphic = geom.rings[0]
        }

        // geometry.type = graphic.geometry.type
        const newGraphic = {
          ATRIBUTOS: graphic.attributes,
          GEOMETRIA: newGeometryGraphic
        }

        callbackUpdate(newGraphic)
      } else if (evt.type === 'delete') {
        // Borrar grafico
        callbackDelete(evt.graphics[0].attributes.ID_GRAFICO)
      }
    })
  } catch (err) {
    VueInst.$log.error(err)
  }
}

export const paintEventPlanOperaciones = (event) => {
  let typeEvent = event.function

  const symbolColor = event.color ? event.color : [255, 0, 0, 0.5]

  isDrawingArrow = false
  tipoLinea = undefined
  showBackground = event.showBackground

  if (typeEvent === 'cancel') {
    planOpSVM.cancel()
  } else if (typeEvent === 'point') {
    planOpSVM.pointSymbol = {
      type: 'simple-marker',
      style: 'circle',
      color: symbolColor,
      size: '16px',
      outline: { color: symbolColor, width: 3 },
      label: { style: 'point', color: symbolColor }
    }
  } else if (typeEvent === 'polyline') {
    planOpSVM.polylineSymbol = null

    if (event.cimSymbol) {
      planOpSVM.polylineSymbol = null
      const simboloLimpio = structuredClone(event.cimSymbol)
      planOpSVM.polylineSymbol = simboloLimpio
      planOpSVM.polylineSymbol.label = { style: event.type_line || 'solid', color: symbolColor }
    } else {
      const estiloSeguro = (event.type_line && ['solid', 'shortDash', 'shortDot', 'shortDashDotDot'].includes(event.type_line))
        ? event.type_line
        : 'solid'

      planOpSVM.polylineSymbol = {
        type: 'simple-line',
        color: symbolColor,
        width: 3,
        style: estiloSeguro,
        label: {
          style: event.type_line,
          label: event.label || '',
          color: symbolColor
        }
      }
    }
  } else if (typeEvent === 'polygon') {
    const is3D = store.getters['map/isMap3D']

    if (event.cimSymbol && !is3D) {
      planOpSVM.polygonSymbol = structuredClone(event.cimSymbol)
      tipoLinea = 'cimPolygon'
    } else {
      tipoLinea = event.type_polygon
    }

    planOpSVM.polygonSymbol.label = event.labelPolygon
  } else if (typeEvent === 'icon') {
    typeEvent = 'point'
    tipoLinea = event.medio_externo
    planOpSVM.pointSymbol = {
      type: 'picture-marker',
      url: event.src,
      label: event.label,
      width: '35px',
      height: '35px',
      rotation: event.rotation,
      angle: -event.rotation
    }
  } else if (typeEvent === 'text') {
    typeEvent = 'point'
    planOpSVM.pointSymbol = {
      type: 'text',
      color: symbolColor,
      text: event.text,
      xoffset: 0,
      yoffset: 0,
      angle: event.angle || 0,
      haloColor: 'white',
      haloSize: '2px',
      font: {
        size: event.size,
        family: 'Arial Unicode MS',
        weight: 'bold'
      }
    }
  } else if (typeEvent === 'arrow') {
    typeEvent = 'polyline'
    isDrawingArrow = true
    planOpSVM.polygonSymbol.color = symbolColor
    planOpSVM.polygonSymbol.outline.color = symbolColor
  }

  planOpSVM.pointSymbol.label = event.label ?? 'Sin nombre'
  planOpSVM.create(typeEvent)
}

export const stopPlanOperacionesSketchViewModel = () => {
  if (planOpSVM === null) return
  const layer = map.findLayerById('layer-plan-operaciones')
  layer.removeAll()
  planOpSVM.cancel()
  planOpSVM.destroy()
}

let isDrawingArrow = false
let tipoLinea
let showBackground
// Crea un poligono con forma de flecha a partir de una linea de dos puntos
function makeArrow(geom, color) {
  const deferred = Q.defer()

  try {
    const start = new Point({ x: geom[0][0], y: geom[0][1] })
    const end = new Point({ x: geom[1][0], y: geom[1][1] })

    const dist = start.distance(end)
    const dy = end.y - start.y
    const dx = end.x - start.x
    let rads = Math.atan2(dx, dy)

    if (rads < 0) {
      rads = Math.abs(rads)
    } else {
      rads = 2 * Math.PI - rads
    }

    const degrees = (rads * 180) / Math.PI

    const width = 0.2 * dist // 1/5, can adjust

    const bottomLeft = [start.x - width / 10, start.y]
    const bottomRight = [start.x + width / 10, start.y]
    const midLeft = [bottomLeft[0], [bottomLeft[1] + dist - width]]
    const midRight = [bottomRight[0], [bottomRight[1] + dist - width]]
    const edgeLeft = [midLeft[0] - width / 2, midLeft[1]]
    const edgeRight = [midRight[0] + width / 2, midRight[1]]
    const point = [start.x, start.y + dist]

    const rings = [
      point,
      edgeLeft,
      midLeft,
      bottomLeft,
      bottomRight,
      midRight,
      edgeRight
    ]

    const poly = new Polygon({
      rings: [rings],
      spatialReference: { wkid: 102100 }
    })

    const rotatedPoly = GeometryEngine.rotate(poly, degrees, start)
    const symbol = {
      type: 'simple-fill',
      color,
      outline: {
        color,
        width: '2'
      }
    }

    deferred.resolve(
      new Graphic({
        geometry: rotatedPoly,
        symbol
      })
    )
  } catch (err) {
    deferred.reject(err)
    VueInst.$log.error(err)
  }

  return deferred.promise
}
function makeCustomLine(geom, url, color) {
  const deferred = Q.defer()

  try {
    const poly = new Polyline({
      paths: geom,
      spatialReference: { wkid: 102100 }
    })

    let symbolJSON = null

    if (symbology.getCIMSymbol && typeof symbology.getCIMSymbol === 'function') {
      symbolJSON = symbology.getCIMSymbol(url, color)
    }

    if (!symbolJSON) {
      symbolJSON = symbology[url]
    }

    let symbol
    if (symbolJSON) {
      const safeJSON = structuredClone(symbolJSON)

      if (safeJSON.type === 'cim') {
        delete safeJSON.type
        delete safeJSON.style
        delete safeJSON.color
        delete safeJSON.width
        delete safeJSON.outline
        delete safeJSON.font

        symbol = new CIMSymbol(safeJSON)
      } else {
        symbol = symbolJSON
      }
    }

    deferred.resolve({
      geometry: poly,
      symbol
    })
  } catch (err) {
    deferred.reject(err)
    VueInst.$log.error(err)
  }

  return deferred.promise
}

function makeCustomPolygon(geom, url, color) {
  try {
    const poly = new Polygon({
      rings: geom,
      spatialReference: { wkid: 102100 }
    })

    let symbol = null

    if (symbology.getCIMSymbol && typeof symbology.getCIMSymbol === 'function') {
      symbol = symbology.getCIMSymbol(url, color)
    }

    return {
      geometry: poly,
      symbol
    }
  } catch (err) {
    VueInst.$log.error(err)
    throw err
  }
}

async function createGraphicsDraw(graphicList) {
  const deferred = Q.defer()

  try {
    const graphics = []

    for (let i = 0; i < graphicList.length; i++) {
      const item = graphicList[i]
      let coords = item.geometry || item.GEOMETRIA

      if (!coords || coords === 'undefined' || coords === 'null') continue
      if (typeof coords === 'string') {
        try { coords = JSON.parse(coords) } catch (e) { continue }
      }

      const tipo = item.type || item.TIPO

      const rawAttr = item.attributes || item.ATRIBUTOS
      let atributos = {}
      if (typeof rawAttr === 'string') {
        try { atributos = JSON.parse(rawAttr) } catch (e) { atributos = {} }
      } else if (rawAttr) {
        atributos = JSON.parse(JSON.stringify(rawAttr))
      }

      let estiloNombre = 'solid'
      let estiloColor = [34, 139, 34, 255]

      if (atributos && atributos.LABEL) {
        if (atributos.LABEL.style) estiloNombre = atributos.LABEL.style
        if (atributos.LABEL.color) {
          estiloColor = atributos.LABEL.color
        }
      }

      let simbolo = null
      let geometry = null

      if (tipo === 'point' || tipo === 'icon' || tipo === 'text') {
        geometry = new Point({ x: coords[0], y: coords[1], spatialReference: { wkid: 3857 } })
        let rawSym = item.symbol || item.SIMBOLO
        rawSym = typeof rawSym === 'string' ? JSON.parse(rawSym) : rawSym
        simbolo = rawSym
      } else if (tipo === 'polyline') {
        let fixedPaths = coords
        if (Array.isArray(coords) && coords.length > 0 && Array.isArray(coords[0]) && typeof coords[0][0] === 'number') {
          fixedPaths = [coords]
        }

        geometry = new Polyline({ hasZ: false, hasM: true, paths: fixedPaths, spatialReference: { wkid: 3857 } })
        atributos.LONGITUD = GeometryEngine.geodesicLength(geometry)
        try {
          const g = await makeCustomLine(fixedPaths, estiloNombre, estiloColor)
          if (g && g.symbol) {
            simbolo = g.symbol
          }
        } catch (err) { console.warn(err) }
      } else if (tipo === 'polygon') {
        geometry = new Polygon({ hasZ: false, hasM: true, rings: coords, spatialReference: { wkid: 3857 } })
        atributos.LONGITUD = GeometryEngine.geodesicLength(geometry)
        atributos.AREA = Math.abs(GeometryEngine.geodesicArea(geometry))
        try {
          const g = await makeCustomPolygon(coords, estiloNombre, estiloColor)
          if (g && g.symbol) simbolo = g.symbol
        } catch (err) {}
      }

      if (!simbolo || (typeof simbolo === 'object' && !simbolo.type)) {
        if (geometry.type === 'point') {
          simbolo = { type: 'simple-marker', style: 'circle', color: [255, 0, 0, 1], size: 8, outline: { color: [255, 255, 255], width: 1 } }
        } else if (geometry.type === 'polyline') {
          simbolo = { type: 'simple-line', color: [34, 139, 34, 1], width: 3, style: 'solid' }
        } else if (geometry.type === 'polygon') {
          simbolo = { type: 'simple-fill', color: [255, 0, 0, 0.2], outline: { color: [255, 0, 0], width: 1 } }
        }
      }

      const graphic = new Graphic({
        geometry,
        symbol: simbolo,
        attributes: atributos
      })
      graphics.push(graphic)
    }

    deferred.resolve(graphics)
  } catch (err) {
    console.error('Error createGraphicsDraw', err)
    deferred.resolve([])
  }

  return deferred.promise
}

function sortGraphics(graphics) {
  return graphics.sort((a, b) => {
    if (!a.geometry || !b.geometry) return 0

    if (a.geometry.type === 'point') {
      return 1
    }
    if (b.geometry.type === 'point') {
      return -1
    }
    if (a.geometry.type === 'polyline' && b.geometry.type === 'polygon') {
      return 1
    }
    if (b.geometry.type === 'polyline' && a.geometry.type === 'polygon') {
      return -1
    }
    return 1
  })
}

export const drawGraphicsPlanOperaciones = async(graphicList) => {
  const layer = map.findLayerById('layer-plan-operaciones')
  if (!layer) return
  layer.removeAll()

  try {
    const graphics = await createGraphicsDraw(graphicList)
    const validGraphics = graphics.filter(g => g.geometry)
    const graphicsSorted = sortGraphics(validGraphics)
    createSizedGraphics(graphicsSorted)
    layer.addMany(graphicsSorted)
  } catch (err) {
    console.error('Error en drawGraphicsPlanOperaciones:', err)
    throw err
  }
}
// #endregion

// #region PERIMETROS ********************************************************************************************************************************************************************
let perimetroSVM = null
let colorPerimetro = [255, 10, 10, 255]
let tipoLineaPerimetro

export const initPerimetroSketchViewModel = async(data) => {
  try {
    const [SketchViewModel] = await loadModules([
      'esri/widgets/Sketch/SketchViewModel'
    ])
    const callbackCreate = data.callbackCreate
    const callbackUpdate = data.callbackUpdate
    const callbackDelete = data.callbackDelete

    const layer = map.findLayerById('layer-perimetros')

    perimetroSVM = new SketchViewModel({
      view: mapView,
      layer
    })

    perimetroSVM.on(['create', 'delete', 'update'], async(evt) => {
      if (
        evt.type === 'create' &&
        evt.graphic?.geometry.type === 'polyline'
      ) {
        if (evt.state === 'active') {
          if (evt.graphic.geometry.paths[0].length === 3) {
            perimetroSVM.complete()
          }
        }
      }

      if (evt.state === 'complete' && evt.type === 'create') {
        // Crear grafico
        const attributes = {
          ID_GRAFICO: VueInst.$uuid.createUUID()
        }

        evt.graphic.attributes = JSON.parse(JSON.stringify(attributes)) // Hack para quitar el __observer__ que causa un bucle infinito a arcgis

        const g = await makeCustomPolygon(
          evt.graphic.geometry.rings[0],
          tipoLineaPerimetro,
          colorPerimetro
        )

        const geometry = evt.graphic.geometry.rings[0]
        const type = 'polygon'
        const symbol = g.symbol
        evt.graphic.geometry = g.geometry
        evt.graphic.symbol = g.symbol

        attributes.LABEL = perimetroSVM.polygonSymbol.label

        const graphic = {
          // TODO: poner los mismos nombres que en BBDD y cambiar en backend en (plan_operaciones addGraphics)
          attributes,
          geometry,
          type,
          symbol
        }
        callbackCreate(graphic)
      } else if (evt.state === 'complete' && evt.type === 'update') {
        // Actualizar grafico
        const graphic = evt.graphics[0]

        const newGraphic = {
          attributes: graphic.attributes,
          geometry: graphic.geometry.rings[0]
        }

        callbackUpdate(newGraphic)
      } else if (evt.type === 'delete') {
        // Borrar grafico
        callbackDelete(evt.graphics[0].attributes.ID_GRAFICO)
      }
    })
  } catch (err) {
    VueInst.$log.error(err)
  }
}

export const paintEventPerimetro = (event) => {
  const typeEvent = event.function

  if (typeEvent === 'cancel') {
    perimetroSVM.cancel()
  } else if (typeEvent === 'polygon') {
    perimetroSVM.polygonSymbol = structuredClone(event.cimSymbol)
    tipoLinea = 'cimPolygon'
    tipoLineaPerimetro = event.typeLine
    colorPerimetro = event.color
  }
  perimetroSVM.polygonSymbol.label = event.label
  perimetroSVM.create(typeEvent)
}

export const stopPerimeterSketchViewModel = () => {
  if (perimetroSVM === null) return
  const layer = map.findLayerById('layer-perimetros')
  layer.removeAll()

  perimetroSVM.cancel()
  perimetroSVM.destroy()
}

function createGraphicsDrawPerimeter(graphicList) {
  try {
    const graphics = []

    for (const item of graphicList) {
      // 1. Extraer y parsear con seguridad
      if (!item.GEOMETRIA || item.GEOMETRIA === 'undefined') continue

      try {
        const coords = typeof item.GEOMETRIA === 'string' ? JSON.parse(item.GEOMETRIA) : item.GEOMETRIA
        const simboloRaw = typeof item.SIMBOLO === 'string' ? JSON.parse(item.SIMBOLO) : item.SIMBOLO
        const atributos = typeof item.ATRIBUTOS === 'string' ? JSON.parse(item.ATRIBUTOS) : item.ATRIBUTOS

        // 2. Construir la Geometría (ArcGIS requiere rings: [ [ [x,y], [x,y] ] ])
        const geometry = new Polygon({
          rings: [coords], // Envolvemos el array en otro array para crear el anillo
          spatialReference: { wkid: 3857 }
        })

        // 3. Construir el Símbolo (Manejo de CIM)
        let finalSymbol = simboloRaw
        if (simboloRaw?.type === 'cim') {
          finalSymbol = new CIMSymbol({
            data: simboloRaw.data
          })
        }

        // 4. Crear el gráfico final
        const graphic = new Graphic({
          geometry,
          symbol: finalSymbol,
          attributes: atributos
        })

        graphics.push(graphic)
      } catch (parseError) {
        console.error('Error procesando fila de BBDD:', parseError, item)
      }
    }
    return graphics
  } catch (err) {
    console.error('error en createGraphicsDrawPerimeter:', err)
    throw err
  }
}

let displayCoords
let archivoQML = null
const R = 6378137.0
function perimeterGeoJsonToGraphicsPerimeter(geojson, isPerimeter) {
  const deferred = Q.defer()

  try {
    let geometry
    const graphics = []
    let forma
    let simbolo

    switch (geojson.features[0].geometry.type.toLowerCase()) {
      case 'multipolygon':
        displayCoords = geojson.features[0].geometry.coordinates[0][0][0]
        break
      case 'point':
        displayCoords = geojson.features[0].geometry.coordinates
        break
      case 'polygon':
        displayCoords = geojson.features[0].geometry.coordinates[0][0]
        break
      case 'geometrycollection':
        displayCoords = geojson.features[0].geometry.geometries[0].coordinates[0][0]
        break
      default:
        displayCoords = null
    }

    if (isLatLon(displayCoords[0], displayCoords[1])) {
      displayCoords = changeCoords(displayCoords, 'point')
    }

    for (let i = 0; i < geojson.features.length; i++) {
      if (!geojson.features[i].geometry) {
        continue
      }

      let tipo = geojson.features[i].geometry.type.toLowerCase()
      const coords = changeCoords(geojson.features[i].geometry.coordinates, tipo)
      const atributos = geojson.features[i].properties
      const colorDefault = [Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), 0.5]

      if (tipo === 'polygon') {
        geometry = new Polygon({
          hasZ: false,
          hasM: true,
          rings: coords,
          spatialReference: { wkid: 3857 }
        })

        forma = crearPoligonoPerimetro(geometry)

        if (isPerimeter) {
          let outlineColor = [255, 0, 0, 255]
          let outlineWidth = 2.5
          let outlineStyle = 'solid'

          if (atributos.COLOR) {
            outlineColor = JSON.parse(atributos.COLOR)
          }

          if (atributos.WIDTH) {
            outlineWidth = Number(atributos.WIDTH)
          }

          if (atributos.DASH === 'true' || atributos.DASH === true) {
            outlineStyle = 'dash'
          }

          forma.symbol.color = [0, 0, 0, 0]

          forma.symbol.outline.color = outlineColor
          forma.symbol.outline.width = outlineWidth
          forma.symbol.outline.style = outlineStyle
        } else {
          if (atributos.fill) {
            forma.symbol.color = hexToBytes(atributos.fill.substring(1))
          } else {
            if (archivoQML) {
              const categoriasQML = [...archivoQML.getElementsByTagName('category')].map(cat => ({
                value: cat.getAttribute('value'),
                symbol: cat.getAttribute('symbol')
              }))
              const simbolosQML = getSymbols(archivoQML)

              forma.symbol.color = getCoords(atributos.Elapsed_Mi, categoriasQML, simbolosQML)
            } else {
              forma.symbol.color = colorDefault
            }
          }
        }

        simbolo = forma.symbol
      }

      if (tipo === 'multipolygon') {
        for (let j = 0; j < coords.length; j++) {
          geometry = new Polygon({
            hasZ: false,
            hasM: true,
            rings: coords[j],
            spatialReference: { wkid: 3857 }
          })

          forma = crearPoligonoPerimetro(geometry)

          if (isPerimeter) {
            let outlineColor = [255, 0, 0, 255]
            let outlineWidth = 2.5
            let outlineStyle = 'solid'

            if (atributos.COLOR) {
              outlineColor = JSON.parse(atributos.COLOR)
            }

            if (atributos.WIDTH) {
              outlineWidth = Number(atributos.WIDTH)
            }

            if (atributos.DASH === 'true' || atributos.DASH === true) {
              outlineStyle = 'dash'
            }

            forma.symbol.color = [0, 0, 0, 0]

            forma.symbol.outline.color = outlineColor
            forma.symbol.outline.width = outlineWidth
            forma.symbol.outline.style = outlineStyle
          } else {
            if (atributos.fill) {
              forma.symbol.color = hexToBytes(atributos.fill.substring(1))
            } else {
              if (archivoQML) {
                const categoriasQML = [...archivoQML.getElementsByTagName('category')].map(cat => ({
                  value: cat.getAttribute('value'),
                  symbol: cat.getAttribute('symbol')
                }))
                const simbolosQML = getSymbols(archivoQML)

                forma.symbol.color = getCoords(atributos.Elapsed_Mi, categoriasQML, simbolosQML)
              } else {
                forma.symbol.color = colorDefault
              }
            }
          }

          simbolo = forma.symbol

          atributos.ID_GRAFICO = VueInst.$uuid.createUUID()
          const graphic = {
            attributes: atributos,
            geometry,
            type: 'polygon',
            symbol: simbolo
          }

          graphics.push(graphic)
        }
      }

      if (tipo === 'point') {
        geometry = new Point({
          longitude: coords[0],
          latitude: coords[1],
          coords
        })

        forma = crearPuntoPerimetro(geometry)

        if (atributos.fill) {
          forma.symbol.color = hexToBytes(atributos.fill.substring(1))
        } else {
          forma.symbol.color = colorDefault
        }

        simbolo = forma.symbol
      }

      if (tipo === 'linestring' || tipo === 'polyline') {
        geometry = new Polyline({
          hasZ: false,
          hasM: true,
          paths: coords,
          spatialReference: { wkid: 3857 }
        })

        forma = crearLineaPerimetro(geometry)

        if (atributos.fill) {
          forma.symbol.color = hexToBytes(atributos.fill.substring(1))
        } else {
          forma.symbol.color = colorDefault
        }

        simbolo = forma.symbol
        tipo = geometry.type
      }
      atributos.ID_GRAFICO = VueInst.$uuid.createUUID()
      const graphic = {
        attributes: atributos,
        geometry,
        type: tipo,
        symbol: simbolo
      }
      graphics.push(graphic)
    }
    deferred.resolve(graphics)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

function changeCoords(coords, tipo) {
  let coordX
  let coordY

  if (tipo === 'multipolygon' || tipo === 'polygon' || tipo === 'linestring' || tipo === 'polyline') {
    for (let i = 0; i < coords.length; i++) {
      for (let j = 0; j < coords[i].length; j++) {
        if (!(typeof (coords[i][j][0]) === 'number')) {
          for (let z = 0; z < coords[i][j].length; z++) {
            coordX = coords[i][j][z][0]
            coordY = coords[i][j][z][1]

            if (isLatLon(coordX, coordY)) {
              const coordXFixed = R * coordX * Math.PI / 180
              const coordYFixed = R * Math.log(Math.tan(Math.PI / 4 + (coordY * Math.PI / 180) / 2))

              coords[i][j][z][0] = coordXFixed
              coords[i][j][z][1] = coordYFixed
            }
          }
        } else {
          coordX = coords[i][j][0]
          coordY = coords[i][j][1]

          if (isLatLon(coordX, coordY)) {
            const coordXFixed = R * coordX * Math.PI / 180
            const coordYFixed = R * Math.log(Math.tan(Math.PI / 4 + (coordY * Math.PI / 180) / 2))

            coords[i][j][0] = coordXFixed
            coords[i][j][1] = coordYFixed
          }
        }
      }
    }
  } else if (tipo === 'point') {
    coordX = coords[0]
    coordY = coords[1]

    if (isLatLon(coordX, coordY)) {
      const coordXFixed = R * coordX * Math.PI / 180
      const coordYFixed = R * Math.log(Math.tan(Math.PI / 4 + (coordY * Math.PI / 180) / 2))

      coords[0] = coordXFixed
      coords[1] = coordYFixed
    }
  }

  return coords
}

export function isLatLon(lon, lat) {
  return (
    lon >= -180 && lon <= 180 &&
    lat >= -90 && lat <= 90
  )
}

export function getDisplayCoords() {
  return displayCoords
}

export function coordsForDisplay(coords) {
  if (coords) {
    const lon = (coords[0] / R) * (180 / Math.PI)
    const latRad = (2 * Math.atan(Math.exp(coords[1] / R))) - (Math.PI / 2)
    const lat = latRad * (180 / Math.PI)
    return { lon, lat }
  } else {
    return null
  }
}

function getSymbols(archivoQML) {
  const symbols = []

  const symbolNodes = archivoQML.getElementsByTagName('symbol')

  for (let i = 0; i < symbolNodes.length; i++) {
    const symbol = symbolNodes[i]
    const name = symbol.getAttribute('name')
    const type = symbol.getAttribute('type')

    const layers = []
    const layerNodes = symbol.getElementsByTagName('layer')

    for (let j = 0; j < layerNodes.length; j++) {
      const layer = layerNodes[j]
      const layerClass = layer.getAttribute('class')

      const props = {}
      const optionContainers = layer.getElementsByTagName('Option')

      for (let k = 0; k < optionContainers.length; k++) {
        const optionGroup = optionContainers[k]

        if (optionGroup.getAttribute('type') === 'Map') {
          const innerOptions = optionGroup.getElementsByTagName('Option')

          for (let m = 0; m < innerOptions.length; m++) {
            const opt = innerOptions[m]
            const key = opt.getAttribute('name')
            const value = opt.getAttribute('value')
            if (key && value !== null) {
              props[key] = value
            }
          }
        }
      }

      layers.push({
        class: layerClass,
        props
      })
    }

    symbols.push({
      name,
      type,
      layers
    })
  }

  return symbols
}

function getCoords(elapsed_mi, categorias, simbolos) {
  let index
  for (let i = 0; i < categorias.length; i++) {
    if (categorias[i].value === elapsed_mi.toString()) {
      index = categorias[i].symbol
      break
    }
  }

  let colorQML
  for (let j = 0; j < simbolos.length; j++) {
    if (index === simbolos[j].name) {
      colorQML = simbolos[j].layers[0].props.color
      break
    }
  }

  const colorFinal = []
  colorFinal[0] = parseInt(colorQML.substring(0, colorQML.indexOf('r') - 1).split(',')[0])
  colorFinal[1] = parseInt(colorQML.substring(0, colorQML.indexOf('r') - 1).split(',')[1])
  colorFinal[2] = parseInt(colorQML.substring(0, colorQML.indexOf('r') - 1).split(',')[2])
  colorFinal[3] = parseInt(colorQML.substring(0, colorQML.indexOf('r') - 1).split(',')[3])

  return colorFinal
}

export const drawGraphicsPerimetro = (graphicList) => {
  const layer = map.findLayerById('layer-perimetros')
  closeMapPopups()
  layer.removeAll()

  if (!graphicList || graphicList.length === 0) {
    return
  }

  const graphics = createGraphicsDrawPerimeter(graphicList)
  layer.addMany(graphics)
}

export const removeGraphicsPerimetro = (graphicList) => {
  const layer = map.findLayerById('layer-perimetros')
  closeMapPopups()
  const list = graphicList.map(graphic => JSON.parse(graphic.ATRIBUTOS).ID_GRAFICO)
  const graphics = layer.graphics.items.filter(graphic => list.includes(graphic.attributes.ID_GRAFICO))
  layer.removeMany(graphics)
}

export async function actualizarHojaCuadriculaWMS(lat, lon) {
  const hoja = await consultarCapa(
    lat,
    lon,
    'cuadricula-mtn50-actual'
  )

  const cuadricula = await consultarCapa(
    lat,
    lon,
    'cuadricula-mtn25-actual'
  )

  return {
    hoja: hoja?.numero || '',
    cuadricula: cuadricula?.numero || ''
  }
}

async function consultarCapa(lat, lon, layerName) {
  const url = 'https://www.ign.es/wms-inspire/cuadriculas'

  const delta = 0.001

  const params = new URLSearchParams({
    SERVICE: 'WMS',
    VERSION: '1.3.0',
    REQUEST: 'GetFeatureInfo',
    CRS: 'EPSG:4326',
    BBOX: `${lat - delta},${lon - delta},${lat + delta},${lon + delta}`,
    WIDTH: 101,
    HEIGHT: 101,
    I: 50,
    J: 50,
    QUERY_LAYERS: layerName,
    LAYERS: layerName,
    INFO_FORMAT: 'application/json'
  })

  try {
    const res = await fetch(`${url}?${params}`)
    const json = await res.json()
    return json?.features?.[0]?.properties || null
  } catch (error) {
    console.error('[helpers/ArcGIS] consultarCapa: Error: ', error)
    return null
  }
}

// #region CREACION DE FORMAS
function crearPuntoPerimetro(geometry) {
  const forma = {
    geometry: {
      longitude: geometry.longitude,
      latitude: geometry.latitude
    },
    symbol: {
      type: 'simple-marker',
      color: [],
      size: 6,
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid'
      }
    }
  }
  return forma
}

function crearPoligonoPerimetro(geometry) {
  const forma = {
    aggregateGeometries: null,
    geometry,
    symbol: {
      type: 'simple-fill',
      color: [],
      style: 'solid',
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid'
      }
    },
    attributes: {},
    popupTemplate: null
  }
  return forma
}

function crearLineaPerimetro(geometry) {
  const forma = {
    aggregateGeometries: null,
    geometry,
    color: 'lightblue',
    symbol: {
      type: 'simple-line',
      color: [],
      width: 3,
      style: 'solid'
    },
    attributes: {},
    popupTemplate: null
  }
  return forma
}

function hexToBytes(hex) {
  const bytes = []
  for (let c = 0; c < hex.length; c += 2) {
    bytes.push(parseInt(hex.substr(c, 2), 16))
  }
  return bytes
}

async function fetchJSON(url) {
  return await fetch(url).then((response) => {
    return response.json()
  })
}

export async function dragAndDrop(selectedFile, type, fileNumber) {
  let layer = {}

  if (type === 'geojson') {
    const urlCreada = URL.createObjectURL(selectedFile)
    layer = iniciarLayer('geojson')
    layer.url = urlCreada

    const archivo = await fetchJSON(urlCreada)
    if (archivo.metadata) {
      layer.id = archivo.metadata.title.split(' ').join('-')
      layer.title = archivo.metadata.title
    } else {
      layer.id = `layer-geojson ${fileNumber}`
      layer.title = `${'Layer GeoJSON '}${fileNumber}`
    }
  } else {
    switch (type) {
      case 'kml':
        layer = iniciarLayer('kml')
        break
      case 'zip':
        layer = iniciarLayer('shp')
        break
      default:
        Vue.notify({
          title: 'Error Drag and Drop',
          text: 'Los archivos deben ser .geojson, .kml o .zip(para mostrar archivos .shp)',
          type: 'error'
        })

        return null
    }
  }

  return layer
}

function iniciarLayer(formato) {
  const layer = {
    id: '',
    type: '',
    title: '',
    url: '',
    visible: true,
    opacity: 1,
    posicion: 999,
    addListLayer: true
  }

  switch (formato) {
    case 'geojson':
      layer.type = 'geojson'
      break
    case 'kml':
      layer.id = 'layerKML'
      layer.type = 'graphicsLayer'
      layer.title = 'Layer KML'
      break
    case 'shp':
      layer.id = 'layerSHP'
      layer.type = 'graphicsLayer'
      layer.title = 'Layer SHP'
  }

  return layer
}

export async function insertLayer(archivo, formato, retorno) {
  if (!archivo) {
    Vue.notify({
      title: 'Error Drag and Drop',
      text: 'No se ha proporcionado ningún archivo.',
      type: 'error'
    })

    return null
  }

  let geojson

  switch (formato) {
    case 'SHP':
      geojson = await procesarArchivoSHP(archivo)
      break
    case 'KML':
      geojson = await procesarArchivoKML(archivo)
      break
  }

  if (!geojson) {
    Vue.notify({
      title: 'Error Drag and Drop',
      text: 'Formato no soportado.',
      type: 'error'
    })

    return null
  }

  const graficos = await perimeterGeoJsonToGraphicsPerimeter(geojson, false)

  if (!retorno) {
    if (formato === 'KML') {
      const capa = map.findLayerById('layerKML')
      capa.removeAll()
      capa.addMany(graficos)
    } else if (formato === 'SHP') {
      const capa = map.findLayerById('layerSHP')
      capa.removeAll()
      capa.addMany(graficos)
    }
  } else {
    return graficos
  }

  return graficos
}

export async function fileToGeoJSON(archivo, formato) {
  try {
    if (!archivo) throw new Error('No se ha proporcionado ningún archivo.')

    let geojson
    if (formato === 'SHP') {
      geojson = await procesarArchivoSHP(archivo)
    } else if (formato === 'KML') {
      geojson = await procesarArchivoKML(archivo)
    } else {
      throw new Error(`Formato no soportado: ${formato}`)
    }
    const graficos = await perimeterGeoJsonToGraphicsPerimeter(geojson, true)
    const capa = map.findLayerById('layer-perimetros')
    capa.removeAll()
    capa.addMany(graficos)

    return graficos
  } catch (error) {
    console.error('Error en procesarArchivoSHP', error)
    throw error
  }
}

async function procesarArchivoSHP(archivo) {
  const zip = await JSZip.loadAsync(archivo)
  const archivos = zip.files

  const nombreShp = Object.keys(archivos).find(n => n.endsWith('.shp'))
  const nombreDbf = Object.keys(archivos).find(n => n.endsWith('.dbf'))
  const nombrePrj = Object.keys(archivos).find(n => n.endsWith('.prj'))
  const nombreShx = Object.keys(archivos).find(n => n.endsWith('.shx'))
  const nombreQml = Object.keys(archivos).find(n => n.endsWith('.qml'))

  if (!nombreShp || !nombreDbf || !nombrePrj || !nombreShx) {
    Vue.notify({
      title: 'Error Drag and Drop',
      text: 'Faltan archivos necesarios en el ZIP (.shp, .dbf, .prj, .shx).',
      type: 'error'
    })

    return null
  }
  if (tienecarpeta(archivos, nombreQml !== undefined)) {
    Vue.notify({
      title: 'Error Drag and Drop',
      text: 'El archivo ZIP no debe contener carpetas, solo los archivos necesarios (.shp, .dbf, .prj, .shx, .qml (opcional))',
      type: 'error'
    })

    return null
  }

  const [shp, dbf, prj, shx] = await Promise.all([
    zip.file(nombreShp).async('arraybuffer'),
    zip.file(nombreDbf).async('arraybuffer'),
    zip.file(nombrePrj).async('string'),
    zip.file(nombreShx).async('arraybuffer')
  ])

  if (nombreQml) {
    const qml = await zip.file(nombreQml).async('string')

    archivoQML = new DOMParser().parseFromString(qml, 'text/xml')
  }

  const formData = new FormData()
  formData.append('shp', new Blob([shp]))
  formData.append('dbf', new Blob([dbf]))
  formData.append('prj', new Blob([prj]))
  formData.append('shx', new Blob([shx]))

  const respuesta = await api.incendio.addImportedPerimetro(formData)
  return respuesta.data
}

function tienecarpeta(archivo, tieneQML) {
  let index = null

  const archivos = [
    Object.keys(archivo).find(n => n.endsWith('.shp')),
    Object.keys(archivo).find(n => n.endsWith('.dbf')),
    Object.keys(archivo).find(n => n.endsWith('.prj')),
    Object.keys(archivo).find(n => n.endsWith('.shx'))
  ]

  if (tieneQML) {
    archivos.push(Object.keys(archivo).find(n => n.endsWith('.qml')))
  }

  for (let i = 0; i < archivos.length; i++) {
    index = archivos[i].indexOf('/')
    if (index !== -1) {
      return true
    }
  }

  return false
}

async function procesarArchivoKML(archivo) {
  const textoKML = await archivo.text()
  const domKML = new DOMParser().parseFromString(textoKML, 'application/xml')
  const geojson = toGeoJSON.kml(domKML)

  geojson.crs = {
    type: 'name',
    properties: {
      name: 'urn:ogc:def:crs:OGC:1.3:CRS84'
    }
  }

  return reproyectarGeoJSON(geojson)
}

function reproyectarGeoJSON(geojson) {
  const origen = 'EPSG:4326'
  const destino = 'EPSG:3857'
  let status = true

  geojson.features = geojson.features.map((feature) => {
    if (!feature.geometry) {
      Vue.notify({
        title: 'Error Drag and Drop',
        text: 'Error al procesar el archivo kml',
        type: 'error'
      })

      status = false
      return null
    }
    const tipo = feature.geometry.type
    const coords = feature.geometry.coordinates

    if (tipo === 'Polygon') {
      feature.geometry.coordinates = coords.map(anillo =>
        anillo.map(([lon, lat]) => proj4(origen, destino, [lon, lat]))
      )
    } else if (tipo === 'MultiPolygon') {
      feature.geometry.coordinates = coords.map(poligono =>
        poligono.map(anillo =>
          anillo.map(([lon, lat]) => proj4(origen, destino, [lon, lat]))
        )
      )
    }

    return feature
  })

  if (!status) {
    return null
  }

  return geojson
}

// #endregion

// #endregion *******************************************************************************************************************************************************************

// #region SIMULACION
let simulacionSVM = null
// let simulacionGraphics = []
// let simulationGraphics.isocronas = []
// let longitudLlamaGraphics = []
// let intensidadLlamaGraphics = []
// let velocidadPropagacionGraphics = []

let simulationGraphics = new Map()

const TIPO_GRAFICO_FOCO = 'foco'

function toWebMercator([x, y]) {
  const [mercX, mercY] = convertirETRS89UTM30NToMercXY(x, y)
  return [mercX, mercY]
}

export function getSimulacionById(id) {
  return simulationGraphics.get(id)
}

export const setSimulacionActivaId = (id) => {
  simulacionActivaId = id
}

export const getSimulacionActivaID = () => {
  return simulacionActivaId
}

export const getSliderTime = () => {
  return elapsedMi
}

export const setSliderTime = (time) => {
  elapsedMi = time
}

export const getAllSimulaciones = () => {
  if (simulationGraphics.size >= 0) {
    return Array.from(simulationGraphics.values())
  } else {
    return []
  }
}

export async function eliminaSimulacion(item) {
  const id = item.ID_SIMULACION

  if (simulationGraphics.size === 1) {
    clearSimulacion()
    VueInst.$store.dispatch('simulacion/cerrarSimulacionSlider', true)
  } else {
    simulationGraphics.delete(id)
    VueInst.$eventHub.$emit('update-simulaciones-list')
    paintOrigenSimulacion()
    paintGraficosSimulacion()

    const simulaciones = getAllSimulaciones()
    const activa = simulaciones[0].sim.simulacion.ID_SIMULACION

    setSimulacionActivaId(activa)
    VueInst.$eventHub.$emit('setSimulacionActiva', activa)

    if (item.TIPO === 'VIENTO' || item.VIENTOS) {
      const layerLineas = map.findLayerById(`layer-viento-lineas-${id}`)
      const layerPoints = map.findLayerById(`layer-viento-${id}`)

      if (layerLineas) {
        map.remove(layerLineas)
      }

      if (layerPoints) {
        map.remove(layerPoints)
      }
    }

    if (item.TIPO !== 'VIENTO') {
      const layerSimulacion = map.findLayerById('layer-simulacion')
      const layerIsocronas = map.findLayerById('layer-isocronas')
      const layerIsocronasLabels = map.findLayerById('layer-isocronas-labels')

      if (layerSimulacion) {
        await deleteFeaturesBySimId(layerSimulacion, id)
      }

      if (layerIsocronas) {
        await deleteFeaturesBySimId(layerIsocronas, id)
      }

      if (layerIsocronasLabels) {
        const labelsToKeep = []

        for (const simulacion of [...simulationGraphics.values()]) {
          if (simulacion.sim.isocronasLabels && simulacion.sim.id !== id) {
            labelsToKeep.push(
              ...simulacion.sim.isocronasLabels.filter(label => label.attributes.Elapsed_Mi <= elapsedMi)
            )
          }
        }

        layerIsocronasLabels.removeAll()

        if (labelsToKeep.length > 0) {
          layerIsocronasLabels.addMany(labelsToKeep)
        }
      }
    }
  }
}

const deleteFeaturesBySimId = async(layer, id) => {
  const query = layer.createQuery()
  query.where = id !== null ? `simId = '${id}'` : '1=1'

  const result = await layer.queryFeatures(query)

  if (result.features.length > 0) {
    await layer.applyEdits({
      deleteFeatures: result.features
    })
  }
}

export const initSimulacionSketchViewModel = async(data) => {
  // TODO:
  try {
    const [SketchViewModel, projection] = await loadModules([
      'esri/widgets/Sketch/SketchViewModel',
      'esri/geometry/projection'
    ])
    const event = data.event
    const callback = data.callback

    let color

    switch (data.categoria) {
      case 'foco':
        color = [255, 0, 0, 0.5]
        break
      case 'combustible':
        color = [183, 244, 216, 0.5]
        break
      case 'zonaNoCombustible':
        color = [15, 10, 222, 0.5]
        break
    }

    const layer = map.findLayerById('layer-sketch-simulacion')

    simulacionSVM = new SketchViewModel({
      view: mapView,
      layer,
      pointSymbol: {

        type: 'simple-marker',
        color,
        size: 6

      },
      polylineSymbol: {
        type: 'simple-line',
        style: 'solid',
        color,
        width: 2

      },
      polygonSymbol: {
        type: 'simple-fill',
        style: 'solid',
        color

      }
    })
    simulacionSVM.on(['create', 'delete', 'update'], async(evt) => {
      let symbol, coordinates
      let geometry = null
      if (evt.state === 'complete' && evt.type === 'create') {
        await projection.load()
        geometry = projection.project(evt.graphic.geometry, {
          wkid: 25830
        })

        if (
          evt.graphic.geometry.type === 'point' &&
          evt.graphic.symbol.type === 'simple-marker'
        ) {
          symbol = simulacionSVM.pointSymbol
          coordinates = [geometry.x, geometry.y]
        } else if (evt.graphic.geometry.type === 'polyline') {
          symbol = simulacionSVM.polylineSymbol
          coordinates = geometry.paths[0]
        } else if (evt.graphic.geometry.type === 'polygon') {
          symbol = simulacionSVM.polygonSymbol
          coordinates = geometry.rings[0]
        }

        const graphic = {
          coordinates,
          type: evt.graphic.geometry.type,
          symbol
        }
        callback(graphic)
      }
    })

    // Create graphic
    simulacionSVM.create(event)
  } catch (err) {
    VueInst.$log.error(err)
  }
}

export const addPointElement = (data) => {
  const layer = map.findLayerById('layer-sketch-simulacion')

  const callback = data.callback

  const geometry = {
    type: 'point',
    latitude: data.latitude,
    longitude: data.longitude
  }

  let color

  switch (data.categoria) {
    case 'foco':
      color = [255, 0, 0, 0.5]
      break
    case 'combustible':
      color = [183, 244, 216, 0.5]
      break
    case 'zonaNoCombustible':
      color = [15, 10, 222, 0.5]
      break
  }

  const punto = {
    geometry,
    // attributes: null, // attributes,
    symbol: {
      type: 'simple-marker',
      color,
      size: 6,
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid'
      }
    }
  }

  layer.add(punto)
  callback(punto)
}

export const drawSimulationElements = (elements) => {
  elements = JSON.parse(JSON.stringify(elements))

  const layer = map.findLayerById('layer-sketch-simulacion')
  // layer.graphics.removeAll()

  for (let i = 0; i < elements.length; i++) {
    let forma

    if (elements[i].geometry.symbol.type === 'esriSMS' || elements[i].geometry.symbol.type === 'simple-marker') {
      forma = crearPunto(elements[i].latitud, elements[i].longitud, elements[i].categoria)
    }

    if (elements[i].geometry.symbol.type === 'esriSLS') {
      forma = crearLinea(elements[i].geometry.coordinates, elements[i].categoria)
    }

    if (elements[i].geometry.symbol.type === 'esriSFS') {
      forma = crearPoligono(elements[i].geometry.coordinates, elements[i].categoria)
    }
    layer.add(forma)
  }
}

function crearPunto(lat, lon, categoria) {
  const geometry = {
    type: 'point',
    latitude: lat,
    longitude: lon
  }

  let color

  switch (categoria) {
    case 'foco':
      color = [255, 0, 0, 0.5]
      break
    case 'combustible':
      color = [183, 244, 216, 0.5]
      break
    case 'zonaNoCombustible':
      color = [15, 10, 222, 0.5]
      break
  }

  const forma = {
    geometry,
    symbol: {
      type: 'simple-marker',
      color,
      size: 6,
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid'
      }
    }
  }
  return forma
}

function crearLinea(paths, categoria) {
  const geometry = new Polyline({
    paths: [paths.map(toWebMercator)],
    spatialReference: { wkid: 102100, latestWkid: 3857 }
  })

  let color

  switch (categoria) {
    case 'foco':
      color = [255, 0, 0, 0.5]
      break
    case 'combustible':
      color = [183, 244, 216, 0.5]
      break
    case 'zonaNoCombustible':
      color = [15, 10, 222, 0.5]
      break
  }

  const forma = {
    aggregateGeometries: null,
    geometry,
    symbol: {
      type: 'simple-line',
      color,
      width: 2,
      style: 'solid'
    },
    attributes: {},
    popupTemplate: null
  }

  return forma
}

function crearPoligono(rings, categoria) {
  const geometry = new Polygon({
    rings: [rings.map(toWebMercator)],
    spatialReference: { wkid: 102100, latestWkid: 3857 }
  })

  let color

  switch (categoria) {
    case 'foco':
      color = [255, 42, 4, 0.5]
      break
    case 'combustible':
      color = [183, 244, 216, 0.5]
      break
    case 'zonaNoCombustible':
      color = [15, 10, 222, 0.5]
      break
  }

  const forma = {
    aggregateGeometries: null,
    geometry,
    symbol: {
      type: 'simple-fill',
      color,
      style: 'solid',
      outline: {
        type: 'simple-line',
        color: [50, 50, 50, 255],
        width: 1,
        style: 'solid'
      }
    },
    attributes: {},
    popupTemplate: null
  }

  return forma
}

export async function obtenerCentro(geometriaData, tipo, wkidEntrada) {
  const [Extent] = await loadModules([
    'esri/geometry/Extent'
  ])

  const esriModules = { Polygon, Polyline, Extent }

  if (tipo === 'INCENDIO') {
    const puntoCentro = calcularCentroIncendio(geometriaData, wkidEntrada, esriModules)
    return proyectarPuntoAMercator(puntoCentro, wkidEntrada)
  }

  return calcularCentroVientos(geometriaData.response?.features, esriModules)
}

function calcularCentroIncendio(geometriaData, wkid, { Polygon, Polyline }) {
  const { type } = geometriaData
  const coords = geometriaData.coordinates || geometriaData.rings
  const spatialReference = { wkid }
  let poligono, polilinea

  const procesarCoordenadas = () => {
    if (geometriaData.rings) {
      return coords.filter(punto => Array.isArray(punto) && punto.length === 2)
    }
    return [coords]
  }

  switch (type?.toLowerCase()) {
    case 'point':
      if (!coords || coords.length < 2) {
        throw new Error('Las coordenadas del Point son inválidas.')
      }
      return { x: coords[0], y: coords[1] }

    case 'polygon':
      poligono = new Polygon({ rings: procesarCoordenadas(), spatialReference })
      return poligono.centroid

    case 'polyline':
      polilinea = new Polyline({ paths: procesarCoordenadas(), spatialReference })
      return polilinea.extent.center

    default:
      throw new Error(`Tipo de geometría no soportado para calcular el centro: ${type}`)
  }
}

function proyectarPuntoAMercator(puntoCentro, wkidEntrada) {
  const pointWebMercator = {
    x: puntoCentro.x,
    y: puntoCentro.y,
    z: 0,
    spatialReference: {
      wkid: 102100,
      latestWkid: 3857
    }
  }

  if (wkidEntrada === 25830) {
    const [x3857, y3857] = convertirETRS89UTM30NToMercXY(puntoCentro.x, puntoCentro.y)
    pointWebMercator.x = x3857
    pointWebMercator.y = y3857
  }

  return pointWebMercator
}

function calcularCentroVientos(features = [], { Polygon, Polyline, Extent }) {
  if (!features || features.length === 0) {
    throw new Error('No se pudo calcular el centro: la colección de features está vacía o es inválida.')
  }

  const extentGlobal = features.reduce((acumulador, feature) => {
    const { geometry } = feature
    const type = geometry.type.toLowerCase()
    const coords = geometry.coordinates
    let tempExtent = null

    if (type === 'point') {
      tempExtent = new Extent({
        xmin: coords[0],
        ymin: coords[1],
        xmax: coords[0],
        ymax: coords[1],
        spatialReference: 4326
      })
    } else if (type === 'polygon') {
      tempExtent = new Polygon({ rings: coords, spatialReference: 4326 }).extent
    } else if (type === 'polyline') {
      tempExtent = new Polyline({ paths: coords, spatialReference: 4326 }).extent
    }

    if (!tempExtent) return acumulador

    return acumulador ? acumulador.union(tempExtent) : tempExtent.clone()
  }, null)

  if (!extentGlobal) {
    throw new Error('No se pudo generar un Extent válido a partir de las features.')
  }

  return extentGlobal.center
}

export const clearSimulacionSketch = () => {
  const layer = map.findLayerById('layer-sketch-simulacion')
  layer.removeAll()
}
export const stopSimulacionSketchViewModel = () => {
  if (!simulacionSVM) return
  simulacionSVM.cancel()
}

export const convertListGeometryToLatLon = listgeometry => {
  const newList = []
  for (const geometry of listgeometry) {
    const newGeom = []
    for (const point of geometry) {
      const newpoint = convertirETRS89ToLatLon(point[0], point[1])

      const temp = newpoint[0]
      newpoint[0] = newpoint[1]
      newpoint[1] = temp

      newGeom.push(newpoint)
    }
    newList.push(newGeom)
  }

  return newList
}

let cacheLongitud, cacheIntensidad, cacheVelocidad, cacheSimulacionData
export const paintSimulacion = async(simulacion) => {
  if (!map) return
  const uuid = simulacion.sim.simulacion.ID_SIMULACION
  const tipo = simulacion.id

  const simData = simulationGraphics.get(uuid)
  if (simData && simData.sim.ultimoModo === tipo) {
    return
  }

  simulacion.sim.id = uuid
  try {
    let response
    let isocronasData

    switch (tipo) {
      case 'simulacion':
        if (simulationGraphics.has(uuid)) {
          simulationGraphics.get(uuid).sim.modo = 'simulacion'
          response = { data: cacheSimulacionData }
          break
        }
        response = await api.simulacion.getSimulacion(uuid, 28530)

        if (response.status > 200) {
          // Hacer que salga error cuando el simStatus no sea correcto
          return
        }

        cacheSimulacionData = response.data
        simulationGraphics.set(uuid, simulacion)
        VueInst.$eventHub.$emit('update-simulaciones-list')

        simulationGraphics.get(uuid).sim.coordenadas = await createGraphicsSimulacion(response.data, simulacion.sim.id, simulacion.sim.simulacion.FECHA_INICIO)

        isocronasData = await createGraphicsIsocronas(response.data, simulacion.sim.id)
        simulationGraphics.get(uuid).sim.isocronas = isocronasData.polygons
        simulationGraphics.get(uuid).sim.isocronasLabels = isocronasData.labels

        simulationGraphics.get(uuid).sim.graficosSimulacion = simulacion.sim.simulacion.GRAFICOS ? await createAuxiliarGraphicsSimulacion(simulacion.sim.simulacion.GRAFICOS, simulacion.sim.id) : []
        simulationGraphics.get(uuid).sim.origen = createOriginGraphics(simulacion.sim.simulacion.GRAFICOS, simulacion.sim.id)
        simulationGraphics.get(uuid).sim.modo = 'simulacion'

        if (!simulationGraphics.get(uuid).sim.simulacion || !simulationGraphics.get(uuid).sim.isocronas) {
          //  'La simulación no se ha creado correctamente'
          return
        }

        break

      case 'longitudLlama':
        if (simulationGraphics.get(uuid).sim.longitudLlama.length === 0) {
          response = await api.simulacion.getFlameLength(uuid, 28530)
          simulationGraphics.get(uuid).sim.longitudLlama = await createGraphics(response.data, simulacion.sim.id)
          cacheLongitud = response.data
        } else {
          simulationGraphics.get(uuid).sim.longitudLlama = await createGraphics(cacheLongitud, simulacion.sim.id)
          response = { data: cacheLongitud }
        }

        simulationGraphics.get(uuid).sim.modo = 'longitudLlama'
        break

      case 'intensidadLlama':
        if (simulationGraphics.get(uuid).sim.intensidadLlama.length === 0) {
          response = await api.simulacion.getIntensity(uuid, 28530)
          simulationGraphics.get(uuid).sim.intensidadLlama = await createGraphics(response.data, simulacion.sim.id)
          cacheIntensidad = response.data
        } else {
          simulationGraphics.get(uuid).sim.intensidadLlama = await createGraphics(cacheIntensidad, simulacion.sim.id)
          response = { data: cacheIntensidad }
        }
        simulationGraphics.get(uuid).sim.modo = 'intensidadLlama'
        break

      case 'velocidadPropagacion':
        if (simulationGraphics.get(uuid).sim.velocidadPropagacion.length === 0) {
          response = await api.simulacion.getSpreadRate(uuid, 28530)
          simulationGraphics.get(uuid).sim.velocidadPropagacion = await createGraphics(response.data, simulacion.sim.id)
          cacheVelocidad = response.data
        } else {
          simulationGraphics.get(uuid).sim.velocidadPropagacion = await createGraphics(cacheVelocidad, simulacion.sim.id)
          response = { data: cacheVelocidad }
        }
        simulationGraphics.get(uuid).sim.modo = 'velocidadPropagacion'
        break

      default:
        console.error('Tipo no reconocido:', tipo)
        break
    }

    store.dispatch('shareMap/paintSimulation', { response, simulacion })
    await paintSimulacionLayer(simulacion)
    if (tipo === 'simulacion') {
      await centerSimulacion(simulacion.sim.simulacion.TIPO, uuid)
    }

    simulacionActivaId = uuid
  } catch (err) {
    console.error('Error arcgis paintSimulacion ', err)
    throw err
  }
}

let elapsedMi = 0 // minuto transcurrido que se muestra; coincide con el min del slider
async function paintSimulacionLayer(simulacion) {
  const layerSimulacion = map.findLayerById('layer-simulacion')
  const layerIsocronas = map.findLayerById('layer-isocronas')
  const layerIsocronasLabels = map.findLayerById('layer-isocronas-labels')

  const uuid = simulacion.sim.id
  const simData = simulationGraphics.get(uuid)
  if (!simData) return

  await deleteFeaturesBySimId(layerSimulacion, uuid)
  await deleteFeaturesBySimId(layerIsocronas, uuid)

  const labelsToKeep = layerIsocronasLabels.graphics.items.filter(
    g => g.attributes.simId !== uuid
  )

  layerIsocronasLabels.removeAll()

  if (labelsToKeep.length > 0) {
    layerIsocronasLabels.addMany(labelsToKeep)
  }

  if (simulacion.id === 'simulacion') {
    updateSimulationRenderer(layerSimulacion, getAllVisibleSimulationGraphics())
    await layerSimulacion.applyEdits({ addFeatures: simData.sim.coordenadas })

    updateSimulationRenderer(layerIsocronas, simData.sim.isocronas)
    await layerIsocronas.applyEdits({ addFeatures: simData.sim.isocronas })

    const labels = simData.sim.isocronasLabels.filter(
      x => x.attributes.Elapsed_Mi <= elapsedMi
    )

    if (labels.length > 0) {
      layerIsocronasLabels.addMany(labels)
    }

    layerSimulacion.definitionExpression = buildSimulationExpression(elapsedMi)
    layerIsocronas.definitionExpression = buildSimulationExpression(elapsedMi)

    layerIsocronas.visible = true
    layerIsocronasLabels.visible = true
  } else {
    const graphics = simData.sim[simulacion.id]

    if (Array.isArray(graphics) && graphics.length > 0) {
      updateSimulationRenderer(layerSimulacion, getAllVisibleSimulationGraphics())
      await layerSimulacion.applyEdits({ addFeatures: graphics })
    }

    layerSimulacion.definitionExpression = buildSimulationExpression(elapsedMi)
    layerIsocronas.definitionExpression = buildSimulationExpression(elapsedMi)

    layerSimulacion.visible = true
    layerIsocronas.visible = true
    layerIsocronasLabels.visible = true
  }

  simData.sim.ultimoModo = simulacion.id

  paintOrigenSimulacion()
  paintGraficosSimulacion()
}

function paintOrigenSimulacion() {
  const layerOrigen = map.findLayerById('layer-origen-simulacion')
  if (!layerOrigen) return

  const graphics = []

  for (const simulacion of [...simulationGraphics.values()]) {
    if (simulacion.sim.modo !== 'simulacion') continue
    if (Array.isArray(simulacion.sim.origen)) graphics.push(...simulacion.sim.origen)
  }

  layerOrigen.removeAll()

  if (graphics.length > 0) layerOrigen.addMany(graphics)
}

function paintGraficosSimulacion() {
  const layerGraficosSimulacion = map.findLayerById('layer-graficos-simulacion')
  if (!layerGraficosSimulacion) return

  const graphics = []

  for (const simulacion of [...simulationGraphics.values()]) {
    if (simulacion.sim.modo !== 'simulacion') continue
    if (Array.isArray(simulacion.sim.graficosSimulacion)) graphics.push(...simulacion.sim.graficosSimulacion)
  }

  layerGraficosSimulacion.removeAll()

  if (graphics.length > 0) layerGraficosSimulacion.addMany(graphics)
}

function buildSimulationExpression(elapsedMin) {
  const simulacionesActivas = [...simulationGraphics.values()]
    .map(x => x.sim.id)

  if (simulacionesActivas.length === 0) {
    return '1=0'
  }

  const simExpression = simulacionesActivas
    .map(id => `simId = '${id}'`)
    .join(' OR ')

  return `
    (${simExpression})
    AND Elapsed_Mi <= ${elapsedMin}
  `
}

function updateSimulationRenderer(layer, graphics) {
  const colors = [...new Set(
    graphics.map(g => g.attributes.COLOR)
  )]

  layer.renderer = {
    type: 'unique-value',
    field: 'COLOR',
    uniqueValueInfos: colors.map(colorString => {
      const color = colorString.split(',').map(Number)

      return {
        value: colorString,
        symbol: {
          type: 'simple-fill',
          color: [color[0], color[1], color[2], 0.8],
          outline: {
            color: 'white',
            width: 0
          }
        }
      }
    })
  }
}

function getAllVisibleSimulationGraphics() {
  const graphics = []

  for (const sim of [...simulationGraphics.values()]) {
    if (sim.sim.modo === 'simulacion') {
      if (sim.sim.coordenadas) {
        graphics.push(
          ...sim.sim.coordenadas.filter(
            x => x.attributes.Elapsed_Mi <= elapsedMi
          )
        )
      }
    } else {
      const modeGraphics = sim.sim[sim.sim.modo]

      if (Array.isArray(modeGraphics)) {
        graphics.push(...modeGraphics)
      }
    }
  }

  return graphics
}

function createAuxiliarGraphicsSimulacion(data, simId) {
  const deferred = Q.defer()

  try {
    const graphics = []
    const auxiliares = (data ?? []).filter(g => g.TIPO_GRAFICO !== TIPO_GRAFICO_FOCO)

    for (let i = 0; i < auxiliares.length; i++) {
      let graphic

      switch (auxiliares[i].TIPO_GEOMETRIA) {
        case 'polyline':
          graphic = crearLinea(JSON.parse(auxiliares[i].GEOMETRIA), auxiliares[i].TIPO_GRAFICO)
          break
        case 'polygon':
          graphic = crearPoligono(JSON.parse(auxiliares[i].GEOMETRIA), auxiliares[i].TIPO_GRAFICO)
          break
      }

      if (graphic) {
        graphic.attributes = { ...graphic.attributes, simId }
        graphics.push(graphic)
      }
    }

    deferred.resolve(graphics)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

function createOriginGraphic(grafico, simId) {
  const coordenadas = JSON.parse(grafico.GEOMETRIA)
  const attributes = { simId, TIPO_GRAFICO: TIPO_GRAFICO_FOCO }

  switch (grafico.TIPO_GEOMETRIA) {
    case 'point': {
      const [x, y] = toWebMercator(coordenadas)
      return new Graphic({
        geometry: new Point({ x, y, spatialReference: { wkid: 102100, latestWkid: 3857 } }),
        symbol: {
          type: 'simple-marker',
          color: [255, 0, 0, 0.75],
          size: 6,
          outline: { type: 'simple-line', color: [50, 50, 50, 255], width: 1, style: 'solid' }
        },
        attributes
      })
    }

    case 'polyline': {
      const { geometry, symbol } = crearLinea(coordenadas, 'foco')
      return new Graphic({ geometry, symbol, attributes })
    }

    case 'polygon': {
      const { geometry, symbol } = crearPoligono(coordenadas, 'foco')
      return new Graphic({ geometry, symbol, attributes })
    }

    default:
      return null
  }
}

function createOriginGraphics(graficos, simId) {
  try {
    return (graficos ?? [])
      .filter(g => g.TIPO_GRAFICO === TIPO_GRAFICO_FOCO)
      .map(g => createOriginGraphic(g, simId))
      .filter(Boolean)
  } catch (err) {
    console.error('[Helpers/ArcGIS.js] createOriginGraphics: ', err)
    return []
  }
}

let globalGraphicObjectId = 1
function createGraphicsSimulacion(response, id = null, fechaInicio = null) {
  const deferred = Q.defer()

  try {
    const featuresJson = response.features

    const groups = featuresJson.reduce((acc, item) => {
      const elapsedMin = item.properties?.Elapsed_Mi
      if (!acc[elapsedMin]) acc[elapsedMin] = []
      acc[elapsedMin].push(item)
      return acc
    }, {})

    const timeSteps = Object.keys(groups)
      .map(Number)
      .sort((a, b) => a - b)

    const initColor = [255, 255, 0]
    const endColor = [255, 0, 0]
    const colorGradient = getColorGradient(
      initColor,
      endColor,
      timeSteps.length
    )

    const graphics = []
    let geometriaAcumuladaAnterior = null

    for (let i = 0; i < timeSteps.length; i++) {
      const currentTime = timeSteps[i]
      const featuresInGroup = groups[currentTime]

      const rawGeometries = []

      featuresInGroup.forEach(f => {
        if (!f.geometry) return

        let rings = []
        if (f.geometry.type === 'MultiPolygon') {
          rings = f.geometry.coordinates.flat(1)
        } else if (f.geometry.type === 'Polygon') {
          rings = f.geometry.coordinates
        }

        const polygon = new Polygon({
          hasZ: false,
          hasM: false,
          rings,
          spatialReference: { wkid: 4326 }
        })
        rawGeometries.push(polygon)
      })

      if (rawGeometries.length === 0) continue
      let unionGeometry = GeometryEngine.union(rawGeometries)
      unionGeometry = GeometryEngine.simplify(unionGeometry)

      let geometriaParaPintar = unionGeometry

      if (geometriaAcumuladaAnterior) {
        geometriaParaPintar = GeometryEngine.difference(unionGeometry, geometriaAcumuladaAnterior)
      }

      if (geometriaParaPintar) {
        const color = colorGradient[i]
        const featureDate = fechaInicio ? DateService.toDayjs(fechaInicio).add(currentTime, DayjsUnit.Minute) : null

        const graphic = new Graphic({
          geometry: geometriaParaPintar,
          attributes: {
            OBJECTID: globalGraphicObjectId++,
            COLOR: `${color[0]},${color[1]},${color[2]}`,
            Elapsed_Mi: currentTime,
            ...featuresInGroup[0].properties,
            ...(featureDate?.isValid() ? { Hour: featureDate.hour(), Day: featureDate.date(), Month: featureDate.month() + 1 } : {}),
            simId: id
          }
        })

        graphics.push(graphic)
      }

      if (geometriaAcumuladaAnterior) {
        geometriaAcumuladaAnterior = GeometryEngine.union([geometriaAcumuladaAnterior, unionGeometry])
      } else {
        geometriaAcumuladaAnterior = unionGeometry
      }
    }

    deferred.resolve(graphics)
  } catch (err) {
    console.error('Error creando geometrías simulacion:', err)
    deferred.reject(err)
  }

  return deferred.promise
}

function createGraphics(response, id = null) {
  const deferred = Q.defer()
  try {
    const featuresJson = response.features

    const initColor = [255, 255, 0] // Amarillo
    const endColor = [255, 0, 0] // Rojo

    const mergedFeatures = []
    featuresJson.forEach((v) => {
      const features = v
      let geometry
      if (features.geometry.type === 'MultiPolygon') {
        features.geometry.coordinates.forEach((geo) => {
          const newGeometry = new Polygon({
            hasZ: false,
            hasM: false,
            rings: geo,
            spatialReference: {
              wkid: 4326
            }
          })

          mergedFeatures.push({
            geometry: newGeometry,
            properties: v.properties
          })
        })
      } else {
        geometry = new Polygon({
          hasZ: false,
          hasM: false,
          rings: features.geometry.coordinates,
          spatialReference: {
            wkid: 4326
          }
        })

        mergedFeatures.push({
          geometry,
          properties: v.properties
        })
      }
    })

    mergedFeatures.sort((a, b) => {
      return a.properties.upperValue - b.properties.upperValue
    })

    const colorGradient = getColorGradient(
      initColor,
      endColor,
      mergedFeatures.length
    )

    // Crear los graficos
    const graphics = []
    for (let i = 0; i < mergedFeatures.length; i++) {
      const f = mergedFeatures[i]
      const geometry = f.geometry

      let color = colorGradient[i]

      if (f.properties.fill) {
        const colorFill = f.properties.fill.replace('#', '')
        color = hexToBytes(colorFill)
      }

      const graphic = new Graphic({
        geometry,
        attributes: {
          OBJECTID: globalGraphicObjectId++,
          COLOR: `${color[0]},${color[1]},${color[2]}`,
          Elapsed_Mi: 0,
          ...f.properties,
          simId: id
        } // attributes
      })

      graphics.push(graphic)
    }

    deferred.resolve(graphics)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

let elapsedMinText = 0
async function createGraphicsIsocronas(response, id = null) {
  const deferred = Q.defer()

  try {
    const featuresJson = response.features

    // Agrupar por ElapsedMin
    const groups = await featuresJson.reduce((groups, item) => {
      const elapsedMin = item.properties?.Elapsed_Mi
      const group = groups[elapsedMin] || []
      group.push(item)
      groups[elapsedMin] = group
      return groups
    }, {})

    Object.keys(groups).forEach(key => {
      groups[key] = groups[key].filter(item => item.geometry !== null)
    })

    const initColor = [255, 255, 0] // Amarillo
    const endColor = [255, 0, 0] // Rojo
    const colorGradient = getColorGradient(
      initColor,
      endColor,
      Object.keys(groups).length
    )

    // Crear las geometrias juntando los poligonos del mismo ElapsedMin
    const mergedFeatures = []
    const elapsedMins = []
    try {
      Object.entries(groups).forEach(([k, v]) => {
        if (!Array.isArray(v) || v.length === 0) return
        const features = v

        elapsedMins.push(features[0].properties.Elapsed_Mi)
        const type = features[0].geometry.type
        let geometry

        switch (type) {
          case 'MultiPolygon':
            for (let j = 0; j < features[0].geometry.coordinates.length; j++) {
              geometry = new Polygon({
                hasZ: false,
                hasM: false,
                rings: features.map((x) => x.geometry.coordinates[j]).flat(),
                spatialReference: {
                  wkid: 4326
                }
              })

              if (geometry && geometry.type === 'polygon' && geometry.rings.length > 1) {
                const cleanedRings = geometry.rings.filter((ring, index) => {
                  return index === 0 || (ring !== undefined && ring !== null)
                })

                if (cleanedRings.length !== geometry.rings.length) {
                  geometry.rings = cleanedRings
                }
              }

              mergedFeatures.push({
                geometry,
                properties: v[0].properties
              })
            }
            break
          case 'Polygon':
            geometry = new Polygon({
              hasZ: false,
              hasM: false,
              rings: features.map((x) => x.geometry.coordinates).flat(),
              spatialReference: {
                wkid: 4326
              }
            })
            break
        }

        mergedFeatures.push({
          geometry,
          properties: v[0].properties
        })
      })
    } catch (err) {
      console.error('[Helpers/ArcGIS.js] createGraphicsIsocronas: Error creando las geometrias: ', err)
      return null
    }

    const colorFinal = [colorGradient, elapsedMins]

    // Crear los graficos
    const graphics = []
    const textGraphics = []
    for (let i = 0; i < mergedFeatures.length; i++) {
      const f = mergedFeatures[i]
      let geometry = f.geometry

      if (i > 0) {
        const prevFeature = mergedFeatures[i - 1]
        const prevGeometry = prevFeature.geometry
        geometry = GeometryEngine.difference(geometry, prevGeometry)

        if (!geometry) {
          continue
        }
      }

      let color
      for (let h = 0; h < colorFinal[1].length; h++) {
        if (f.properties.Elapsed_Mi === colorFinal[1][h]) {
          color = colorFinal[0][h]
          break
        }
      }

      if (!geometry || !geometry.rings || geometry.rings.length === 0) {
        continue
      }

      const graphic = new Graphic({
        geometry,
        attributes: {
          OBJECTID: globalGraphicObjectId++,
          COLOR: `${color[0]},${color[1]},${color[2]}`,
          ...f.properties,
          simId: id
        } // attributes
      })

      const punto = new Point({
        x: geometry.rings[0][0][0],
        y: geometry.rings[0][0][1]
      })

      const graphicText = new Graphic({
        geometry: punto,
        attributes: {
          ...f.properties,
          simId: id
        }, // attributes
        symbol: {
          type: 'text', // autocasts as new TextSymbol()
          color: 'black',
          haloColor: 'white',
          haloSize: '1px',
          text: '',
          xoffset: 0,
          yoffset: 0,
          font: {
            // autocasts as new Font()
            size: 8,
            family: 'Arial Unicode MS',
            weight: 'bold'
          }
        }
      })

      if (f.properties.Elapsed_Mi !== elapsedMinText) {
        elapsedMinText = f.properties.Elapsed_Mi
        graphicText.symbol.text = f.properties.Elapsed_Mi
      }

      graphics.push(graphic)
      textGraphics.push(graphicText)
    }

    deferred.resolve({
      polygons: graphics,
      labels: textGraphics
    })
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

export const updateSimulacionTime = async(elapsedMin) => {
  if (!map) return
  elapsedMi = elapsedMin

  const layerSimulacion = map.findLayerById('layer-simulacion')
  const layerIsocronas = map.findLayerById('layer-isocronas')
  const layerIsocronasLabels = map.findLayerById('layer-isocronas-labels')

  const simsActualizadasIds = []

  for (const simulacion of [...simulationGraphics.values()]) {
    const simType = simulacion.sim.simulacion.TIPO
    const simId = simulacion.sim.id

    if (simType === 'INCENDIO') {
      if (simulacion.sim.modo !== 'simulacion') continue

      simsActualizadasIds.push(simId)

      if (simulacion.sim.simulacion.VIENTOS) {
        await updateWindLayer(simulacion, elapsedMin)
      }
    } else if (simType === 'VIENTO') {
      await updateWindLayer(simulacion, elapsedMin)
    }
  }

  const expression = buildSimulationExpression(elapsedMin)

  if (layerSimulacion) {
    updateSimulationRenderer(layerSimulacion, getAllVisibleSimulationGraphics())
    layerSimulacion.definitionExpression = expression
    layerSimulacion.visible = true
  }

  if (layerIsocronas) {
    layerIsocronas.definitionExpression = expression
  }

  if (layerIsocronasLabels) {
    layerIsocronasLabels.removeAll()

    for (const simulacion of [...simulationGraphics.values()]) {
      const simType = simulacion.sim.simulacion.TIPO

      if (simType !== 'INCENDIO') continue
      if (simulacion.sim.modo !== 'simulacion') continue

      const labels = simulacion.sim.isocronasLabels.filter(
        x => x.attributes.Elapsed_Mi <= elapsedMin
      )

      layerIsocronasLabels.addMany(labels)
    }

    layerIsocronasLabels.visible = true
  }

  calcularSuperficie(elapsedMin)
}

export function calcularSuperficie(elapsedMin) {
  loadModules(['esri/geometry/geometryEngine']).then(([geometryEngine]) => {
    const simulation = simulationGraphics.get(simulacionActivaId)

    if (!simulation || !simulation.sim.isocronas) return

    const isocronasFiltradas = simulation.sim.isocronas.filter(
      x => x.attributes.Elapsed_Mi <= elapsedMin
    )

    let superficie = 0
    let elapsedMinPrev = 0
    const unionGeometrias = []

    for (const iso of isocronasFiltradas) {
      const min = iso.attributes.Elapsed_Mi

      if (min !== elapsedMinPrev && iso.geometry && iso.geometry.spatialReference) {
        unionGeometrias.push(iso.geometry)
        elapsedMinPrev = min
      }
    }

    const geoms = unionGeometrias.filter(g => g && g.spatialReference)

    if (geoms.length === 0) {
      VueInst.$eventHub.$emit('superficieSimulacion', 0)
      return
    }

    const geometriaUnida = geoms.length === 1 ? geoms[0] : geometryEngine.union(geoms)
    const area = Math.abs(geometryEngine.geodesicArea(geometriaUnida, 'square-meters'))
    superficie = area / 10000
    VueInst.$eventHub.$emit('superficieSimulacion', Math.round(superficie * 100) / 100)
  })
}

export const centerSimulacion = async(tipo, activeId) => {
  if (map) {
    let target
    let targetCoords
    let targetZoom

    try {
      const simulation = simulationGraphics.get(activeId)
      if (!simulation) return

      if (tipo === 'INCENDIO' && simulation.sim.coordenadas) {
        target = simulation.sim.coordenadas
        targetCoords = target
      } else {
        const centro = await obtenerCentro(simulation, 'VIENTOS', 4326)
        targetCoords = [centro.x, centro.y]
        targetZoom = 14
        target = { target: targetCoords, zoom: targetZoom }
      }

      if (isAlreadyCentered(targetCoords, targetZoom)) {
        return
      }

      await mapView.goTo(target, {
        duration: 2500,
        easing: 'out-expo'
      })
    } catch (error) {
      console.error('[Helpers/ArcGIS.js] centerSimulacion: Error al centrar la simulacion: ', error)
    }
  }
}

const isAlreadyCentered = (targetPos, targetZoom) => {
  const { center, zoom } = mapView

  const xLatLon = targetPos.length > 2 ? targetPos[0].geometry.centroid.x : targetPos.x
  const yLatLon = targetPos.length > 2 ? targetPos[0].geometry.centroid.y : targetPos.y

  const [targetX, targetY] = convertir4326to3857(xLatLon, yLatLon)

  const tolerance = 300 // Revisar valor

  const sameCoords = Math.abs(center.x - targetX) < tolerance && Math.abs(center.y - targetY) < tolerance
  const sameZoom = targetZoom ? Math.round(zoom) === Math.round(targetZoom) : true

  return sameCoords && sameZoom
}

export const clearSimulacion = async() => {
  if (!map) {
    return
  }

  try {
    const layerSimulacion = map.findLayerById('layer-simulacion')
    const layerIsocronas = map.findLayerById('layer-isocronas')
    const layerIsocronasLabels = map.findLayerById('layer-isocronas-labels')
    const layerGraficosSimulacion = map.findLayerById('layer-graficos-simulacion')

    // Simulación
    if (layerSimulacion) {
      await deleteFeaturesBySimId(layerSimulacion, null)
      layerSimulacion.definitionExpression = '1=0'
    }

    // Isócronas
    if (layerIsocronas) {
      await deleteFeaturesBySimId(layerIsocronas, null)
      layerIsocronas.definitionExpression = '1=0'
    }

    // Labels
    if (layerIsocronasLabels) {
      layerIsocronasLabels.removeAll()
    }

    // Gráficos auxiliares
    if (layerGraficosSimulacion) {
      layerGraficosSimulacion.removeAll()
    }

    // Origen de la simulación
    const layerOrigen = map.findLayerById('layer-origen-simulacion')

    if (layerOrigen) {
      layerOrigen.removeAll()
    }

    // Vientos
    const vientoLayers = map.layers.filter(l => l.id.startsWith('layer-viento-') || l.id.startsWith('layer-viento-lineas-'))

    vientoLayers.forEach(l => { map.remove(l) })
  } catch (error) {
    console.error('Error en clearSimulacion: ', error)
  } finally {
    simulationGraphics = new Map()
    elapsedMi = 0
  }
}

export const exportSimulacionBackOld = async function(simulacion, responseType, fileType) {
  const deferred = Q.defer()
  const fecha = new Date().toLocaleDateString().replaceAll('/', '-')
  const nombreSim = simulacion.NOMBRE || 'SinNombre'

  if (!map) return

  const responseSimulacion = await api.simulacion.getSimulacion(simulacion.ID_SIMULACION, 28530)
  if (responseSimulacion.data.simStatus) {
    deferred.resolve(responseSimulacion.data.simStatus)
    return deferred.promise
  }

  const geoJsonSimulacion = {
    type: 'FeatureCollection',
    features: [...responseSimulacion.data.features].reverse()
  }

  try {
    const fileResponse = await api.simulacion.exportSimulacion(simulacion.ID_SIMULACION, responseType, fileType, geoJsonSimulacion)

    switch (responseType) {
      case 'blob': {
        handleBlobDownload(fileResponse.data, fileType, nombreSim, fecha)
        break
      }
      case 'json': {
        const downloadUrl = fileResponse.data.downloadUrl
        navigator.clipboard.writeText(downloadUrl)
        return downloadUrl
      }
      default: {
        console.error('Parámetro responseType mal configurado')
      }
    }
  } catch (error) {
    console.error('Error exportando simulación:', error)
  }
}

export const exportSimulacionBack = async function(simulacion, responseType, fileType) {
  const fecha = new Date().toLocaleDateString().replaceAll('/', '-')
  const nombreSim = simulacion.NOMBRE || 'SinNombre'

  if (!map) return

  try {
    const fechas = []
    if (simulacion.TIPO === 'VIENTO') {
      for (let i = 0; i <= simulacion.NUM_HORAS; i++) {
        const horaSim = DateService.toDayjs(simulacion.FECHA_INICIO).utc().add(i, 'hours').utc().format(FMT.simViento)
        const fechaLimpia = `${horaSim.split('+')[0]}Z`
        fechas.push(fechaLimpia)
      }
    }
    const response = await api.simulacion.exportSimulacion(simulacion.ID_SIMULACION, simulacion.TIPO, JSON.stringify(fechas), responseType, fileType)

    if (response.status === 204 || response.data?.simStatus === 406 || response.data?.simStatus === 404) {
      return response.data?.simStatus || response.status
    }

    switch (responseType) {
      case 'blob': {
        handleBlobDownload(response.data, response.headers, nombreSim, fecha)
        break
      }
      case 'json': {
        const downloadUrl = response.data.downloadUrl

        if (document.hasFocus()) {
          navigator.clipboard.writeText(downloadUrl)
        } else {
          console.warn('No se puede copiar URL de simulacion. La pestaña no está en foco')
        }
        return downloadUrl
      }
      default: {
        console.error('Parámetro responseType mal configurado')
      }
    }
  } catch (error) {
    if (error.response?.status === 406 || error.response?.status === 404) return error.response?.status
    console.error('Error exportando simulación:', error)
    return 500
  }
}

function handleBlobDownload(data, headers, nombreSim, fecha) {
  const contentType = headers['content-type'] || 'application/octet-stream'
  const contentDisposition = headers['content-disposition']

  let backendFileName = 'simulacion.zip'

  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/)
    if (match?.[1]) {
      backendFileName = match[1]
    }
  }

  const finalFileName = `Simulacion_${nombreSim}_${fecha}_${backendFileName}`

  const blob = new Blob([data], { type: contentType })
  const downloadUrl = window.URL.createObjectURL(blob)

  const element = document.createElement('a')
  element.href = downloadUrl
  element.download = finalFileName

  document.body.appendChild(element)
  element.click()
  document.body.removeChild(element)

  URL.revokeObjectURL(downloadUrl)
}

export const downloadBlob = (blob, fileName = 'archivo.pdf') => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  document.body.appendChild(link)

  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// Funcion para exportar simulacion a pdf
export const paintSimulacionFinal = async function(simulacion) {
  const deferred = Q.defer()

  if (map) {
    const layer = map.findLayerById('layer-simulacion')
    try {
      const response = await api.simulacion.getSimulacion(simulacion.ID_SIMULACION, 28530)

      if (response.data.simStatus) {
        // Hacer que salga error cuando el simStatus no sea correcto
        deferred.resolve(response.data.simStatus)
        return deferred.promise
      }
      const graphics = await createGraphicsIsocronas(response.data)
      simulationGraphics.simulacion = graphics

      layer.graphics.items = simulationGraphics.simulacion
      await mapView.goTo(simulationGraphics.simulacion, {
        duration: 3000,
        easing: 'in-expo'
      })
      layer.visible = true
      deferred.resolve()
    } catch (err) {
      deferred.reject(err)
    }
  } else {
    deferred.reject()
  }

  return deferred.promise
}

/**
 * @param {*} from => [r, g, b, a]
 * @param {*} to => [r, g, b, a]
 * @param {*} steps => n
 */
function getColorGradient(from, to, totalNumberOfColors) {
  if (totalNumberOfColors === 0) {
    return [[0, 0, 0]]
  }
  if (totalNumberOfColors === 1) {
    return [from]
  }
  if (totalNumberOfColors === 2) {
    return [from, to]
  }

  const diffR = to[0] - from[0]
  const diffG = to[1] - from[1]
  const diffB = to[2] - from[2]
  // let diffA = to[3] - from[3]

  const steps = totalNumberOfColors - 1

  const stepR = diffR / steps
  const stepG = diffG / steps
  const stepB = diffB / steps
  // let stepA = diffA / steps

  const gradient = []
  gradient.push(from)
  for (let i = 1; i < steps; ++i) {
    const r = Math.round(from[0] + stepR * i)
    const g = Math.round(from[1] + stepG * i)
    const b = Math.round(from[2] + stepB * i)
    // let a = Math.round(from[3] + stepA * i)

    gradient.push([r, g, b])
  }
  gradient.push(to)

  return gradient
}
// #endregion

// #region VIENTOS
export const paintViento = async(simulacion) => {
  if (!map) return
  const uuid = simulacion.sim.simulacion.ID_SIMULACION
  try {
    let aux = null
    simulacion.flechas = []

    if (simulacion.sim.simulacion.TIPO === 'VIENTO') {
      for (let i = 0; i < simulacion.sim.simulacion.NUM_HORAS; i++) {
        const horaSim = DateService
          .toDayjs(simulacion.sim.simulacion.FECHA_INICIO)
          .utc()
          .add(i, DayjsUnit.Hour)
          .format(FMT.simViento)
        const fechaLimpia = `${horaSim.split('+')[0]}Z`

        simulacion.flechas[i] = await api.simulacion.getSimulacionWind(uuid, fechaLimpia)
      }

      aux = simulacion.flechas[0]

      if (!simulationGraphics.has(uuid)) {
        simulationGraphics.set(uuid, simulacion)
        VueInst.$eventHub.$emit('update-simulaciones-list')
      }
      const sim = simulationGraphics.get(uuid)
      sim.sim.coordenadas = {
        x: aux.data.features[0].geometry.coordinates[0],
        y: aux.data.features[0].geometry.coordinates[1]
      }
      sim.response = aux.data

      simulacionActivaId = uuid
    } else {
      for (let i = 0; i < simulacion.sim.simulacion.NUM_HORAS; i++) {
        const horaSim = DateService
          .toDayjs(simulacion.sim.simulacion.FECHA_INICIO)
          .utc()
          .add(i, DayjsUnit.Hour)
          .format(FMT.simulationWithWind)

        simulacion.flechas[i] = await api.simulacion.getJsonWindNinja(uuid, horaSim)
      }
      simulationGraphics.set(uuid, simulacion)
      aux = simulacion.flechas[0]
    }

    store.dispatch('shareMap/paintViento', aux.data)

    aux.data.simId = uuid

    if (simulacion.sim.simulacion.TIPO === 'VIENTO') {
      simulacionActivaId = uuid
      await centerSimulacion(simulacion.sim.simulacion.TIPO, uuid)
    }
    await paintVientoLayer(aux.data)
    return simulationGraphics.get(uuid).flechas
  } catch (err) {
    console.error('Arcgis error paintViento: ', err)
    throw err
  }
}

export const paintVientoLayer = async function(response) {
  const maxSpeed = Number(response.maxSpeed)
  const minSpeed = Number(response.minSpeed)

  const defaultColors = {
    minSpeed: '#0010d9',
    medSpeed: '#0080ff',
    maxSpeed: '#00ffff'
  }

  const is3D = store.getters['map/isMap3D']
  const mode = store.getters['map/wind3DMode']

  const pointLayerId = `layer-viento-${response.simId}`
  const lineLayerId = `layer-viento-lineas-${response.simId}`

  let pointLayer = map.findLayerById(pointLayerId)
  let lineLayer = map.findLayerById(lineLayerId)

  const graphicsVientos = featuresVientoLines(response.features, response.simId)
  const graphics = featuresVientos(response.features, response.simId)

  const fields = [
    { name: 'ObjectID', alias: 'ObjectID', type: 'oid' },
    { name: 'direction', alias: 'Direction', type: 'double' },
    { name: 'speed', alias: 'Speed', type: 'double' },
    { name: 'simId', alias: 'Simulación ID', type: 'string' }
  ]

  if (!pointLayer) {
    pointLayer = new FeatureLayer({
      id: pointLayerId,
      title: `Viento Puntos ${response.simId}`,
      source: graphics,
      objectIdField: 'ObjectID',
      fields,
      geometryType: 'point',
      spatialReference: { wkid: 3857 },
      renderer: is3D ? getWindRenderer3D(minSpeed, maxSpeed, defaultColors) : getWindRenderer2D(minSpeed, maxSpeed, defaultColors),
      elevationInfo: {
        mode: 'relative-to-ground',
        offset: 50,
        unit: 'meters'
      },
      popupTemplate: {
        title: 'Viento',
        content: `
          Dirección: {direction}°<br>
          Velocidad: {speed} km/h
        `
      }
    })

    pointLayer.graphicsVientosCache = new Map()

    graphics.forEach(g => {
      pointLayer.graphicsVientosCache.set(g.attributes.ObjectID, g)
    })

    map.add(pointLayer)
    await pointLayer.load()
    await store.dispatch('map/addLayer', { id: pointLayerId, position: 33 })
  }

  if (!lineLayer) {
    lineLayer = new FeatureLayer({
      id: lineLayerId,
      title: `Viento Líneas ${response.simId}`,
      source: graphicsVientos,
      objectIdField: 'ObjectID',
      fields,
      geometryType: 'polyline',
      spatialReference: { wkid: 3857 },
      renderer: getWindLineRenderer3D(minSpeed, maxSpeed, defaultColors),
      popupTemplate: {
        title: 'Viento',
        content: `
          Dirección: {direction}°<br>
          Velocidad: {speed} km/h
        `
      }
    })

    lineLayer.graphicsVientosCache = new Map()

    graphicsVientos.forEach(g => {
      lineLayer.graphicsVientosCache.set(g.attributes.ObjectID, g)
    })

    map.add(lineLayer)
    await lineLayer.load()
    await store.dispatch('map/addLayer', { id: lineLayerId, position: 33 })

    if (!is3D) {
      pointLayer.visible = true
      lineLayer.visible = false
    } else {
      if (mode === 'objetos') {
        pointLayer.visible = true
        lineLayer.visible = false
      } else {
        pointLayer.visible = false
        lineLayer.visible = true
      }
    }
  }
}

const windUpdateTokens = new Map()
export const updateWindLayer = async(simulacion, elapsedMin) => {
  const simData = simulacion.sim
  const uuid = simData.simulacion.ID_SIMULACION
  const flechas = simulacion.flechas
  const pos = Math.min(Math.floor(elapsedMin / 60), (flechas?.length ?? 0) - 1)

  if (elapsedMin <= simData.simulacion.NUM_HORAS * 60) {
    if (pos < 0 || !flechas[pos]) {
      return
    }

    const aux = flechas[pos]
    const response = aux.data

    const token = Symbol('wind-update')
    windUpdateTokens.set(uuid, token)

    const pointLayer = map.findLayerById(`layer-viento-${uuid}`)
    const lineLayer = map.findLayerById(`layer-viento-lineas-${uuid}`)

    const layerState = store.getters['map/getLayerById'](`layer-viento-${uuid}`)
    const lineState = store.getters['map/getLayerById'](`layer-viento-lineas-${uuid}`)

    const vientosActivados = layerState.visible
    const lineasActivadas = lineState.visible

    const graphics = featuresVientos(response.features, uuid)
    const graphicsVientos = featuresVientoLines(response.features, uuid)

    if (windUpdateTokens.get(uuid) !== token) return

    if (windUpdateTokens.get(uuid) !== token) {
      return
    }

    await syncLayerGraphics(pointLayer, graphics)
    await syncLayerGraphics(lineLayer, graphicsVientos)

    const is3D = store.getters['map/isMap3D']
    const mode = store.getters['map/wind3DMode']

    if (!is3D) {
      pointLayer.visible = vientosActivados
      lineLayer.visible = false
    } else if (mode === 'objetos') {
      pointLayer.visible = vientosActivados
      lineLayer.visible = false
    } else {
      pointLayer.visible = false
      lineLayer.visible = lineasActivadas
    }
  }
}

async function syncLayerGraphics(layer, newGraphics) {
  if (!layer.graphicsVientosCache) {
    layer.graphicsVientosCache = new Map()
  }

  const cache = layer.graphicsVientosCache

  const incomingIds = new Set()

  const adds = []
  const updates = []
  const deletes = []

  newGraphics.forEach(graphic => {
    const id = graphic.attributes.ObjectID

    incomingIds.add(id)

    const existingGraphic = cache.get(id)

    if (existingGraphic) {
      updates.push(graphic)
      cache.set(id, graphic)
    } else {
      adds.push(graphic)
      cache.set(id, graphic)
    }
  })

  cache.forEach((graphic, id) => {
    if (!incomingIds.has(id)) {
      deletes.push(graphic)

      cache.delete(id)
    }
  })

  if (adds.length === 0 && updates.length === 0 && deletes.length === 0) {
    return
  }

  await layer.applyEdits({
    addFeatures: adds,
    updateFeatures: updates,
    deleteFeatures: deletes
  })
}

function featuresVientos(features, simId) {
  const graphics = []

  features.forEach((element, i) => {
    const symbol = {
      type: 'simple-marker',
      style: 'triangle',
      outline: {
        color: 'yellow',
        width: '3px'
      }
    }
    const geometry = new Point({
      x: element.geometry.coordinates[0],
      y: element.geometry.coordinates[1]
    })

    const graphic = new Graphic({
      attributes: {
        ObjectID: i + 1,
        direction: element.properties.direction,
        speed: element.properties.speed,
        simId
      },
      geometry,
      symbol
    })

    graphics.push(graphic)
  })

  return graphics
}

function featuresVientoLines(features, simId) {
  const graphics = []

  features.forEach((element, i) => {
    const coords = element.geometry.coordinates
    const direction = element.properties.direction

    const arrowGeometry = buildArrowPolyline(coords, direction)

    const geometry = new Polyline({
      paths: arrowGeometry.paths,
      spatialReference: { wkid: 4326 }
    })

    const graphic = new Graphic({
      attributes: {
        ObjectID: i + 1,
        direction: element.properties.direction,
        speed: element.properties.speed,
        simId
      },
      geometry
    })

    graphics.push(graphic)
  })

  return graphics
}

function buildArrowPolyline(coords, direction, length = 0.0012) {
  const [lon, lat] = coords

  const drawDirection = (direction + 180) % 360
  const rad = (90 - drawDirection) * Math.PI / 180

  const dx = Math.cos(rad) * length
  const dy = Math.sin(rad) * length

  const headLon = lon + dx
  const headLat = lat + dy

  const sideLen = length * 0.35
  const leftRad = rad + Math.PI / 6
  const rightRad = rad - Math.PI / 6

  const leftLon = headLon - Math.cos(leftRad) * sideLen
  const leftLat = headLat - Math.sin(leftRad) * sideLen

  const rightLon = headLon - Math.cos(rightRad) * sideLen
  const rightLat = headLat - Math.sin(rightRad) * sideLen

  return {
    paths: [
      [[lon, lat], [headLon, headLat]],
      [[headLon, headLat], [leftLon, leftLat]],
      [[leftLon, leftLat], [headLon, headLat]],
      [[headLon, headLat], [rightLon, rightLat]]
    ]
  }
}

const windRendererCache = new Map()
function getCachedWindRenderer(key, factory) {
  if (!windRendererCache.has(key)) {
    windRendererCache.set(key, factory())
  }

  return windRendererCache.get(key)
}

export function updateRendererViento(colors = null) {
  const is3D = store.getters['map/isMap3D']
  const mode = store.getters['map/wind3DMode']

  const defaultColors = {
    minSpeed: '#0010d9',
    medSpeed: '#0080ff',
    maxSpeed: '#00ffff'
  }

  const activeColors = colors || defaultColors
  const colorKey = buildWindColorKey(activeColors)

  simulationGraphics.forEach((simulacion) => {
    let maxSpeed
    let minSpeed

    if (simulacion.flechas) {
      maxSpeed = simulacion.flechas[0].data.maxSpeed
      minSpeed = simulacion.flechas[0].data.minSpeed
    }

    const uuid = simulacion.sim.simulacion.ID_SIMULACION
    const pointLayer = map.findLayerById(`layer-viento-${uuid}`)
    const lineLayer = map.findLayerById(`layer-viento-lineas-${uuid}`)

    if (!pointLayer || !lineLayer) {
      return
    }

    if (!is3D) {
      pointLayer.visible = true
      lineLayer.visible = false

      const renderer = getCachedWindRenderer(`2d_${minSpeed}_${maxSpeed}_${colorKey}`, () =>
        getWindRenderer2D(minSpeed, maxSpeed, activeColors)
      )

      if (pointLayer.renderer !== renderer) {
        pointLayer.renderer = renderer
      }

      return
    }

    if (mode === 'objetos') {
      pointLayer.visible = true
      lineLayer.visible = false

      const renderer = getCachedWindRenderer(`3d_obj_${minSpeed}_${maxSpeed}_${colorKey}`, () =>
        getWindRenderer3D(minSpeed, maxSpeed, activeColors)
      )

      if (pointLayer.renderer !== renderer) {
        pointLayer.renderer = renderer
      }
    } else {
      pointLayer.visible = false
      lineLayer.visible = true

      const renderer = getCachedWindRenderer(`3d_line_${minSpeed}_${maxSpeed}_${colorKey}`, () =>
        getWindLineRenderer3D(minSpeed, maxSpeed, activeColors)
      )

      if (lineLayer.renderer !== renderer) {
        lineLayer.renderer = renderer
      }
    }
  })
}

export function updateVientosVisibility(value) {
  const is3D = store.getters['map/isMap3D']
  const mode = store.getters['map/wind3DMode']

  simulationGraphics.forEach(simulacion => {
    const uuid = simulacion.sim.simulacion.ID_SIMULACION
    const pointLayer = map.findLayerById(`layer-viento-${uuid}`)
    const lineLayer = map.findLayerById(`layer-viento-lineas-${uuid}`)

    if (!pointLayer || !lineLayer) {
      return
    }

    if (!value) {
      pointLayer.visible = value
      lineLayer.visible = value

      return
    }

    if (is3D && mode === 'lineas') {
      pointLayer.visible = !value
      lineLayer.visible = value
    } else {
      pointLayer.visible = value
      lineLayer.visible = !value
    }
  })
}
// #endregion VIENTOS

// #region IMAGEN SATELITE

export const drawImagenSatelite = async function(data, url, bbox) {
  if (map) {
    const layer = map.findLayerById('layer-imagen-satelite')

    const graphic = await createGraphicsImagenSateliteWMS(data, url, bbox)

    layer.source.elements.removeAll() // Para que solo haya una imagen cada vez
    await layer.source.elements.add(graphic)

    layer.visible = true
  }
}

// Funcion para introducir valores en la capa de imagen satelite para la implementación WFSWMS
async function createGraphicsImagenSateliteWMS(url, bbox) {
  try {
    const [ImageElement, ExtentAndRotationGeoreference, Extent] =
      await loadModules([
        'esri/layers/support/ImageElement',
        'esri/layers/support/ExtentAndRotationGeoreference',
        'esri/geometry/Extent'
      ])

    const image = new Image()
    image.crossOrigin = 'Anonymous'
    image.src = url

    await new Promise((resolve, reject) => {
      image.onload = resolve
      image.onerror = reject
    })

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = image.width
    canvas.height = image.height
    ctx.drawImage(image, 0, 0)

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]

      if (r < 50 && g < 50 && b > 150) {
        data[i + 3] = 0
      }
    }

    ctx.putImageData(imageData, 0, 0)

    const source = new ImageElement({
      image: canvas,
      georeference: new ExtentAndRotationGeoreference({
        extent: new Extent({
          spatialReference: {
            wkid: 3857
          },
          xmin: bbox.xmin,
          ymin: bbox.ymin,
          xmax: bbox.xmax,
          ymax: bbox.ymax
        })
      })
    })

    return source
  } catch (error) {
    console.error('Error al crear la capa de imagen satelital: ', error)
  }
}

// Funcion para introducir valores en la capa de imagen satelite
async function createGraphicsImagenSatelite(data, url) {
  const deferred = Q.defer()

  try {
    const [ImageElement, ExtentAndRotationGeoreference, Extent] =
      await loadModules([
        'esri/layers/support/ImageElement',
        'esri/layers/support/ExtentAndRotationGeoreference',
        'esri/geometry/Extent'
      ])

    let source = null

    const aux = convertir4326to3857(data.LONGITUD, data.LATITUD)

    source = new ImageElement({
      image: url,
      // image: 'http://localhost:3000/satelite_imagen/B43D98CF-14A6-471E-9F00-6B225D435B3B/9751129_B43D98CF-14A6-471E-9F00-6B225D435B3B_14-7-2022_RGB',
      georeference: new ExtentAndRotationGeoreference({
        extent: new Extent({
          spatialReference: {
            wkid: 3857 // 4326
          },
          xmin: aux[0] - 1113.1949134293125 * 1.369 * 7, // 1113.1949134293125 * 1.35
          ymin: aux[1] - 1113.1949134293125 * 1.369 * 7, // El 5 viene del offset de la imagen(0.05) * 100
          xmax: aux[0] + 1113.1949134293125 * 1.369 * 7,
          ymax: aux[1] + 1113.1949134293125 * 1.369 * 7
        })
      })
    })

    deferred.resolve(source)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

// export const drawGeoJsonImagenSatelite = async function (geoJson) {
//   if (map) {
//     let layer = map.findLayerById('layer-vector-imagen-satelite')
//     layer.destroy()

//     let graphic = await createGraphicsImagenSateliteGeoJson(geoJson)

//     layer.url = graphic

//     createGeoJsonLayer(layer).then((layer) => {
//       map.add(layer)
//       store.dispatch('map/addLayer', {
//         id: layer.id,
//         title: layer.title,
//         visible: layer.visible,
//         opacity: layer.opacity,
//         posicion: layer.posicion,
//         addListLayer: layer.addListLayer,
//         url: layer.url
//       })
//     })
//   }
// }

// Funcion para introducir valores en la capa de imagen satelite
async function createGraphicsImagenSateliteGeoJson(geoJson) {
  const deferred = Q.defer()

  try {
    const blob = new Blob([JSON.stringify(geoJson)], {
      // layerItem.url | geojson
      type: 'application/json'
    })

    // URL reference to the blob
    const url = URL.createObjectURL(blob)

    deferred.resolve(url)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

export const hideImagenSatelite = () => {
  if (map) {
    const layer = map.findLayerById('layer-imagen-satelite')
    layer.source.elements.removeAll()
    layer.visible = false
  }
}

export const hideImagenSateliteGeoJSON = () => {
  if (map) {
    const layer = map.findLayerById('layer-vector-imagen-satelite')
    // layer.url.elements.removeAll()

    layer.destroy()

    layer.url = []

    createGeoJsonLayer(layer).then((layer) => {
      map.add(layer)
      store.dispatch('map/addLayer', {
        id: layer.id,
        title: layer.title,
        visible: layer.visible,
        opacity: layer.opacity,
        posicion: layer.posicion,
        addListLayer: layer.addListLayer,
        url: layer.url
      })
    })
  }
}

export const generarBbox3857 = function(latitud, longitud) {
  const coordenadas = [longitud, latitud]

  const proyeccion = proj4(proj4.defs('EPSG:4326'), proj4.defs('EPSG:3857'), coordenadas)

  const offset = 10000 // Un buffer de 1000 metros alrededor del punto central
  const bbox = {
    xmin: proyeccion[0] - offset,
    ymin: proyeccion[1] - offset,
    xmax: proyeccion[0] + offset,
    ymax: proyeccion[1] + offset
  }

  return {
    xmin: bbox.xmin,
    ymin: bbox.ymin,
    xmax: bbox.xmax,
    ymax: bbox.ymax
  }
}
// #endregion

// #region CONVERT_COORDINATES
export const convertirGMSLatLon = (gmsLat, gmsLon) => {
  const partsLat = gmsLat.split(/[^\d\w]+/)
  const partsLon = gmsLon.split(/[^\d\w]+/)
  const lat = convertirDMSToDD(
    Number(partsLat[0]),
    Number(partsLat[1]),
    Number(`${partsLat[2]}.${partsLat[3]}`),
    partsLat[4]
  )
  const lng = convertirDMSToDD(
    Number(partsLon[0]),
    Number(partsLon[1]),
    Number(`${partsLon[2]}.${partsLon[3]}`),
    partsLon[4]
  )
  return [lat, lng]
}
function convertirDMSToDD(degrees, minutes, seconds, direction) {
  let dd = degrees + minutes / 60 + seconds / (60 * 60)

  if (
    direction === 'S' ||
    direction === 's' ||
    direction === 'W' ||
    direction === 'w'
  ) {
    dd = dd * -1
  } // Don't do anything for N or E
  return dd
}
export const convertirLatLonGMS = (valor, tipo) => {
  let grados = Math.abs(parseInt(valor))
  let minutos = (Math.abs(valor) - grados) * 60
  let segundos = minutos
  minutos = Math.abs(parseInt(minutos))
  segundos = Math.round((segundos - minutos) * 60 * 1000000) / 1000000
  const signo = valor < 0 ? -1 : 1
  const direccion =
    tipo === 'LATITUDE' ? (signo > 0 ? 'N' : 'S') : signo > 0 ? 'E' : 'W'

  if (isNaN(direccion)) {
    grados = grados * signo
  }

  return {
    grados,
    minutos,
    segundos,
    direccion,
    valor:
      `${grados
      }º ${minutos.toString().padStart(2, '0')
      }' ${segundos.toFixed(2).toString().padStart(5, '0').replace('.', ',')
      }" ${isNaN(direccion) ? ` ${direccion}` : ''}`
  }
}

export const convertirETRS89 = (x, y) => {
  const zone = constants.HUSO_UTM
  const firstProjection = 'EPSG:3857'
  const secondProjection =
    `+proj=utm +zone=${zone} +ellps=GRS80 +units=m +no_defs `
  return proj4(firstProjection, secondProjection, [
    parseFloat(x),
    parseFloat(y)
  ])
}
export const convertirLatLonToETRS89 = (lat, lon) => {
  const zone = constants.HUSO_UTM
  const firstProjection = 'EPSG:4326'
  const secondProjection =
    `+proj=utm +zone=${zone} +ellps=GRS80 +units=m +no_defs `
  return proj4(firstProjection, secondProjection, [
    parseFloat(lon),
    parseFloat(lat)
  ])
}
export const convertirETRS89ToLatLon = (x, y) => {
  const zone = constants.HUSO_UTM
  const firstProjection =
    `+proj=utm +zone=${zone} +ellps=GRS80 +units=m +no_defs`
  const secondProjection = 'EPSG:4326'
  return proj4(firstProjection, secondProjection, [
    parseFloat(x),
    parseFloat(y)
  ])
}

export const convertirMercXYToLatLon = (x, y) => {
  const zone = constants.HUSO_UTM
  const firstProjection =
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +units=m +k=1.0 +nadgrids=@null +no_defs'
  const secondProjection = 'EPSG:4326'
  return proj4(firstProjection, secondProjection, [
    parseFloat(x),
    parseFloat(y)
  ])
}

export const convertirMercXYToETRS89UTM30N = (x, y) => {
  const firstProjection =
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 ' +
    '+x_0=0.0 +y_0=0 +units=m +k=1.0 +nadgrids=@null +no_defs'

  const secondProjection =
    '+proj=utm +zone=30 +ellps=GRS80 +units=m +no_defs'

  return proj4(firstProjection, secondProjection, [
    parseFloat(x),
    parseFloat(y)
  ])
}

function convertir4326to3857(lon, lat) {
  const x = (lon * 20037508.34) / 180
  let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180)
  y = (y * 20037508.34) / 180
  return [x, y]
}

export const convertir4326to3857Mercator = async(lon, lat) => {
  const [webMercatorUtils] = await loadModules([
    'esri/geometry/support/webMercatorUtils'
  ])

  const point4326 = new Point({
    longitude: lon,
    latitude: lat,
    spatialReference: { wkid: 4326 }
  })

  const point3857 = await webMercatorUtils.geographicToWebMercator(point4326)
  return [point3857.toJSON().x, point3857.toJSON().y]
}

export const convertirETRS89UTM30NToMercXY = (x, y) => {
  const firstProjection =
    '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs'

  const secondProjection =
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs'

  return proj4(firstProjection, secondProjection, [
    parseFloat(x),
    parseFloat(y)
  ])
}

export const transformarGeoJsonAEPSG4326 = geoJson => {
  return {
    ...geoJson,
    features: geoJson.features.map(feature => {
      if (feature.geometry.type === 'Polygon') {
        const nuevasCoordenadas = feature.geometry.coordinates.map(ring =>
          ring.map(([x, y]) => convertirMercXYToLatLon(x, y))
        )

        return {
          ...feature,
          geometry: {
            ...feature.geometry,
            coordinates: nuevasCoordenadas
          }
        }
      }

      return feature
    })
  }
}

// #endregion

// #region TOKEN
function getToken() {
  return new Promise((resolve, reject) => {
    const tokenData = constants.ArcGISToken

    const params = new FormData()
    params.append('client_id', tokenData.client_id)
    params.append('client_secret', tokenData.client_secret)
    params.append('grant_type', tokenData.grant_type)

    /* let params = {
      client_id: tokenData.client_id,
      client_secret: tokenData.client_secret,
      grant_type: tokenData.grant_type
    } */

    VueInst.$http
      .post(tokenData.urlToken, params)
      .then((response) => {
        const accessToken = response.data.access_token
        resolve(accessToken)
      })
      .catch((error) => {
        reject(error)
      })
  })
}
async function registerToken() {
  const [IdentityManager] = await loadModules([
    'esri/identity/IdentityManager'
  ])
  const urlServices = constants.ArcGISToken.serverLayer
  if (IdentityManager.findCredential(urlServices) === undefined) {
    const accessToken = await getToken()
    IdentityManager.registerToken({
      server: urlServices,
      token: accessToken
    })
  }
}
// #endregion

// #region MAP MOVEMENT
export const setBaseMap = async(basemap) => {
  try {
    const [Basemap, WMTSLayer, WebTileLayer] = await loadModules([
      'esri/Basemap',
      'esri/layers/WMTSLayer',
      'esri/layers/WebTileLayer'
    ])

    setVisibilityLayer('hybrid-reference-layer', basemap.id === 'base4')

    let baseLayer
    let tileBasemap

    if (basemap.type === 'esri') {
      map.basemap = basemap.map
    } else if (basemap.type === 'webtile') {
      baseLayer = new WebTileLayer({
        urlTemplate: basemap.urlTemplate,
        id: basemap.id,
        title: basemap.label
      })
      tileBasemap = new Basemap({
        baseLayers: [baseLayer],
        id: basemap.id,
        title: basemap.label
      })
      map.basemap = tileBasemap
    }
    // if (basemap.id === 'base7') {
    //   // IDERioja
    //   let baseLayer = new WebTileLayer({
    //     urlTemplate: basemap.map
    //   })
    //   let tileBasemap = new Basemap({
    //     baseLayers: [baseLayer],
    //     id: basemap.id,
    //     title: basemap.label
    //   })
    //   map.basemap = tileBasemap

    // } else if (basemap.id === 'base8') {
    //   // Mapa base OneAtlas
    //   let baseLayer = new WMTSLayer({
    //     url: basemap.map,
    //     copyright:
    //       '<a target=_top href="http://www.intelligence-airbusds.com"> Airbus DS, Inc</a>'
    //   })
    //   let tileBasemap = new Basemap({
    //     baseLayers: [baseLayer],
    //     id: basemap.id,
    //     title: basemap.label
    //   })
    //   map.basemap = tileBasemap
  } catch (err) {
    VueInst.$log.error(err)
  }
}
// Metodo antiguo: lo añade a cada mapview/sceneview segun se cambia
/* export const setSearchWidget = async () => {
  try {
    const [Search, Locator, Extent] = await loadModules(['esri/widgets/Search', 'esri/tasks/Locator', 'esri/geometry/Extent'])

    // Filtrar locator por extent de zona
    let paramsComunidad = store.getters['map/paramsComunidad']
    let extent = paramsComunidad ? new Extent(paramsComunidad.EXTENT) : null
    let searchFilter = {
      geometry: extent
    }

    let searchWidget = new Search({
      sources: [{
        locator: new Locator({ url: 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer' }),
        countryCode: 'ES',
        singleLineFieldName: 'SingleLine',
        name: 'Geocoding España',
        localSearchOptions: {
          minScale: 300000,
          distance: 50000
        },
        filter: searchFilter,
        placeholder: 'Buscar',
        maxResults: 3,
        maxSuggestions: 6,
        suggestionsEnabled: true,
        minSuggestCharacters: 0,
        enableInfoWindow: false,
        popupEnabled: false
      }],
      view: mapView,
      includeDefaultSources: false,
      id: 'search'
    })

    // Add the search widget to the top right corner of the view if not exist
    if (!mapView.ui.find('search')) {
      mapView.ui.add(searchWidget, {
        position: 'top-right'
      })
    }
  } catch (err) {
    VueInst.$log.error(err)
  }
} */

// Metodo nuevo: crea uno al principio en la toolbar del mapa
let searchWidget = null
export const setSearchWidget = async() => {
  const deferred = Q.defer()

  try {
    const [Search, Locator, Extent] =
      await loadModules([
        'esri/widgets/Search',
        'esri/rest/locator',
        'esri/geometry/Extent'
      ])

    const paramsComunidad = store.getters['map/paramsComunidad']
    const extent = paramsComunidad ? new Extent(paramsComunidad.EXTENT) : null
    const searchFilter = {
      geometry: extent
    }

    if (!searchWidget) {
      searchWidget = new Search({
        sources: [
          {
            url: 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer',
            countryCode: 'ES',
            singleLineFieldName: 'SingleLine',
            name: 'Geocoding España',
            localSearchOptions: {
              minScale: 300000,
              distance: 50000
            },
            filter: searchFilter,
            placeholder: 'Buscar topónimos',
            maxResults: 3,
            maxSuggestions: 6,
            suggestionsEnabled: true,
            minSuggestCharacters: 0,
            enableInfoWindow: false,
            popupEnabled: false
          }
        ],
        view: mapView,
        container: 'searchWidget',
        includeDefaultSources: false,
        id: 'search'
      })
    } else {
      searchWidget.view = mapView
    }
    deferred.resolve()
  } catch (err) {
    VueInst.$log.error(err)
    deferred.reject(err)
  }

  return deferred.promise
}

export const setZoom = (value) => {
  const newZoom = mapView.zoom + value
  mapView.goTo({
    target: mapView.center,
    zoom: newZoom
  })
}
export const setExtent = (extent) => {
  setExtentMap(extent)
}

async function setExtentMap(extent) {
  try {
    const [Extent, SpatialReference] = await loadModules([
      'esri/geometry/Extent',
      'esri/geometry/SpatialReference'
    ])

    const properties = {
      xmin: extent.xmin,
      ymin: extent.ymin,
      xmax: extent.xmax,
      ymax: extent.ymax
    }

    if (extent.wkid) {
      properties.spatialReference = new SpatialReference({ wkid: extent.wkid })
    } else {
      properties.spatialReference = extent.spatialReference
    }

    if (extent.viewpoint?.targetGeometry) {
      const viewpoint = JSON.parse(JSON.stringify(extent.viewpoint))
      viewpoint.targetGeometry.type = 'point'
      mapView.viewpoint = viewpoint
    }

    mapView.extent = new Extent(properties)
  } catch (err) {
    VueInst.$log.error(err)
  }
}

export const setCenterMap = (data) => {
  if (mapView) {
    mapView.goTo({
      target: data.target,
      zoom: data.zoom
    })
  }
}
/*
export const centerCompass = () => {
  if (mapView.rotation !== undefined) {
    mapView.rotation = 0
  } else {
    mapView.goTo({
      heading: 0
    })
  }
}
*/
// #endregion

// #region CAPTURE MAP
export const captureMap = () => {
  const deferred = Q.defer()

  const pixelRatio = 3
  mapView
    .takeScreenshot({
      width: mapView.width * pixelRatio,
      height: mapView.height * pixelRatio,
      format: 'jpg'
    })
    .then((screenshot) => {
      deferred.resolve(screenshot.dataUrl)
    })

  return deferred.promise
}
export const setCenterAndCaptureMap = async(data) => {
  const deferred = Q.defer()

  if (mapView) {
    await mapView.goTo({
      target: data.target,
      zoom: data.zoom
    })
    setTimeout(async() => {
      const image = await captureMap(mapView)
      deferred.resolve(image)
    }, 500)
  }

  return deferred.promise
}
export const captureOverviewMap = async() => {
  const pixelRatio = 3

  const screenshot = await mapViewOverview
    .takeScreenshot({
      width: mapViewOverview.width * pixelRatio,
      height: mapViewOverview.height * pixelRatio,
      // area: store.getters['map/paramsComunidad'].RINGS,
      format: 'jpg'
    })

  return screenshot.dataUrl
}
export const getCaptureMapFeatures = async(graphics, layer) => {
  try {
    const deferred = Q.defer()

    const layerID = map.findLayerById(layer)
    const array = []

    if (layerID.visible === true && layerID.type === 'feature') {
      graphics.forEach(async(graphic) => {
        const query = layerID.createQuery()
        graphic.geometry = graphic.geometry ? graphic.geometry : JSON.parse(graphic.GEOMETRIA)
        query.spatialRelationship = 'intersects'
        query.returnGeometry = true
        query.outFields = ['*']

        try {
          const result = await layerID.queryFeatures(query)

          let coordMin = convertirETRS89(graphic.geometry.xmin, graphic.geometry.ymin)
          let coordMax = convertirETRS89(graphic.geometry.xmax, graphic.geometry.ymax)
          coordMin = convertirETRS89ToLatLon(coordMin[0], coordMin[1])
          coordMax = convertirETRS89ToLatLon(coordMax[0], coordMax[1])
          const x = [coordMin[0], coordMax[0]]
          const y = [coordMin[1], coordMax[1]]

          const filteredFeatures = result.features.filter(feature => (
            feature.geometry.x >= x[0] && feature.geometry.x <= x[1] &&
            feature.geometry.y >= y[0] && feature.geometry.y <= y[1]
          ))

          const attr = filteredFeatures.map((feature) => { return feature.attributes })
          deferred.resolve(attr)
        } catch (err) {
          VueInst.$log.error(err)
        }
      })
    } else if (layerID.visible === true && layerID.type === 'graphics') {
      graphics.forEach(async(graphic) => {
        if (layerID.title === 'Gráficos Perímetros' && layerID.graphics.length > 0) {
          for (const layerGraphic of layerID.graphics) {
            const intersects = await intersectGraphic(layerGraphic, graphic)
            if (intersects) {
              const datos = {
                tipo: layerGraphic.attributes.LABEL.label,
                color: layerGraphic.attributes.LABEL.color,
                icon: layerGraphic.attributes.LABEL.src
              }
              array.push(datos)
            }
          }
        } else if (layerID.title === 'Gráficos Plan Operaciones' && layerID.graphics.length > 0) {
          for (const layerGraphic of layerID.graphics) {
            const intersects = await intersectGraphic(layerGraphic, graphic)
            if (intersects) {
              const datos = {
                plan: layerGraphic.geometry.type,
                tipo: layerGraphic.attributes.LABEL.label,
                icon: layerGraphic.attributes.LABEL.src
              }
              array.push(datos)
            }
          }
        } else {
          deferred.resolve([])
        }
        deferred.resolve(array)
      })
    } else {
      deferred.resolve([])
    }
    return deferred.promise
  } catch (err) {
    VueInst.$log.error(err)
  }
}

export const intersectGraphic = async(layerGraphic, graphic) => {
  const [Extent, geometryEngine] = await loadModules([
    'esri/geometry/Extent',
    'esri/geometry/geometryEngine'
  ])

  let geometry
  const extent = new Extent({
    xmin: graphic.geometry.xmin,
    ymin: graphic.geometry.ymin,
    xmax: graphic.geometry.xmax,
    ymax: graphic.geometry.ymax,
    spatialReference: { wkid: 102100 }
  })
  if (layerGraphic.geometry.type === 'polygon') {
    geometry = new Polygon({
      rings: layerGraphic.geometry.rings,
      spatialReference: { wkid: 102100 }
    })
  } else if (layerGraphic.geometry.type === 'point') {
    geometry = new Point({
      x: layerGraphic.geometry.x,
      y: layerGraphic.geometry.y,
      spatialReference: { wkid: 102100 }
    })
  } else if (layerGraphic.geometry.type === 'polyline') {
    geometry = new Polyline({
      paths: layerGraphic.geometry.paths,
      spatialReference: { wkid: 102100 }
    })
  } else {
    return false
  }

  return geometryEngine.intersects(geometry, extent)
}
// #endregion

// #region MEASUREMENT
let measurementTool
export const measure = async(type) => {
  try {
    const [Measurement] = await loadModules(['esri/widgets/Measurement'])

    if (!measurementTool) {
      // Se inicializa si no lo está ya
      measurementTool = new Measurement({ areaUnit: 'hectares' }) // areaUnit:

      mapView.ui.add(measurementTool, 'bottom-right')
      measurementTool.view = mapView
    }

    if (type === 'distance') {
      measurementTool.activeTool =
        mapView.type.toUpperCase() === '2D' ? type : 'direct-line'
    } else if (type === 'area') {
      measurementTool.activeTool = type
    }
  } catch (err) {
    VueInst.$log.error(err)
  }
}
export const clearMeasurement = () => {
  if (measurementTool) {
    measurementTool.clear()
    measurementTool.destroy()
    measurementTool = null
  }
}
// #endregion

// #region CONTORNO
export const drawContorno = async() => {
  const contornoGraphic = await createGraphicsContorno()

  const layerContorno = map.findLayerById('layer-contorno')
  layerContorno.graphics.items = contornoGraphic
}

async function createGraphicsContorno() {
  const deferred = Q.defer()

  try {
    const [Extent, geometryEngineAsync] = await loadModules([
      'esri/geometry/Extent',
      'esri/geometry/geometryEngineAsync'
    ])

    const symbol = {
      type: 'simple-fill',
      color: [157, 198, 216, 1], // 0.3
      width: 1
    }

    const worldExtent = new Extent({
      xmin: -20037508.34278924, // -20037508.3427892430765884088807
      ymin: -19971868.8804086,
      xmax: 20037508.34278924, // 20037508.3427892430765884088807
      ymax: 19971868.8804086,
      spatialReference: {
        wkid: 3857
      }
    })

    const worldPolygon = Polygon.fromExtent(worldExtent)

    const comunidad = {
      type: 'polygon',
      rings: store.getters['map/paramsComunidad'].RINGS,
      spatialReference: {
        wkid: 102100
      }
    }
    // PROBLEMA DE CARGA LENTA
    const buffer = null // await geometryEngineAsync.buffer(comunidad, 10000000)
    store.dispatch('map/setBufferComunidad', buffer) // Guardar poligono buffer

    const contorno = await geometryEngineAsync.difference(worldPolygon, comunidad)
    const contornoGraphic = new Graphic({
      geometry: contorno,
      symbol
    })

    deferred.resolve([contornoGraphic])
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

export const pointInBuffer = async(lat, lon) => {
  const deferred = Q.defer()

  try {
    const [projection] = await loadModules([
      'esri/geometry/projection'
    ])

    let point = new Point(lon, lat)
    point = projection.project(point, { wkid: 3857 })
    const buffer = store.getters['map/bufferComunidad']
    const contains = true // GeometryEngine.contains(buffer, point)

    deferred.resolve(contains)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}
// #endregion

// #region COORDS_MAP
export const drawPointCoords = async(lat, lon) => {
  try {
    mapView.graphics = []

    if (lat && lon) {
      const symbol = {
        type: 'simple-marker',
        style: 'cross',
        outline: {
          color: 'yellow',
          width: '3px'
        }
      }

      const geometry = new Point({
        x: lon,
        y: lat
      })

      const graphic = new Graphic({
        geometry,
        symbol
      })
      mapView.graphics.add(graphic)
      setTimeout(deletePointCoords, 60 * 1000)
    }
  } catch (err) {
    VueInst.$log.error(err)
  }
}
function deletePointCoords() {
  mapView.graphics = []
}
// #endregion

// #region SHARE MAP SKETCH VIEW MODEL
let shareMapSVM = null
export const initShareMapSketchViewModel = async() => {
  try {
    const [SketchViewModel] = await loadModules([
      'esri/widgets/Sketch/SketchViewModel'
    ])

    const layer = map.findLayerById('layer-drawSketcher')
    if (!layer) {
      VueInst.$log.error(new Error("Capa 'layer-drawSketcher' no encontrada, el mapa no ha terminado de cargar"))
      return
    }

    shareMapSVM = new SketchViewModel({
      view: mapView,
      layer
    })

    shareMapSVM.on(['create', 'delete', 'update'], async(evt) => {
      if (evt.state === 'complete' && evt.type === 'create') {
        let graphic

        const attributes = {
          ID: VueInst.$uuid.createUUID()
        }
        evt.graphic.attributes = JSON.parse(JSON.stringify(attributes)) // Hack para quitar el __observer__ que causa un bucle infinito a arcgis

        const color = [255, 100, 100, 0.4] // REMINDER: si no se pone el color en este formato, al convertirse a JSON el alpha se 'rompe'

        if (evt.graphic.geometry.type === 'multipoint') {
          // eslint-disable-next-line no-unreachable-loop
          for (let i = 0; i < evt.graphic.geometry.points.length; i++) {
            graphic = {
              attributes,
              geometry: evt.graphic.geometry.points[i],
              type: 'point',
              symbol: {
                type: shareMapSVM.pointSymbol.type,
                color, // shareMapSVM.pointSymbol.color,
                angle: shareMapSVM.pointSymbol.angle,
                size: shareMapSVM.pointSymbol.size,
                style: shareMapSVM.pointSymbol.style
              }
            }
            store.dispatch('shareMap/addGraphics', graphic)
          }
          return
        } else if (evt.graphic.geometry.type === 'point') {
          graphic = {
            attributes,
            geometry: [evt.graphic.geometry.x, evt.graphic.geometry.y],
            type: evt.graphic.geometry.type,
            symbol: {
              type: shareMapSVM.pointSymbol.type,
              color, // shareMapSVM.pointSymbol.color,
              size: shareMapSVM.pointSymbol.size,
              style: shareMapSVM.pointSymbol.style
            }
          }
        } else if (evt.graphic.geometry.type === 'polyline') {
          graphic = {
            attributes,
            geometry: evt.graphic.geometry.paths[0],
            type: 'polyline',
            symbol: {
              type: shareMapSVM.polylineSymbol.type,
              color, // shareMapSVM.polylineSymbol.color,
              width: shareMapSVM.polylineSymbol.width,
              style: shareMapSVM.polylineSymbol.style,
              join: shareMapSVM.polylineSymbol.join,
              miterLimit: shareMapSVM.polylineSymbol.miterLimit
            }
          }
        } else if (evt.graphic.geometry.type === 'polygon') {
          graphic = {
            attributes,
            geometry: evt.graphic.geometry.rings[0],
            type: 'polygon',
            symbol: {
              type: shareMapSVM.polygonSymbol.type,
              color, // shareMapSVM.polygonSymbol.color,
              style: shareMapSVM.polygonSymbol.style,
              outline: {
                color: 'white',
                width: 0
              }
            }
          }
        }

        store.dispatch('shareMap/addGraphics', graphic)
      } else if (evt.type === 'delete') {
        store.dispatch('shareMap/deleteGraphics', evt.graphics) // TODO:
      } else if (evt.state === 'complete' && evt.type === 'update') {
        const rawGeometry = evt.graphics[0].geometry

        const geometry = {
          type: rawGeometry.type,
          paths: rawGeometry.paths,
          rings: rawGeometry.rings,
          x: rawGeometry.x,
          y: rawGeometry.y
        }

        const g = {
          attributes: JSON.parse(JSON.stringify(evt.graphics[0].attributes)),
          geometry
        }
        store.dispatch('shareMap/updateGraphics', [g]) // TODO:
      }
    })
  } catch (err) {
    VueInst.$log.error(err)
  }
}

export const paintEventShareMap = (event) => {
  const layer = map.findLayerById('layer-drawSketcher')
  if (!layer || !shareMapSVM) return

  const typeEvent = event.function

  isDrawingArrow = false
  if (typeEvent === 'cancel') {
    shareMapSVM.cancel()
  } else if (typeEvent === 'reset') {
    layer.removeAll()
    shareMapSVM.cancel()

    store.dispatch('shareMap/removeAllGraphics')
  } else {
    shareMapSVM.create(typeEvent)
  }
}

export const resetShareMapLayer = () => {
  const layer = map.findLayerById('layer-drawSketcher')
  if (!layer) return
  layer.removeAll()
  if (shareMapSVM) {
    shareMapSVM.cancel()
  }
}

export const shareMapSimulacion = async(data) => {
  const deferred = Q.defer()

  if (map) {
    try {
      const uuid = data.simulacion.sim.id

      if (!simulationGraphics.has(uuid)) {
        simulationGraphics.set(uuid, {
          id: uuid,
          sim: {
            id: uuid,
            simulacion: JSON.parse(JSON.stringify(data.simulacion.sim.simulacion)),
            coordenadas: [],
            isocronas: [],
            isocronasLabels: [],
            longitudLlama: [],
            intensidadLlama: [],
            velocidadPropagacion: [],
            graficosSimulacion: [],
            origen: []
          }
        })
      }
      const simData = simulationGraphics.get(uuid)

      if (data.simulacion.id === 'simulacion') {
        if (!(simData.sim.coordenadas?.length > 0)) {
          simData.sim.coordenadas = await createGraphicsSimulacion(data.response.data, uuid, simData.sim.simulacion.FECHA_INICIO)
        }

        if (!(simData.sim.isocronas?.length > 0)) {
          const isocronasData = await createGraphicsIsocronas(data.response.data, uuid)
          simData.sim.isocronas = isocronasData.polygons
          simData.sim.isocronasLabels = isocronasData.labels
        }

        if (simData.sim.simulacion.GRAFICOS && !(simData.sim.graficosSimulacion?.length > 0)) {
          simData.sim.graficosSimulacion = await createAuxiliarGraphicsSimulacion(simData.sim.simulacion.GRAFICOS, uuid)
        }

        if (!(simData.sim.origen?.length > 0)) {
          simData.sim.origen = createOriginGraphics(simData.sim.simulacion.GRAFICOS, uuid)
        }
      } else {
        if (!(simData.sim[data.simulacion.id]?.length > 0)) {
          simData.sim[data.simulacion.id] = await createGraphics(data.response.data, uuid)
        }
      }

      simData.sim.modo = data.simulacion.id

      await paintSimulacionLayer(data.simulacion)

      deferred.resolve()
    } catch (err) {
      deferred.reject(err)
    }
  } else {
    deferred.reject()
  }

  return deferred.promise
}

function comparador(a) {
  // Texto tiene que estar siempre antes
  if (a.symbol.type === 'text') {
    return 1
  }

  // En cualquier otro caso, va despues
  return -1
}

export const drawShareMapGraphics = async(isNotEditingMode) => {
  const layer = map.findLayerById('layer-drawSketcher')
  if (!layer || !shareMapSVM) return

  const graphicsList = store.getters['shareMap/graphics']
  const graphics = await createGraphicsDraw(graphicsList)

  if (isNotEditingMode) {
    shareMapSVM.updateOnGraphicClick = false
  } else {
    shareMapSVM.updateOnGraphicClick = true
  }

  const graphicsSorted = sortGraphics(graphics)

  layer.removeAll()
  layer.addMany(graphicsSorted)
}

export const stopShareMapSketchViewModel = () => {
  store.dispatch('shareMap/removeAllGraphics')

  if (!shareMapSVM) return

  shareMapSVM.cancel()
  shareMapSVM.destroy()
  shareMapSVM = null
}
// #endregion

// #region ALINEACIONES CAMPBELL
let planACSVM = null

export const initAlineacionesCampbellSketchViewModel = async(data) => {
  try {
    const [SketchViewModel] = await loadModules([
      'esri/widgets/Sketch/SketchViewModel'
    ])
    const callbackCreate = data.callbackCreate
    const callbackUpdate = data.callbackUpdate
    const callbackDelete = data.callbackDelete

    const layer = map.findLayerById('layer-alineaciones-campbell')

    planACSVM = new SketchViewModel({
      view: mapView,
      layer,
      defaultCreateOptions: { hasZ: false }
    })
    planACSVM.on(['create', 'delete', 'update'], async(evt) => {
      if (evt.state === 'complete' && evt.type === 'create') {
        // Crear grafico
        const attributes = {
          ID_GRAFICO: VueInst.$uuid.createUUID()
        }

        evt.graphic.attributes = JSON.parse(JSON.stringify(attributes)) // Hack para quitar el __observer__ que causa un bucle infinito a arcgis

        let geometry
        let type = evt.graphic.geometry.type
        let symbol
        if (evt.graphic.symbol.type === 'text') {
          // Texto
          const defaultSymbol = planACSVM.pointSymbol

          geometry = [evt.graphic.geometry.x, evt.graphic.geometry.y]
          type = 'text'
          symbol = {
            type: 'text',
            color: defaultSymbol.color,
            text: defaultSymbol.text,
            xoffset: 0,
            yoffset: 0,
            haloColor: 'white',
            haloSize: '5px',
            font: {
              size: defaultSymbol.font.size,
              family: 'Arial Unicode MS',
              weight: 'bold'
            }
          }
        } else if (evt.graphic.geometry.type === 'polygon') {
          // Poligonos
          const defaultSymbol = planACSVM.polygonSymbol

          geometry = evt.graphic.geometry.rings[0]
          symbol = {
            type: defaultSymbol.type,
            color: defaultSymbol.color,
            style: defaultSymbol.style,
            outline: {
              color: defaultSymbol.outline.color,
              width: defaultSymbol.outline.width
            }
          }
        }

        // Test
        // attributes.LONGITUD = GeometryEngine.geodesicLength(geometry)
        // attributes.AREA = Math.abs(GeometryEngine.geodesicArea(geometry))

        const graphic = {
          // TODO: poner los mismos nombres que en BBDD y cambiar en backend en (plan_operaciones addGraphics)
          attributes,
          geometry,
          type,
          symbol
        }

        callbackCreate(graphic)
      } else if (evt.state === 'complete' && evt.type === 'update') {
        // Actualizar grafico
        const graphic = evt.graphics[0]
        const geom = graphic.geometry

        let newGeometryGraphic
        if (geom.type === 'polygon') {
          newGeometryGraphic = geom.rings[0]
        } else {
          newGeometryGraphic = [geom.x, geom.y]
        }

        // geometry.type = graphic.geometry.type
        const newGraphic = {
          ATRIBUTOS: graphic.attributes,
          GEOMETRIA: newGeometryGraphic
        }

        callbackUpdate(newGraphic)
      } else if (evt.type === 'delete') {
        // Borrar grafico
        callbackDelete(evt.graphics[0].attributes.ID_GRAFICO)
      }
    })
  } catch (err) {
    VueInst.$log.error(err)
  }
}

export const paintEventAlineacionesCampbell = (event) => {
  let typeEvent = event.function

  if (typeEvent === 'polygon') {
    planACSVM.polygonSymbol.color = event.color
    planACSVM.polygonSymbol.outline = {
      color: event.color,
      width: 2
    }
  } else if (typeEvent === 'text') {
    typeEvent = 'point'
    planACSVM.pointSymbol = {
      type: 'text',
      color: [0, 0, 0, 1],
      text: event.text,
      xoffset: 0,
      yoffset: 0,
      haloColor: 'white',
      haloSize: '20px',
      font: {
        size: event.size,
        family: 'Arial Unicode MS',
        weight: 'bold'
      }
    }
  }
  planACSVM.create(typeEvent)
}

export const cancelDrawingAlineaciones = () => {
  planACSVM.cancel()
}

export const stopAlineacionesCampbellSketchViewModel = () => {
  const layer = map.findLayerById('layer-alineaciones-campbell')
  if (layer) layer.removeAll()

  store.dispatch('shareMap/stopAlineacionesCampbell')

  if (planACSVM === null) return
  planACSVM.cancel()
  planACSVM.destroy()
}

function createGraphicsDrawAlineacionesCampbell(graphicList) {
  const deferred = Q.defer()

  try {
    const graphicsDraw = graphicList
    let geometry
    const graphics = []
    for (let i = 0; i < graphicsDraw.length; i++) {
      const tipo = graphicsDraw[i].type || graphicsDraw[i].TIPO // TODO: esto no mola
      let coords = graphicsDraw[i].geometry || graphicsDraw[i].GEOMETRIA
      if (typeof coords === 'string') coords = JSON.parse(coords)
      let simbolo = graphicsDraw[i].symbol || graphicsDraw[i].SIMBOLO
      if (typeof simbolo === 'string') simbolo = JSON.parse(simbolo)
      let atributos = graphicsDraw[i].attributes || graphicsDraw[i].ATRIBUTOS
      if (typeof atributos === 'string') atributos = JSON.parse(atributos)
      atributos = JSON.parse(JSON.stringify(atributos)) // HACK __observer__

      if (tipo === 'text') {
        geometry = new Point({
          x: coords[0],
          y: coords[1],
          spatialReference: { wkid: 3857 },
          hasZ: true
        })
      } else if (tipo === 'polygon') {
        geometry = new Polygon({
          hasZ: false, // Si se pone true, no funciona el 3D (no-sense)
          hasM: true,
          rings: coords,
          spatialReference: { wkid: 3857 }
        })

        // atributos.LONGITUD = GeometryEngine.geodesicLength(geometry)
        // atributos.AREA = Math.abs(GeometryEngine.geodesicArea(geometry))
      }

      const graphic = new Graphic({
        geometry,
        symbol: simbolo,
        attributes: atributos
      })

      graphics.push(graphic)
    }

    deferred.resolve(graphics)
  } catch (err) {
    VueInst.$log.error(err)
  }

  return deferred.promise
}

export const drawGraphicsAlineacionesCampbell = async function(graphicList) {
  const deferred = Q.defer()

  const layer = map.findLayerById('layer-alineaciones-campbell')
  if (!layer) {
    deferred.resolve()
    return deferred.promise
  }
  layer.removeAll()

  store.dispatch('shareMap/paintAlineacionesCampbell', graphicList)

  const graphics = await createGraphicsDrawAlineacionesCampbell(graphicList)
  const graphicsSorted = sortGraphics(graphics)
  layer.addMany(graphicsSorted)

  deferred.resolve()

  return deferred.promise
}
// #endregion

// #region COMPASS WIDGET
let compass = null
export const compassWidget = async() => {
  try {
    const [Compass] = await loadModules(['esri/widgets/Compass'])

    if (!compass) {
      compass = new Compass({
        view: mapView,
        id: 'compasss',
        container: 'compassWidget'
      })
    } else {
      compass.view = mapView
    }
  } catch (err) {
    VueInst.$log.error(err)
  }
}

let legend = null
let legendContent = null

function updateWidgetView() {
  if (compass) {
    compass.view = mapView
  }

  if (legend) {
    legend.view = mapView
    mapView.ui.add(legend, 'bottom-right')
  }

  if (planACSVM) {
    planACSVM.view = mapView
  }

  if (searchWidget) {
    searchWidget.view = mapView
  }
}
// #endregion

// #region LEGEND WIDGET
export const legendWidget = async() => {
  try {
    const [Legend, Expand] = await loadModules(['esri/widgets/Legend', 'esri/widgets/Expand'])

    if (!legend) {
      // Se inicializa si no lo está ya
      legendContent = new Legend({
        view: mapView,
        style: 'card',
        hideLayersNotInCurrentView: true
      })

      const actualizarLegend = () => {
        const capasSinViento = mapView.map.layers
          .filter(layer => !layer.id?.startsWith('layer-viento'))

        legendContent.layerInfos = capasSinViento.map(layer => ({
          layer
        }))
      }

      actualizarLegend()

      mapView.map.layers.on('change', actualizarLegend)

      legend = new Expand({
        id: 'legend',
        content: legendContent,
        expandIconClass: 'esri-icon-description',
        view: mapView,
        expanded: false
      })

      mapView.ui.add(legend, 'bottom-right')
    }
  } catch (err) {
    VueInst.$log.error(err)
  }
}
// #endregion

// #region areasIncendio

export const measurePerimetros = async(graficos) => {
  if (!graficos || graficos.length === 0) return { area: 0, perimetro: 0 }
  const [geometryEngine] = await loadModules([
    'esri/geometry/geometryEngine'
  ])

  let area = 0
  let perimetro = 0

  graficos.forEach((graphic) => {
    const rawRings = graphic.geometry ? graphic.geometry : JSON.parse(graphic.GEOMETRIA)

    const polygon = new Polygon({
      rings: [rawRings],
      spatialReference: { wkid: 102100 }
    })

    area += Math.abs(geometryEngine.geodesicArea(polygon, 'square-meters'))
    perimetro += geometryEngine.geodesicLength(polygon, 'kilometers')
  })

  area /= 10000

  const perimeterMeasure = { area: Math.round(area * 100) / 100, perimetro: Math.round(perimetro * 100) / 100 }
  return perimeterMeasure
}

// #endregion

/*
async function test () {
  try {
    const [MapView] = await loadModules(['esri/views/MapView'])
  } catch (err) {
    VueInst.$log.error(err)
  }
}
*/
